// DairyOps ERP — i18n dictionaries (English + Romanian)
// Missing keys gracefully fall back to English, then to the key itself.

export const en = {
  lang: { english: "English", romanian: "Română", label: "Language" },

  common: {
    save: "Save", cancel: "Cancel", delete: "Delete", edit: "Edit", add: "Add",
    create: "Create", close: "Close", confirm: "Confirm", done: "Done", search: "Search",
    loading: "Loading...", actions: "Actions", yes: "Yes", no: "No", back: "Back",
    download: "Download", view: "View", total: "Total", date: "Date", notes: "Notes",
    status: "Status", all: "All", none: "None", qty: "Qty", quantity: "Quantity",
    unit: "Unit", name: "Name", email: "Email", phone: "Phone", address: "Address",
    price: "Price", product: "Product", products: "Products", packaging: "Packaging",
    client: "Client", clients: "Clients", expiry: "Expiry", remaining: "Remaining",
    saving: "Saving...", deleting: "Deleting...", generating: "Generating...",
    optional: "optional", required: "required", today: "Today", noData: "No data available",
    retry: "Retry", apply: "Apply", filter: "Filter", clear: "Clear", select: "Select",
    month: "Month",
    allClients: "All clients", noMatchClient: "No records match the selected client.",
    of: "of", items: "items", item: "item", currency: "Currency", code: "Code",
    failed: "Something went wrong", success: "Success", lot: "Lot", batch: "Batch",
  },

  nav: {
    dashboard: "Dashboard", milk: "Milk Reception", calculator: "Fat Calculator", yieldSimulator: "Yield Simulator",
    productions: "Productions", planning: "Planning", productionPlanner: "Production Planner", templates: "Production Templates",
    haccp: "HACCP", stock: "Stock", orders: "Orders", deliveryNotes: "Delivery Notes",
    subproducts: "By-products",
    traceability: "Traceability",
    guide: "Guide",
    clients: "Clients", priceLists: "Price Lists", settings: "Company Settings",
    activity: "Activity Log", users: "Users", suppliers: "Suppliers", milkSuppliers: "Milk suppliers", materialSuppliers: "Ingredient suppliers", rawMaterials: "Raw Materials",
    permissions: "Permissions",
    billing: "Billing",
    salesInventory: "Sales & Inventory", administration: "Administration",
    productPackaging: "Product Packaging",
    products: "Products",
    staff: "Staff", operators: "Operators", timeClock: "Time Clock",
    tasks: "Tasks", complaints: "Complaints",
    modMilkReception: "Raw Materials Reception", modProduction: "Production",
    modStockOrders: "Stock & Orders", modWorkforce: "Workforce", modSettingsAdmin: "Settings & Admin",
    modFinance: "Finance", expenses: "Expenses", reports: "Reports",
    stockMovements: "Stock Movements",
    losses: "Losses & Write-offs",
    nonconformities: "Nonconformity Register",
    modCompliance: "HACCP & Traceability",
  },

  footer: { units: "Metric units · °C · Liters", brandSub: "Production & HACCP" },

  expenses: {
    title: "Expenses",
    subtitle: "Track operating costs — rent, utilities, cleaning and more — and see how your spending trends.",
    addExpense: "Add expense", editExpense: "Edit expense",
    manageCategories: "Categories",
    amount: "Amount", currency: "Currency", category: "Category", date: "Date",
    vendor: "Vendor / supplier", vendorPlaceholder: "e.g. Electrica, landlord…",
    pickCategory: "Pick a category",
    allExpenses: "All expenses", empty: "No expenses yet. Add your first one to start tracking.",
    allCategories: "All categories",
    periodAll: "All time", periodThisWeek: "This week", periodThisMonth: "This month",
    periodLastMonth: "Last month", periodThisYear: "This year", periodCustom: "Custom range",
    from: "From", to: "To", clearFilters: "Clear", filteredTotal: "Filtered total",
    noResults: "No expenses match these filters.",
    weekly: "Weekly", monthly: "Monthly",
    thisWeek: "This week", thisMonth: "This month",
    vsPrevious: "vs previous", allTime: "All time", noChange: "No data",
    trendTitle: "Spending trend", byCategory: "By category", spent: "Spent", noData: "No spending in this period.",
    created: "Expense added", updated: "Expense updated", deleted: "Expense deleted",
    amountRequired: "Enter an amount greater than 0", categoryRequired: "Please choose a category",
    deleteTitle: "Delete this expense?", deleteDesc: "This will permanently remove the expense entry.",
    catSubtitle: "Add, rename or remove expense categories.",
    catAdd: "Add", catPlaceholder: "New category (e.g. Rent, Gas)…", catEmpty: "No categories yet.",
    catAdded: "Category added", catRenamed: "Category renamed", catDeleted: "Category deleted",
    catDeleteTitle: "Delete this category?",
    catDeleteDesc: "Delete \"{name}\"? Categories used by existing expenses cannot be deleted.",
    fromRecurring: "Auto-generated from a recurring rule",
    recurringBtn: "Recurring", recurringTitle: "Recurring expenses",
    recurringSubtitle: "Rules that automatically create an expense every week, month or year.",
    recurringEmpty: "No recurring rules yet. Add an expense and turn on \"Make recurring\".",
    makeRecurring: "Make recurring", makeRecurringDesc: "Auto-repeat this expense on a schedule.",
    frequency: "Frequency", freqWeekly: "Weekly", freqMonthly: "Monthly", freqYearly: "Yearly",
    startDate: "Start date", endDate: "End date (optional)", endAfterStart: "End date must be after the start date",
    nextCharge: "Next", ends: "Ends", forever: "Runs until stopped",
    pause: "Pause", resume: "Resume", paused: "Paused", resumed: "Resumed",
    editRule: "Edit recurring expense",
    ruleCreated: "Recurring expense created", ruleUpdated: "Recurring rule updated", ruleDeleted: "Recurring rule deleted",
    ruleDeleteTitle: "Delete this recurring rule?",
    ruleDeleteDesc: "Future expenses will no longer be created. Expenses already generated stay in your history.",
  },

  guide: {
    title: "User Guide",
    subtitle: "Learn how to use LactoPilot and what each setting does. Pick a topic from the list.",
    contents: "Contents",
  },

  operators: {
    title: "Operators", subtitle: "Manage your staff roster",
    add: "Add operator", edit: "Edit operator", new: "New operator",
    name: "Name", jobTitle: "Job title / role", notes: "Notes",
    linkedUser: "Linked login account", linkedUserHint: "Optional — lets this person clock in from their own account",
    noUser: "No login account", active: "Active", inactive: "Inactive",
    status: "Status", linked: "Linked", empty: "No operators yet. Add your first one.",
    created: "Operator added", updated: "Operator updated", deleted: "Operator deleted",
    confirmDelete: "Delete this operator?", deleteBlocked: "This operator has time records — deactivate instead.",
    saveFailed: "Could not save operator", count: "{n} operators", actions: "Actions",
  },

  clock: {
    title: "Time Clock", subtitle: "Clock in when at work, clock out when leaving",
    myClock: "My clock", kiosk: "Kiosk", history: "History",
    clockIn: "Clock in", clockOut: "Clock out",
    clockedIn: "Clocked in", notClockedIn: "Not clocked in",
    since: "Since {time}", todayWorked: "Today: {dur}",
    nowAtWork: "At work now", peopleIn: "{n} clocked in", totalToday: "Total today",
    selectOperator: "Pick an operator to clock", noOperators: "No active operators. Add operators first.",
    notLinked: "Your account isn't linked to an operator. Ask an admin to link it, or use the kiosk below.",
    clockedInToast: "{name} clocked in", clockedOutToast: "{name} clocked out · {dur}",
    actionFailed: "Action failed", entriesEmpty: "No time records for this day.",
    operator: "Operator", in: "In", out: "Out", duration: "Duration", date: "Date", source: "Source",
    sourceKiosk: "Kiosk", sourceSelf: "Self", ongoing: "ongoing", viewDay: "Day",
    filterDay: "Day", filterWeek: "Week", filterMonth: "Month", filterView: "View",
    rangeLabel: "{from} – {to}", weekendBadge: "Weekend", overtimeBadge: "Overtime",
    legendWeekend: "Non-working day", legendOvertime: "Over {h}h (overtime)",
    totalRange: "Total in range", entriesEmptyRange: "No time records in this range.",
    break: "Break", totalBreak: "Total break", totalOvertime: "Total overtime", totalDeficit: "Total deficit", allEmployees: "All employees", weekendOnly: "Weekend only",
    overtimeBasis: "Overtime & deficit are calculated per ISO week against {h}h/week mandatory hours (standard day × working days), net of missed days; leave and holidays are excused.",
    timeOffTitle: "Leave & holidays", timeOffHint: "Leave and holidays excuse working days so they are not counted against mandatory weekly hours.",
    leavesTitle: "Employee leave", addLeave: "Add leave", leavesEmpty: "No leave in this period.",
    holidaysTitle: "Company holidays", addHoliday: "Add holiday", holidaysEmpty: "No holidays in this period.",
    leaveDialogHint: "Excuse an employee's working days (leave, sick, unpaid). These days are removed from their mandatory weekly hours.",
    leaveStart: "From", leaveEnd: "To", leaveTypeLabel: "Type",
    leaveType_leave: "Leave", leaveType_sick: "Sick", leaveType_unpaid: "Unpaid", leaveType_other: "Other",
    holidayDialogHint: "A company-wide non-working day, excused for all employees.", holidayDate: "Date", holidayName: "Name", holidayNamePh: "e.g. National Day",
    offFillAll: "Please fill in all required fields.",
    leaveSaved: "Leave added", leaveDeleted: "Leave removed", holidaySaved: "Holiday added", holidayDeleted: "Holiday removed",
    breakMinutes: "Break (minutes)", breakHint: "Total break time to deduct from the worked hours.",
    // Admin manual edit
    manage: "Manage entries", addEntry: "Add entry", editEntry: "Edit entry",
    addEntryTitle: "Add a time entry", editEntryTitle: "Edit time entry",
    selectOp: "Operator", clockInTime: "Clock in", clockOutTime: "Clock out (optional)",
    leaveOpenHint: "Leave clock-out empty to keep the entry open.",
    entryCreated: "Time entry added", entryUpdated: "Time entry updated", entryDeleted: "Time entry deleted",
    confirmDeleteEntry: "Delete this time entry? This cannot be undone.",
    saveEntryFailed: "Could not save the time entry", deleteEntryFailed: "Could not delete the entry",
    photo: "Photo", photoIn: "In photo", photoOut: "Out photo", noPhoto: "No photo",
    actionsCol: "Actions", sourceManual: "Manual",
    // Kiosk link / banner
    openKiosk: "Open kiosk", kioskLink: "Kiosk link", copyLink: "Copy link", linkCopied: "Kiosk link copied",
    kioskHint: "Share this link on a shared tablet so staff can clock in/out with a photo.",
    // Public kiosk page
    kioskTitle: "Time Clock Kiosk", kioskSubtitle: "Tap your name, take a photo, and clock in or out.",
    kioskPickPrompt: "Who's clocking?", kioskTakePhoto: "Take photo", kioskRetake: "Retake",
    kioskConfirmIn: "Confirm clock in", kioskConfirmOut: "Confirm clock out",
    kioskCameraError: "Camera not available. Please allow camera access.",
    kioskStartCamera: "Start camera", kioskCancel: "Cancel", kioskDone: "Done",
    kioskSuccessIn: "{name} clocked in", kioskSuccessOut: "{name} clocked out · {dur}",
    kioskBack: "Back", kioskNoOps: "No active operators yet.", kioskRefresh: "Refresh",
    kioskCapturing: "Capturing…", kioskProcessing: "Processing…",
    startBreak: "Start break", endBreak: "End break", onBreak: "On break", onBreakSince: "On break since {time}",
    kioskConfirmBreakStart: "Confirm break start", kioskConfirmBreakEnd: "Confirm break end",
    kioskSuccessBreakStart: "{name} is on break", kioskSuccessBreakEnd: "{name} is back from break",
  },

  tasks: {
    title: "Tasks", subtitle: "Assign work to operators",
    add: "New task", edit: "Edit task",
    taskTitle: "Title", description: "Description", assignTo: "Assign to",
    unassigned: "Unassigned", dueDate: "Due date", linkTo: "Open section",
    noLink: "None", markDone: "Mark done", reopen: "Reopen", open: "Open", done: "Done",
    openTasks: "Open", doneTasks: "Done", allTasks: "All", filterOperator: "Operator",
    empty: "No tasks yet.", emptyOpen: "No open tasks.", emptyDone: "No completed tasks yet.",
    created: "Task created", updated: "Task updated", deleted: "Task deleted",
    statusUpdated: "Task updated", confirmDelete: "Delete this task?",
    saveFailed: "Could not save task", dueOn: "Due {date}", completedBy: "Done by {name}",
    goTo: "Open {section}", createdBy: "By {name}",
    repeat: "Repeat", repeatHint: "When completed, the next occurrence is created automatically and shows in Reminders when due.",
    every: "Every", unitDaysPlain: "days", unitWeeksPlain: "weeks",
    recur_none: "One-time", recur_daily: "Daily", recur_weekly: "Weekly", recur_monthly: "Monthly", recur_custom: "Custom",
    recur_working_days: "Working days (Mon–Fri)", recur_weekdays: "Specific weekdays",
    dayMon: "Mon", dayTue: "Tue", dayWed: "Wed", dayThu: "Thu", dayFri: "Fri", daySat: "Sat", daySun: "Sun",
    pickDays: "Pick at least one weekday",
    recurEvery: "Every {interval}", unitDays: "days", unitWeeks: "weeks",
    ccpField: "Critical Control Point", noCcp: "None", ccp: "CCP",
    ccpHint: "Completing this task will open the HACCP form to log this CCP's reading.",
    ccpLog: "Log CCP: {name}", autoFromCcp: "Auto from CCP",
  },

  complaints: {
    title: "Complaints", subtitle: "Internal complaints & issues log",
    add: "New complaint", file: "File a complaint",
    complaintTitle: "Title", description: "Description", about: "About operator (optional)",
    none: "—", status: "Status",
    open: "Open", inProgress: "In progress", resolved: "Resolved",
    setStatus: "Set status", markResolved: "Mark resolved", reopen: "Reopen",
    empty: "No complaints filed.", filterAll: "All", filterOpen: "Open", filterResolved: "Resolved",
    created: "Complaint filed", updated: "Complaint updated", deleted: "Complaint deleted",
    confirmDelete: "Delete this complaint?", saveFailed: "Could not save complaint",
    filedBy: "Filed by {name}", resolvedBy: "Resolved by {name}", aboutOperator: "About {name}",
  },

  userMenu: { signOut: "Sign out" },
  changePw: {
    menu: "Change password", title: "Change password",
    current: "Current password", new: "New password", confirm: "Confirm new password",
    success: "Password changed", failed: "Failed to change password",
    tooShort: "Password must be at least 6 characters", mismatch: "Passwords do not match",
  },
  activate: {
    title: "Activate your account", subtitle: "Set a password to finish setting up your account",
    missing: "This activation link is missing its token.",
    newPassword: "New password", confirmPassword: "Confirm password", activate: "Activate account",
    tooShort: "Password must be at least 6 characters", mismatch: "Passwords do not match",
    failed: "Activation failed. The link may be invalid or expired.",
    successTitle: "Account activated", successBody: "Your account is ready. You can now sign in.",
    errorTitle: "Activation failed", goToLogin: "Go to login",
  },
  reset: {
    title: "Reset your password", subtitle: "Choose a new password for your account",
    missing: "This reset link is missing its token.",
    newPassword: "New password", confirmPassword: "Confirm password", reset: "Reset password",
    tooShort: "Password must be at least 6 characters", mismatch: "Passwords do not match",
    failed: "Reset failed. The link may be invalid or expired.",
    successTitle: "Password updated", successBody: "Your password has been changed. You can now sign in.",
    errorTitle: "Reset failed", goToLogin: "Go to login",
  },
  reports: {
    title: "Sales reports", subtitle: "Delivered sales by product, quantity and revenue.",
    revenue: "Revenue", prevRevenue: "Prev. revenue", totalQty: "Total quantity",
    acrossUnits: "across {n} unit(s)", productLines: "Product lines", trendTitle: "Sales trend",
  },

  stockMovements: {
    title: "Stock Movements",
    subtitle: "Every finished product going into stock (production, sub-variants) and out (sales, write-offs), with a running balance per product.",
    from: "From", to: "To", allProducts: "All products",
    export: "Export Excel", exportDone: "Export ready", exportError: "Export failed", loadError: "Could not load stock movements",
    totalIn: "Total in", totalOut: "Total out", netChange: "Net change", movements: "Movements",
    acrossProducts: "across {n} product(s)",
    balanceTitle: "Balance by product (kg)", detailTitle: "Movement details",
    opening: "Opening", inKg: "In (kg)", outKg: "Out (kg)", closing: "Closing",
    date: "Date", direction: "Direction", movementType: "Type", qty: "Quantity", kg: "Kg", lot: "Lot / batch", reference: "Reference",
    empty: "No stock movements in this period.", emptyDetail: "No movements to show for the selected filters.",
    kgNote: "Balances are shown in kg-equivalent (net weight) so mixed packaging can be summed. Closing = Opening + In − Out.",
    dir: { in: "In", out: "Out" },
    type: {
      production: "Production yield", subvariant: "Sub-variant packing", repack: "Repackaging",
      manual: "Manual entry", adjustment: "Adjustment", sale: "Sale / delivery", carve: "Sub-variant carve-out",
      writeoff: "Write-off",
    },
    losses: {
      title: "Losses by reason",
      subtitle: "Write-offs in the selected period, split by reason — waste patterns stand out at a glance.",
      writeOffs: "{n} write-off(s)",
      empty: "No losses recorded in this period.",
      estValueNote: "Values estimated from your price list (base level 1).",
      uncovered: "{kg} kg without a defined price — value is underestimated.",
      reason: { expired: "Expired", damaged: "Damaged", quality: "Quality issue", other: "Other", unspecified: "Unspecified" },
    },
  },
  losses: {
    title: "Losses & Write-offs",
    subtitle: "Every write-off and stock reduction, with reason, estimated value and one-click undo.",
    loadError: "Could not load losses",
    totalLost: "Total lost",
    estValue: "Estimated value",
    writeOffCount: "Write-offs",
    revertedCount: "+{n} reverted (not counted)",
    reasonCol: "Reason",
    valueCol: "Est. value",
    byWhom: "By",
    allReasons: "All reasons",
    empty: "No losses in this period.",
    tableNote: "Reverted write-offs stay listed for audit but are excluded from totals and all loss reports.",
    undo: "Undo",
    reverted: "Reverted",
    revertConfirmTitle: "Undo this write-off?",
    revertConfirmText: "{qty} {unit} will be returned to lot {code} · {product}. The loss disappears from all reports; the Activity Log keeps both the original entry (flagged) and the undo.",
    revertDone: "Write-off reverted — stock restored for {code}",
    revertError: "Could not revert the write-off",
    manageReasons: "Manage reasons",
    builtinReasons: "Built-in reasons",
    customReasons: "Your custom reasons",
    noCustomReasons: "No custom reasons yet — add one below.",
    newReason: "New reason",
    newReasonPh: "e.g. Theft, Spillage, Returned by client",
    reasonAdded: "Reason added",
    reasonDeleted: "Reason removed",
    reasonAddError: "Could not save the reason",
    manageHint: "Removing a custom reason only hides it from the write-off picker — past write-offs keep it in reports.",
    exportXlsx: "Export Excel",
    exportError: "Export failed — please try again",
  },
  ncr: {
    title: "Nonconformity Register",
    subtitle: "Every failed quality check across productions, with its corrective action — one HACCP audit list.",
    loadError: "Could not load the register",
    totalCard: "Nonconformities",
    withCorrective: "With corrective action",
    missingCorrective: "Missing corrective action",
    checkCol: "Check",
    issueCol: "Issue",
    operatorCol: "Operator",
    missing: "Missing",
    empty: "No nonconformities in this period — everything within spec.",
    tableNote: "Use the row menu to acknowledge, add the corrective action, resolve or dismiss a nonconformity. Corrective notes typed on the production batch are shown too.",
    statusCol: "NCR status", openCard: "Open", resolvedCard: "Resolved",
    filter_active: "Active (not dismissed)", filter_open: "Open", filter_acknowledged: "Acknowledged", filter_resolved: "Resolved", filter_dismissed: "Dismissed", filter_all: "All",
    status_open: "Open", status_acknowledged: "Acknowledged", status_resolved: "Resolved", status_dismissed: "Dismissed",
    actAcknowledge: "Acknowledge (seen)", actResolve: "Mark as fixed…", actAddCorrective: "Add corrective action…", actEditCorrective: "Edit corrective action…",
    actDismiss: "Dismiss (not applicable)…", actReopen: "Reopen",
    resolveTitle: "Mark nonconformity as fixed", editCorrectiveTitle: "Corrective action", dismissTitle: "Dismiss nonconformity",
    correctivePlaceholder: "What was done to correct the deviation and prevent recurrence…", noteOpt: "Note (optional)",
    dismissReason: "Reason", dismissPlaceholder: "e.g. measurement error, re-checked in range, duplicate entry…",
    dismissHint: "Dismissed items stay in the register and export for audit, but are excluded from open / missing counts.",
    dismissBtn: "Dismiss", resolveBtn: "Mark as fixed",
    correctiveRequired: "Please describe the corrective action before marking as fixed.", reasonRequired: "Please give a reason for dismissing.",
    acknowledged: "Nonconformity acknowledged", resolvedToast: "Nonconformity marked as fixed", dismissed: "Nonconformity dismissed",
    reopened: "Nonconformity reopened", correctiveSaved: "Corrective action saved", saveError: "Could not save the change",
    issueFailOption: "non-conforming option",
    belowMin: "below min",
    aboveMax: "above max",
  },
  perm: {
    title: "Role Permissions",
    subtitle: "Choose which sections each role can access. Admins always have full access.",
    adminNote: "Admin always has access to every section — this cannot be changed.",
    sectionCol: "Section",
    dashboard: "Dashboard",
    alwaysOn: "Always available to everyone",
    save: "Save changes", saving: "Saving…",
    savedToast: "Permissions updated", saveError: "Failed to save permissions", loadError: "Failed to load permissions",
    all: "All", none: "None",
    roleManager: "Manager", roleQa: "QA", roleOperator: "Operator",
    groupGeneral: "General",
  },

  login: {
    title: "LactoPilot",
    subtitle: "Sign in to manage milk, production & HACCP",
    email: "Email", password: "Password", signIn: "Sign in",
    demo: "Demo accounts (click to fill)",
    manager: "Manager", qa: "QA", operator: "Operator",
    failed: "Login failed. Please try again.",
    noAccount: "Don't have an account?", signUpCta: "Create your workspace",
    forgotPassword: "Forgot password?", forgotTitle: "Reset your password",
    forgotHint: "Enter your account email and we'll send you a link to reset your password.",
    forgotSend: "Send reset link", forgotSent: "If that email exists, a reset link has been sent.",
  },

  signup: {
    title: "Create your workspace", subtitle: "Start managing your dairy in minutes",
    company: "Company name", adminName: "Your name", email: "Work email", password: "Password",
    submit: "Create workspace", haveAccount: "Already have an account?", signInCta: "Sign in",
    error: "Could not create workspace. Please try again.",
    captchaRequired: "Please confirm you are not a robot.",
    verifyTitle: "Confirm your email",
    verifySent: "We've sent an activation link to",
    verifyHint: "Open the link in your inbox to activate your account, then sign in. The link is valid for 48 hours.",
    resend: "Resend email",
    resent: "Verification email sent.",
    resendFailed: "Could not resend the email. Please try again.",
    backToLogin: "Back to sign in",
  },

  verify: {
    loading: "Confirming your email…",
    missing: "Invalid link — no verification token found.",
    failed: "This verification link is invalid or has expired.",
    successTitle: "Email confirmed",
    successBody: "Your account is now active. You can sign in.",
    errorTitle: "Verification failed",
    goToLogin: "Go to sign in",
  },

  pwa: {
    title: "Add LactoPilot to your home screen",
    androidHint: "Opens full-screen like an app, with quick shortcuts for CCP readings and milk receptions.",
    iosStep1: "In Safari tap Share", iosStep2: "then \"Add to Home Screen\".",
    genericHint: "In your browser menu choose \"Install app\" or \"Add to Home Screen\" to open LactoPilot full-screen.",
    install: "Install",
  },
  quick: { title: "Quick actions", logCcp: "Record CCP reading", addMilk: "Add milk reception" },
  supplierTrend: {
    title: "Supplier quality trend", subtitle: "Monthly averages weighted by volume, last {n} months", flagged: "{n} supplier(s) slipping",
    months: "{n} mo", colSupplier: "Supplier", colVolume: "Volume", colSignals: "Signals", receptions: "{n} receptions", overTemp: "{n} over temp. limit",
    stable: "Stable", viewAll: "All suppliers", empty: "No milk receptions in this period.", loadError: "Could not load the supplier trend",
    flag_fat_down: "Fat dropping", flag_protein_down: "Protein dropping", flag_temp_up: "Warmer deliveries", flag_temp_over: "Over temp. limit",
    footnote: "Latest month compared with the volume-weighted average of the earlier months in the window. Signals: fat −0.2 pp or more, protein −0.15 pp or more, temperature +1 °C or more, or above the company limit.",
    tabReceptions: "Receptions", tabSuppliers: "Suppliers",
  },
  usage: {
    tab: "Usage", title: "Tenant activity", lastDays: "Last {n} days", loadError: "Could not load usage data", empty: "No organizations yet.", refresh: "Refresh",
    status_active: "Active", status_low: "Low activity", status_dormant: "Dormant", status_never: "Never used",
    hint_active: "Seen in the last 7 days", hint_low: "Seen in the last {days} days, not this week", hint_dormant: "Nothing in the last {days} days", hint_never: "No login or data ever",
    colStatus: "Status", colOrg: "Organization", colUsers: "Active / users", colLastLogin: "Last login", colLastActivity: "Last activity",
    count_activity: "Actions logged", count_productions: "Productions", count_haccp_logs: "CCP checks", count_delivery_notes: "Deliveries", count_orders: "Orders", count_stock_lots: "Stock lots", count_milk_batches: "Milk receptions",
    since: "since {date}", logins: "{n} logins total",
    footnote: "Counts cover the selected window. 'Active users' = users who logged in within the window (login tracking starts from this release). 'Last activity' = newest record created in the tenant.",
    colTrend: "Trend ({n} wk)", trendTooltipTitle: "Actions per week (oldest → newest)", weekAgo: "{n} wk ago", thisWeek: "This week", trendTotal: "{n} actions in {weeks} weeks", trendEmpty: "No activity in the last {weeks} weeks",
    dormantTitle: "Dormant tenant alert", dormantHint: "Every day at {hour}:00 (Bucharest) paying tenants with no login or activity for {days} days are emailed to the superadmin. Each tenant is alerted once per dormancy period; the alert re-arms when it becomes active again.",
    dormantRun: "Check now", dormantRunning: "Checking…", dormantAlerted: "Alert emailed", dormantNoneToast: "Check complete — no new dormant paying tenants", dormantAlertedToast: "Alert emailed for {n} tenant(s)", dormantResolvedToast: "{n} tenant(s) active again — alert re-armed",
    dormantRunError: "Dormant check failed", dormantRecipients: "Recipients: {emails}", dormantLastCheck: "Last check: {when}", dormantNever: "not yet", paidPlan: "Paying", freePlan: "Free / trial",
    colLogins: "Logins ({n} wk)", loginsTooltipTitle: "Logins per week (oldest → newest)", loginsTotal: "{n} logins in {weeks} weeks", loginsEmpty: "No logins recorded in the last {weeks} weeks", loginsNote: "Login tracking per week starts from this release.",
    reengage: "Send email", reengageTitle: "Re-engagement email", reengageDesc: "A friendly check-in to {name}'s admin. Edit the text before sending — replies go to {replyTo}.",
    reengageTo: "To", reengageSubject: "Subject", reengageBody: "Message", reengageSend: "Send email", reengageSending: "Sending…", reengageQuiet: "Quiet for {n} days",
    reengageSent: "Email sent to {emails}", reengageFailed: "Could not send the email", reengageNoRecipient: "This tenant has no admin email", reengageLast: "Last sent {when}", reengageHistory: "Previously sent", reengageDraftError: "Could not prepare the email",
  },
  superadmin: {
    title: "Platform admin", orgs: "Organizations", addOrg: "Add organization",
    create: "Create", empty: "No organizations yet.",
    total: "Total", active: "Active", suspended: "Suspended",
    activeLabel: "Active", suspendedLabel: "Suspended",
    suspendBtn: "Suspend", activateBtn: "Activate", notesPh: "Internal notes (e.g. invoice status)…",
    deleteBtn: "Delete",
    deletedToast: "Tenant \"{name}\" deleted",
    deleteTitle: "Delete tenant permanently?",
    deleteWarning: "This will permanently delete \"{name}\" and ALL of its data — productions, stock, suppliers, users, invoices and everything else. This action cannot be undone.",
    deleteConfirmLabel: "Type the company name ({name}) to confirm:",
    createdToast: "Organization created", statusToast: "Status updated",
    loadError: "Failed to load organizations", actionError: "Action failed",
    tabOrgs: "Organizations", tabInvoices: "Invoices", tabPlans: "Plans",
    plan: "Plan", assignPlan: "Assign plan", billingLocked: "Payment overdue",
    overdue: "overdue", outstanding: "Outstanding", newInvoice: "New invoice",
  },

  backups: {
    tab: "Backups",
    autoTitle: "Automatic daily backup",
    autoHint: "A full platform backup runs every day at {hour}:00 UTC. The last {keep} daily backups are kept. Backups include the whole database and uploaded files.",
    backupNow: "Back up now",
    startedBackup: "Backup started — it will appear in the list shortly.",
    startedRestore: "Restore started — a safety snapshot is being saved first.",
    backingUp: "Backing up",
    restoring: "Restoring",
    failed: "Operation failed",
    listTitle: "Available backups",
    empty: "No backups yet. Run one now or wait for the daily backup.",
    colWhen: "When", colType: "Type", colRecords: "Records", colFiles: "Files", colSize: "Size", colActions: "Actions",
    kind_auto: "Daily", kind_manual: "Manual", kind_safety: "Safety",
    download: "Download", restore: "Restore",
    restoreTitle: "Restore this backup?",
    restoreScope: "What to restore",
    scopePlatform: "Entire platform (all tenants)",
    restoreWarn: "This will replace ALL current platform data with the snapshot from {when}. This affects every tenant and cannot be undone.",
    restoreWarnTenant: "This will replace only {tenant}'s data with the snapshot from {when}. Other tenants are not affected. This cannot be undone.",
    restoreSafety: "A fresh safety backup of the current state is saved automatically right before restoring.",
    restoreConfirmLabel: "Type RESTORE to confirm",
    restoreConfirmBtn: "Restore now",
  },


  billing: {
    // shared / statuses
    invoices: "Invoices", invoice: "Invoice", plan: "Plan", plans: "Plans",
    statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid", statusOverdue: "Overdue",
    number: "Number", org: "Organization", amount: "Amount", currency: "Currency",
    issueDate: "Issue date", dueDate: "Due date", status: "Status", notes: "Notes",
    actions: "Actions", downloadPdf: "PDF", markPaid: "Mark paid", markSent: "Mark sent",
    edit: "Edit", delete: "Delete", create: "Create invoice", save: "Save",
    lines: "Line items", description: "Description", qty: "Qty", unitPrice: "Unit price",
    addLine: "Add line", total: "Total", empty: "No invoices yet.", period: "Period",
    createdToast: "Invoice created", updatedToast: "Invoice updated", deletedToast: "Invoice deleted",
    statusToast: "Invoice status updated", loadError: "Failed to load billing data",
    actionError: "Action failed", confirmDelete: "Delete this invoice?",
    filterAll: "All statuses", monthly: "/mo", yearly: "/yr",
    // plans tab
    priceRon: "Price (RON)", priceEur: "Price (EUR)", features: "Features", planName: "Plan name",
    savePlan: "Save plan", plansSaved: "Plans updated",
    milkLimit: "Milk limit (L/mo)", milkLimitHint: "0 = unlimited", milkUsage: "Milk usage",
    thisMonth: "this month", unlimited: "Unlimited",
    // org-facing billing page
    pageTitle: "Billing", pageSubtitle: "Your subscription plan and invoices",
    currentPlan: "Current plan", noPlan: "No plan assigned",
    outstanding: "Outstanding balance", allPaid: "All invoices are paid. Thank you!",
    lockedBanner: "Your workspace is locked because of an overdue invoice. Please settle the balance below to restore access.",
    contactSupport: "Contact support", myInvoices: "My invoices",
  },

  subproducts: {
    title: "By-products in stock", subtitle: "Traceable by-product lots produced from your runs",
    lotsTitle: "By-product lots", inStockOnly: "In stock only",
    filterName: "By-product", allNames: "All by-products", dateFrom: "From", dateTo: "To", reset: "Reset",
    lotCode: "Lot code", name: "By-product", source: "From production", date: "Date",
    quantity: "Produced", remaining: "Remaining", depleted: "Depleted",
    adjust: "Adjust stock", adjustTitle: "Adjust by-product stock",
    newRemaining: "New remaining quantity", setZero: "Set to 0",
    rangeHint: "Value must be between 0 and {max}",
    reasonLabel: "Reason (for the reduction)", reasonPlaceholder: "Select a reason (optional)",
    noteLabel: "Note (optional)",
    adjusted: "Stock adjusted", adjustError: "Could not adjust the stock",
    empty: "No by-product lots match.", loadError: "Failed to load by-products",
  },

  landing: {
    nav: {
      features: "Features", industries: "Industries", pricing: "Pricing", testimonials: "Testimonials",
      how: "How it works",
      login: "Log in", getStarted: "Get started", openApp: "Open app",
      menu: "Menu", themeToggle: "Toggle theme", tagline: "Dairy Production ERP",
    },
    hero: {
      badge: "ERP for dairy producers",
      title1: "The ERP Built for", title2: "Dairy Producers",
      subtitle: "From milk intake to finished products, LactoPilot helps you plan, produce, trace and grow with confidence.",
      ctaPrimary: "Get started", ctaSecondary: "Watch video",
      proofItems: ["End-to-end traceability", "Real-time insights", "Built for dairy"],
    },
    mock: {
      barTitle: "Dashboard", plant: "Milk Plant 1",
      todayProduction: "Today's Production", milkIntake: "Milk Intake", yield: "Yield (This Month)", waste: "Waste",
      overview: "Production Overview", thisMonth: "This month", lastMonth: "Last month",
      topProducts: "Top Products", recentBatches: "Recent Batches", qualityAlerts: "Quality Alerts",
      batchId: "Batch ID", product: "Product", qty: "Qty", status: "Status",
      inProgress: "In Progress", completed: "Completed", viewAll: "View all",
      cheese: "Cheese", yogurt: "Yogurt", milk: "Milk", other: "Other",
    },
    features: {
      title: "All the tools you need in one platform",
      subtitle: "Integrated modules covering the whole flow — from milk intake to finished, traceable products.",
      items: [
        { icon: "CalendarDays", title: "Production Planning", desc: "Plan recipes, schedules and resources with ease." },
        { icon: "Boxes", title: "Batch Tracking", desc: "Track every batch in real-time from start to finish." },
        { icon: "ClipboardList", title: "Recipe Management", desc: "Standardize recipes and maintain consistency." },
        { icon: "Package", title: "Inventory Control", desc: "Manage raw materials, packaging and finished goods." },
        { icon: "ShieldCheck", title: "Traceability", desc: "Full traceability from farm to finished product." },
        { icon: "FlaskConical", title: "Quality Control", desc: "Ensure quality with checks and lab integrations." },
        { icon: "Truck", title: "Sales & Distribution", desc: "Manage orders, shipments and customer relations." },
        { icon: "PieChart", title: "Cost Analysis", desc: "Analyze costs, yields and improve profitability." },
      ],
    },
    stats: {
      title: "Real-time insights. Better decisions.",
      subtitle: "Monitor what matters and drive your business forward.",
      items: [
        { icon: "TrendingUp", value: "11.42%", label: "Average Yield", delta: "+0.6% vs last month", positive: true },
        { icon: "Droplet", value: "98.6%", label: "Milk Utilization", delta: "+2.1% vs last month", positive: true },
        { icon: "Tag", value: "1.35%", label: "Waste Rate", delta: "-0.3% vs last month", positive: true },
        { icon: "Layers", value: "2,450", label: "Active SKUs", delta: "+120 this month", positive: true },
        { icon: "DollarSign", value: "+18.7%", label: "Gross Margin", delta: "+3.4% vs last month", positive: true },
      ],
    },
    industries: {
      title: "Built for every type of dairy business",
      subtitle: "From artisan dairies to multi-site processors — LactoPilot adapts to how you work.",
      items: [
        { icon: "Layers", title: "Cheese Factories", desc: "Manage complex recipes and aging processes." },
        { icon: "CupSoda", title: "Yogurt Producers", desc: "Maintain consistency and quality at scale." },
        { icon: "Droplets", title: "Mozzarella Plants", desc: "Optimize yield and production efficiency." },
        { icon: "Milk", title: "Milk Processors", desc: "Streamline pasteurization and packaging." },
        { icon: "Home", title: "Artisan Dairies", desc: "Grow your business while staying compliant." },
      ],
    },
    how: {
      title: "Implementation without the headaches",
      subtitle: "We guide you step by step, from setup to go-live.",
      steps: [
        { title: "We map your flows", desc: "Reception → production → delivery, tailored to how your plant actually works." },
        { title: "We import & configure", desc: "Nomenclatures, product types and role-based access for your whole team." },
        { title: "We turn on HACCP", desc: "CCPs, alerts and audit-ready reports so you stay compliant from day one." },
        { title: "Go-live & support", desc: "We launch together, then keep improving with ongoing Romanian-language support." },
      ],
    },
    testimonials: {
      title: "Plants that work smarter",
      subtitle: "Built with and for dairy and cheese processors.",
      items: [
        { quote: "We replaced spreadsheets and paper logs. Now traceability from milk to delivery takes seconds, not hours.", name: "Andrei Munteanu", role: "Plant Manager, cheese factory" },
        { quote: "HACCP audits used to be stressful. With LactoPilot, our reports are always ready and exceptions are tracked.", name: "Ioana Petrescu", role: "QA Lead, dairy processor" },
        { quote: "Production planning and stock finally talk to each other. Fewer mistakes, better yields.", name: "Cristian Dobre", role: "Owner, family dairy" },
      ],
    },
    pricing: {
      title: "Simple, transparent pricing",
      subtitle: "Start with one location and scale to multi-site operations.",
      perMonth: "/month", popular: "Most popular", customPrice: "Custom", contactCta: "Talk to us",
      tiers: [
        { name: "Start", for: "a single location", price: "€149", cta: "Get started", highlight: false,
          features: ["Milk reception & suppliers", "Basic production tracking", "Stock & orders", "Email support"] },
        { name: "Pro", for: "production + HACCP", price: "€349", cta: "Choose Pro", highlight: true,
          features: ["Everything in Start", "Full HACCP & compliance reports", "Planning & Pearson calculator", "Workforce & time clock", "Priority support"] },
        { name: "Enterprise", for: "multi-site + integrations", price: "Custom", cta: "Talk to us", highlight: false,
          features: ["Everything in Pro", "Multi-site management", "Custom integrations", "Dedicated onboarding", "SLA & training"] },
      ],
    },
    finalCta: {
      eyebrow: "Ready to take control?",
      title: "Let's grow your dairy business together.",
      subtitle: "Join dairy producers who trust LactoPilot to run their operations smarter.",
      ctaPrimary: "Get started",
      ctaSecondary: "Contact sales",
    },
    footer: {
      tagline: "Operations, traceability and food safety for modern dairy plants.",
      product: "Product", company: "Company", legal: "Legal",
      rights: "All rights reserved.", madeIn: "Built for the dairy industry",
      links: {
        features: "Features", pricing: "Pricing", how: "How it works",
        about: "About", contact: "Contact", support: "Support",
        privacy: "Privacy", terms: "Terms",
      },
    },
  },

  client: {
    store: "LactoPilot Store", portal: "Client portal", shop: "Shop", myOrders: "My Orders",
    clientRole: "Client",
    catalogEmpty: "No products available right now.",
    perUnit: "per {unit}", madeToOrder: "made to order",
    available: "{qty} {unit} available", outOfStock: "Out of stock",
    packageType: "Package type", inStock: "in stock",
    priceNotSet: "Price not set", priceNotSetHint: "This package is available but has no price yet. Ask us to set a price to order it.",
    addToOrder: "Add to order", cart: "Cart", placeOrder: "Place order",
    yourOrder: "Your order", emptyCart: "Your cart is empty.",
    orderPlaced: "Order placed successfully", deliveryAddress: "Delivery address",
    noOrders: "You have no orders yet.", orderCode: "Order", placedOn: "Placed on",
    yourPrices: "{name} · your prices (Level {level})",
    levelPricing: "Level {level} pricing",
    qtyPlaceholder: "Qty", addProductsHint: "Add products to build your order.",
    enterQty: "Enter a quantity", onlyAvailable: "Only {qty} {unit} available",
    added: "{product} added", myOrdersTitle: "My orders",
    orderHistory: "Order history", noOrdersYet: "You haven't placed any orders yet.",
    colItems: "Items", emptyCartToast: "Your cart is empty",
    failedPlace: "Failed to place order",
  },

  status: {
    placed: "Placed", confirmed: "Confirmed", delivered: "Delivered", cancelled: "Cancelled",
  },

  prices: {
    addEdit: "Add / edit price", title: "Price lists", empty: "No price entries yet.",
    level: "Level {n}", addEntry: "Add price entry", editEntry: "Edit price entry",
    saved: "Price list saved", entryRemoved: "Entry removed",
  },

  productPackaging: {
    title: "Product Packaging",
    subtitle: "Choose which package sizes each product is sold in. These selections drive the packaging dropdowns in Orders, the Client Portal, Price Lists and Stock. Products with no selection show all packaging.",
    addLabel: "Add packaging label",
    noLabels: "No packaging labels yet. Add your first one to start assigning them to products.",
    noProducts: "No products yet. Add product types in Company Settings first.",
    allAvailable: "All packaging",
    nSelected: "{n} selected",
    kgNote: "Bulk \"kg\" is always available as the master stock unit.",
    stockMode: "Stock mode", modeBulk: "Bulk kg", modePackage: "By package",
    modeBulkHint: "Production yield is added to one bulk kg pool; package availability is calculated from it.",
    modePackageHint: "Production yield is stored as separate package lots; orders deduct from those lots.",
    labelName: "Packaging label",
    labelPlaceholder: "e.g. 400g, 8kg, 330ml, 200 jar",
    netKg: "Net weight (kg, optional)",
    netHint: "Used to convert packages to/from bulk kg (e.g. 400g = 0.4).",
    labelRequired: "Packaging label is required",
    eanTitle: "EAN barcode per package",
    eanCodeTitle: "EAN & code per package",
    eanPlaceholder: "e.g. 5941234567890",
    codePlaceholder: "Code",
    eanCodeHint: "EAN barcode and product code are printed per package on the Declaration of Conformity.",
    eanSaved: "EAN saved",
    codeSaved: "Code saved",
  },

  products: {
    title: "Products",
    subtitle: "Manage your products and the subproducts (by-products) each one yields. Subproducts can be quickly selected when recording a production.",
    addProduct: "Add product", productName: "Product name",
    productPlaceholder: "e.g. Telemea, Mozzarella",
    nameRequired: "Product name is required",
    labelName: "Commercial / label name",
    labelNamePlaceholder: "e.g. Fresh Cream Cheese 'Cremvit'",
    labelNameHint: "Shown on the Declaration of Conformity instead of the internal product name. Leave empty to use the product name.",
    empty: "No products yet. Add your first product to get started.",
    active: "Active", inactive: "Inactive",
    subproducts: "Subproducts", subproductsCount: "{n} subproducts",
    noSubproducts: "No subproducts yet.",
    addSubproduct: "Add subproduct", subproductPlaceholder: "e.g. Whey, Cream",
    subproductsHint: "By-products yielded when making this product (e.g. Whey, Cream).",
    created: "Product created", updated: "Product updated", deleted: "Product deleted",
    saveFailed: "Could not save the product", deleteFailed: "Could not delete the product",
    merge: "Merge / consolidate", mergeTitle: "Merge \"{name}\" into another product",
    mergeHint: "Reassigns all productions, stock, orders, delivery notes and packaging from this product to the one you choose, then deletes this (duplicate) product. Useful to remove a duplicate that can't be deleted because it is still in use.",
    mergeInto: "Merge into", mergeSelect: "Choose the product to keep", mergeConfirm: "Merge & delete",
    merged: "Merged \"{from}\" into \"{to}\"", mergeFailed: "Could not merge the product",
    confirmDelete: "Delete \"{name}\"? This cannot be undone.",
    renameTitle: "Edit product", rename: "Edit",
    code: "Code", codePlaceholder: "e.g. TLM-01", storageTemp: "Storage temp.", tempPlaceholder: "e.g. +2…+4 °C",
    duplicateSub: "That subproduct already exists.",
    days: "days",
    variantsTitle: "Sellable sub-variants",
    variantsEmpty: "No sub-variants yet.",
    variantsHint: "Sub-variants (e.g. \"Fresh Telemea\") are sold as-is with their own shorter shelf life. They appear in stock and can be sold like any product.",
    variantNamePlaceholder: "e.g. Fresh Telemea",
    variantShelfPlaceholder: "Shelf life",
    addVariant: "Add variant",
    variantShelfDays: "{n} days shelf life",
    variantNoShelf: "no shelf life set",
    variantAdded: "Sub-variant \"{name}\" added",
    variantRemoved: "Sub-variant \"{name}\" removed",
    variantUpdated: "Sub-variant \"{name}\" updated",
    variantError: "Could not update sub-variant",
    unitKg: "kg", unitL: "L", unit: "Unit",
    yields: "Subproducts produced (outputs)",
    yieldsHint: "By-products yielded when making this product (e.g. Whey, Cream). Set the unit for each. The droplet icon marks subproducts deducted from milk by default in the specific-consumption calc.",
    deductOn: "Deducted from milk by default (specific consumption) — click to disable",
    deductOff: "Not deducted from milk — click to deduct by default (specific consumption)",
    deductToggle: "Toggle milk deduction for",
    lotPrefix: "Lot prefix",
    lotPrefixHint: "Short letters shown at the start of generated lot numbers (e.g. TL → TL0207-001). Max 5 letters/digits. Leave empty for no prefix.",
    consumes: "Subproducts used as raw material (inputs)",
    consumesHint: "Subproducts this product consumes when produced (e.g. Ricotta uses Whey, Butter uses Cream).",
    consumesEmpty: "No input subproducts yet.",
    pickToConsume: "Add a subproduct to consume",
    noYieldsDefined: "Define subproducts on other products first (e.g. Whey, Cream) so they can be selected here.",
  },

  clients: {
    add: "Add client", title: "Clients", empty: "No clients yet.",
    level: "Level {n}", contact: "Contact", portal: "Portal", none: "none",
    portalAccess: "Portal access", editClient: "Edit client", addClient: "Add client",
    companyName: "Company / client name", priceLevel: "Price level", contactPerson: "Contact person",
    taxId: "Tax ID / VAT", regNo: "Reg. no", createLogin: "Create portal login",
    addresses: "Delivery addresses", addAddress: "Add address", noAddresses: "No addresses yet. Add one for deliveries.",
    addrLabelPh: "Label (e.g. Warehouse)", addrLinePh: "Full address", defaultAddr: "Default", setDefault: "Set default",
    docRefs: "Document references", reqCustomOrderNo: "Requires custom order number",
    reqCustomOrderNoHint: "Show an order-number field when creating this client's orders.",
    reqInvoiceNo: "Requires invoice number",
    reqInvoiceNoHint: "Show an invoice-number field for this client's orders & delivery notes.",
    createLoginHint: "Let this client log in to place orders.",
    loginEmail: "Login email", password: "Password", saveAccess: "Save access",
    portalDesc: "Set or reset the client's login email and password.",
    nameRequired: "Client name is required", loginNeeds: "Portal login needs an email and password",
    saved: "Client saved", removed: "Client removed", portalUpdated: "Portal access updated",
    emailPwRequired: "Email and password are required",
  },

  stock: {
    addLot: "Add stock lot", title: "Stock lots", empty: "No stock lots yet.",
    onHand: "On hand", reserved: "Reserved", available: "Available", expiry: "Expiry",
    source: "Source", lotCode: "Lot code", editLot: "Edit stock lot",
    fromProduction: "From production (optional)", manualEntry: "Manual entry",
    qtyGt0: "Quantity must be greater than 0", saved: "Stock lot saved", removed: "Lot removed",
    availability: "Availability by product", lots: "lots",
    liveAvailability: "Live availability",
    hideOutOfStock: "Hide out of stock",
    totalAvailableKg: "Total available:",
    fromExistingLot: "From existing lot",
    newStock: "New stock",
    searchLotOrProduct: "Search by lot or product…",
    noLotMatch: "No lot matches your search.",
    rebatchTitle: "Rebatch from lot",
    rebatchHint: "Repackage part of an existing lot into a smaller batch; the equivalent kg is deducted from the source lot. Expiry is set to today + refrigerated shelf life, but never later than the parent lot's expiry; today is recorded as the packaging date.",
    newPackaging: "New packaging",
    rebatchUses: "Uses {kg} kg from {source} (~{avail} kg available)",
    rebatchedToast: "Created {code} · took {kg} kg from {source}",
    rebatchedKeptToast: "Created {code} · took {kg} kg · kept the parent lot number",
    keepSourceLot: "Keep the parent lot number",
    keepSourceLotHint: "The quantity is still deducted from the source lot, but the new batch keeps the parent lot number — and therefore its expiry date (one lot number = one expiry).",
    rebatchExpiryInherited: "Inherited from the parent lot ({date}). Untick 'keep the parent lot number' to set a different expiry.",
    rebatchExpiryTooLate: "A repackaged batch cannot expire after its parent lot ({date}).",
    rebatchResult: "Result: lot {code} · expires {date}", rebatchNewNumber: "new number",
    expiryMismatch: "Parent lot expires {date}", expiryMismatchHint: "This batch kept the parent lot number but has a different expiry — edit it to match the parent.",
    outOfStock: "Out of stock",
    allOutOfStock: "All products are out of stock.",
    noSellable: "No sellable products yet. Add price list entries and stock lots.",
    nPackagings: "{n} packagings",
    addFirstLot: "Add your first lot", fromProductionCol: "From production",
    onHandReservedLine: "on hand {oh} · reserved {r}",
    expired: "Expired", daysLeft: "{n}d left",
    expiringSoon: "Expiring soon", noExpiringSoon: "No lots are expiring within 7 days.",
    writeOff: "Write off",
    writeOffNote: "Write-off — expired lot",
    writeOffConfirmTitle: "Write off lot?",
    writeOffConfirmText: "Lot {code} · {product}: choose how much of the {qty} {unit} in stock to remove. The adjustment and reason are recorded in the activity log and stock movement reports.",
    writeOffDone: "Lot {code} written off",
    writeOffQtyLabel: "Quantity to write off",
    writeOffQtyMax: "of {qty} {unit} in stock",
    writeOffQtyInvalid: "Enter a quantity between 0 and {qty} {unit}",
    writeOffAll: "All",
    writeOffReason: "Write-off reason",
    writeOffNoteOptional: "Note (optional)",
    reason: { expired: "Expired", damaged: "Damaged", quality: "Quality issue", other: "Other" },
    kgHintNoNet: 'Set a net weight for "{unit}" in Settings to deduct bulk kg automatically.',
    kgHint: 'Packaging from bulk: ≈ {kg} kg will be deducted from bulk "kg" {product} stock.',
    savedKgDeducted: "Stock lot saved · {kg} kg deducted from bulk {product} stock",
    savedKgShort: "Stock lot saved · only {deducted} of {required} kg deducted from bulk ({short} kg short)",
    failedSave: "Failed to save lot", failedDelete: "Failed to delete", expiryDate: "Expiry date",
    noPrice: "No price", madeOn: "Made on",
    allProducts: "All products", dateField: "Date field", noMatch: "No lots match your filters.",
    entryDate: "Stock date", entryDateHint: "When this stock became available — used to show it for orders dated on/after this day.",
    storageState: "Storage", frozen: "Frozen", refrigerated: "Refrigerated",
    takeOutOfFreezer: "Take out of freezer",
    takeOutSuccess: "{code} moved to refrigerated · expires {date}",
    takeOutFailed: "Failed to take lot out of freezer",
    adjustRemaining: "Adjust remaining", remainingAdjusted: "Remaining quantity updated",
    adjustRemainingHint: "Directly set the on-hand quantity for this lot (stock correction / stocktake). This does not change the lot's original quantity.",
    lotQty: "Lot quantity", adjustRange: "Must be between 0 and {max} {unit}.", adjustNotePh: "Reason for adjustment (optional)",
    refrigeratedHint: "Expiry auto-set to stock date + refrigerated shelf life ({days} days). You can still override it.",
    refrigeratedNoDsl: "Set a refrigerated shelf life for this product to auto-calculate the refrigerated expiry.",
    refrigeratedStateHint: "A label only — the expiry stays as entered. Refrigerated shelf life applies when taking a lot out of the freezer or repackaging into smaller portions.",
    frozenHint: "Frozen stock keeps its full shelf life. Refrigerated shelf life applies only when the lot is taken out of the freezer or repackaged.",
  },

  orders: {
    newOrder: "New order", title: "Orders", empty: "No orders yet.",
    order: "Order", client: "Client", items: "Items", placed: "Placed",
    createDelivery: "Create delivery note", viewOrder: "View order",
    confirm: "Confirm", cancel: "Cancel order",
    createOrder: "Create order", selectClient: "Select client", addItem: "Add item",
    deliveryAddress: "Delivery address", selectAddress: "Select delivery address",
    noClientAddress: "This client has no saved address — manage addresses on the Clients page.",
    selectClientFirst: "Select a client to choose a delivery address.",
    customOrderNo: "Custom order number", customOrderNoPh: "e.g. PO-12345",
    invoiceNo: "Invoice number", invoiceNoPh: "e.g. INV-2026-001",
    selectProduct: "Select product", selectPackaging: "Select packaging",
    deleteOrder: "Delete order?", deleteOrderDesc: "Order {code} for {client} will be permanently removed. Any reserved stock is released. This cannot be undone.",
    orderDeleted: "Order {code} deleted", saved: "Order created",
    failedDelete: "Failed to delete order", statusToast: "Order {status}", deleteBtn: "Delete order",
    addOneItem: "Add at least one item", orderPlacedToast: "Order placed", failedPlace: "Failed to place order",
    editBtn: "Edit order", editOrder: "Edit order", orderUpdatedToast: "Order updated",
    clientOption: "{name} (Level {level})", noSellable: "No sellable products. Set price lists and stock first.",
    availLine: "avail {qty} {unit} · {price}/{unit}", placeOrder: "Place order", unitPrice: "Unit price",
    orderDate: "Order date", orderDateHint: "Defaults to today.", backdateHint: "Backdated — showing stock that was valid on this date.",
    deliveryDate: "Delivery date", deliveryDateRequired: "A delivery date is required",
    deliveryDaysHint: "Deliveries allowed on: {days}", deliveryDayNotAllowed: "Deliveries are only allowed on: {days}",
    dueBy: "Due by (on/before)", dueOn: "Delivery day (exact)",
    loadingStock: "Loading available stock…", noSellableDate: "No stock was available on the selected date.",
    selectSourceQty: "Select a source and quantity for at least one line",
    dnCreated: "Delivery note created · stock deducted", failedDn: "Failed to create delivery note",
    deliverDesc: "select orders to consolidate and split lines across multiple lots.",
    ordersToInclude: "Orders to include", allocateFrom: "Allocate from stock / batches",
    bulkDeliverBtn: "Bulk delivery ({orders} orders · {clients} clients)", bulkSelectAll: "Select all open orders on this page", bulkSelectOrder: "Select order for bulk delivery",
    bulkTitle: "Bulk delivery — one note per client", bulkDesc: "{clients} clients · {orders} orders. Lots were pre-allocated first-expiry-first from a shared stock pool; adjust any lot or quantity before creating.",
    bulkNotes: "Note on all delivery notes (optional)", bulkAutoAllocate: "Re-allocate automatically", bulkNoClients: "No clients left to deliver.",
    bulkState_ok: "Ready", bulkState_short: "Incomplete", bulkState_over: "Over-allocated", bulkRemoveClient: "Leave this client out",
    bulkUsedElsewhere: "{n} to other clients", bulkLotOver: "Lot {code} has only {n} available across all clients.",
    bulkOverAllocated: "Some lots are allocated beyond their available stock — fix the red lines first.",
    bulkReady: "{ready} / {total} clients ready", bulkSubmit: "Create {n} delivery notes & deduct stock", bulkCreated: "{n} delivery notes created",
    selectOneOrder: "Select at least one order.", ordered: "ordered {n}", allocated: "allocated {a} / {o}",
    selectSource: "Select source", noStock: "No stock available", sourceLeft: "{n} left",
    expShort: "exp", batchTag: "(batch)", addAnotherLot: "Add another lot", createDeduct: "Create & deduct stock",
    deliveryExpiry: "Delivery expiry", deliveryExpiryHint: "Est. from order date",
    shortShelfLifeTitle: "Confirm short shelf life",
    shortShelfLifeConfirm: "Some stock expires within the minimum sale window. Sell anyway?",
    sellAnyway: "Sell anyway",
    lowStockWarning: "Order saved with insufficient stock for {products}. Produce it before confirming.",
    lowStockBadge: "Low stock",
    confirmAnyway: "Confirm anyway",
    insufficientStockTitle: "Insufficient stock",
    insufficientStockBody: "There isn't enough stock to fulfil this order yet. You can confirm anyway and produce it later.",
    insufficientStockLine: "requested {requested}, available {available}",
  },

  deliv: {
    title: "Delivery notes", empty: "No delivery notes yet. Create one from an order or generate them above.",
    prepTitle: "Delivery prep — goods to load",
    prepSubtitle: "Total goods required for all open orders scheduled on the selected day.",
    deliveryDay: "Delivery day", generateNotes: "Generate notes",
    noOpenOrders: "No open orders to deliver on {day}.",
    loadByPackaging: "Load by packaging", totalKg: "≈ {kg} kg total",
    packs: "packs", detailedBreakdown: "Detailed breakdown", packsQty: "Packs / qty",
    ordersForRun: "Orders for this run ({n})",
    deliveryNote: "Delivery note", deleteTitle: "Delete delivery note?",
    deleteDesc: "Deleting {code} ({client}) will restore the deducted stock to its lots/batches and reopen the linked order(s) as confirmed. This cannot be undone.",
    deleteAndRestore: "Delete & restore stock",
    downloadCert: "Download Certificate of Conformity", noteDeleted: "Delivery note {code} deleted · stock restored · orders reopened",
    failedDownload: "Failed to download certificate", noOpenDay: "No open orders for this day",
    createdToast: "{n} delivery note(s) created · stock deducted", skippedToast: "{n} skipped: {details}",
    nothingToDeliver: "Nothing to deliver", failedGenerate: "Failed to generate delivery notes",
    approxKg: "≈ kg", itemsCount: "{n} items", downloadCertShort: "Download certificate", deleteBtn: "Delete delivery note",
    printSelected: "Print selected ({n})", printSelectedEmpty: "Print selected", selectAll: "Select all delivery notes",
    selectOne: "Select {code}", selectToPrint: "Select at least one delivery note to print", printedToast: "{n} certificate(s) ready",
    customOrderNo: "Order number", invoiceNo: "Invoice number", dispatchNo: "Dispatch note number", invoiceDocType: "Document type", invoiceTypeInvoice: "Invoice", invoiceTypeDispatch: "Dispatch note", notSet: "Not set", saveRefs: "Save references", refsSaved: "References updated",
    colCode: "Code", colLot: "Lot", colPackaged: "Packaged", colDaysLeft: "Days left", colExpiry: "Expiry", colDelExpiry: "Delivery expiry", colStorage: "Storage temp.",
    expired: "expired", daysShort: "d", downloadPacking: "Packing list", printCerts: "Certificates of conformity", printPackingLists: "Packing lists",
  },

  settings: {
    title: "Company Settings", company: "Company details", logo: "Logo",
    packagingOptions: "Packaging options",
    packagingHint: "Pack formats available when creating stock lots and price lists. Net weight (kg) is used to convert packed-on-order quantities and to auto-create stock from production yields.",
    newPackaging: "New packaging label", net: "Net (kg)", saved: "Settings saved",
    failedSave: "Failed to save", netGt0: "Net weight must be greater than 0",
    packAdded: "Packaging added", failedAdd: "Failed to add", packRemoved: "Packaging removed",
    failedRemove: "Failed to remove", failedUpdate: "Failed to update", netUpdated: "{label} net weight updated",
    packRenamed: 'Renamed "{from}" to "{to}"', renameHint: "Rename packaging (updates all linked records)",
    productTypes: "Product types", productTypesHint: "Manage the products you make. New types instantly appear in Production, Stock, Planning and Price Lists. Deactivate to hide from dropdowns without deleting.",
    newProductType: "New product type", productTypePlaceholder: "e.g. Fresh Cottage Cheese", typeAdded: "Product type added", typeRemoved: "Product type removed",
    saveBtn: "Save settings", legalName: "Legal name", coc: "Certificate of Conformity",
    docTitle: "Document title", declarationText: "Declaration text",
    declarationHint: "Shown as the conformity statement on every certificate.",
    footerText: "Footer text (optional)", activeToggle: "Active — click to deactivate",
    inactiveToggle: "Inactive — click to activate", active: "active", inactive: "inactive",
    netShort: "net", packPlaceholder: "e.g. 500g tray", packedTitle: "Sold by package (from stock)",
    packedHint: "Mark products you keep in bulk kg but sell to clients as packages. Add a price per package size in Price Lists; available packages = how many fit in the bulk-kg stock.",
    minMatLabel: "Minimum maturation days before sale",
    minMatHint: "Packed-on-order products can only be sold once a batch has matured at least this many days.",
    packedBadge: "Sold by package: {products}",
    workSection: "Work schedule", workSectionHint: "Used for time-clock overtime highlighting and 'daily on working days' reminders.",
    lotNumbering: "Lot numbering",
    lotNumberingHint: "Controls how lot numbers are generated for productions and manually added stock lots.",
    lotIncludeYear: "Include year in lot numbers",
    lotIncludeYearHint: "When on, new lot numbers use DDMMYY-NNN (e.g. 020726-003). When off, DDMM-NNN (e.g. 0207-003). Existing lots keep their numbers.",
    lotFormatExample: "New lots will look like:",
    yieldCalc: "Yield calculation & milk",
    yieldCalcHint: "Used for the specific-consumption calculation shown on each production.",
    milkDensity: "Milk density (kg/L)",
    milkDensityHint: "Converts milk litres to kg. Standard whole milk is around 1.03 kg/L.",
    milkMaxTemp: "Max milk temperature at reception (°C)",
    milkMaxTempHint: "Receptions above this temperature are flagged in the list, the form and the printed reports. EU standard for cooled raw milk: 6 °C.",
    digestTitle: "Weekly email report",
    digestHint: "Every Friday at 17:00 (Romania time): over-target batches, production summary, stock movements and lots expiring in the next 7 days.",
    digestEnabled: "Send weekly report",
    digestEnabledHint: "On by default. Turn off to stop the automatic Friday email.",
    digestEmails: "Recipients",
    digestEmailsHint: "Email addresses separated by comma or new line (max 20). Don't forget to save.",
    digestTestBtn: "Send test now",
    digestSending: "Sending…",
    digestTestSent: "Test report sent to: {emails}",
    digestTestFailed: "Could not send the test report",
    digestSectionsTitle: "Email sections",
    digestSectionsHint: "Choose which sections appear in the Friday email.",
    digestSectionsAdminOnly: "Only admins can change the sections.",
    digestSecProduction: "Production summary",
    digestSecOverTarget: "Batches over consumption target",
    digestSecStock: "Stock movements",
    digestSecExpiring: "Expiring lots (next 7 days)",
    digestSecLosses: "Losses by reason",
    digestSecNcr: "Quality nonconformities",
    digestSecAudit: "Yield edits & stock adjustments",
    digestPerRecipientTitle: "Sections per recipient",
    digestPerRecipientHint: "Each recipient gets the company sections unless customized below.",
    digestRecipientDefault: "Company sections",
    digestRecipientCustom: "Custom",
    adminAlertsTitle: "Admin alerts",
    adminAlertsEmailLabel: "Alert emails for changes (yield & stock)",
    adminAlertsHint: "Choose how you receive emails when a yield is edited, stock is adjusted or a write-off is undone.",
    adminAlertsPersonal: "Personal preference — bell alerts stay active either way.",
    restockAlertsTitle: "Stock set from 0 to a value (restock)", restockAlertsHint: "Choose whether these adjustments should reach you. Write-offs and other corrections always alert.",
    restockAlertEmail: "Email", restockAlertBell: "Bell notification", restockAlertOn: "Restock alerts enabled", restockAlertOff: "Restock alerts muted",
    adminAlertsModeInstant: "Instant",
    adminAlertsModeInstantHint: "One email the moment each change happens.",
    adminAlertsModeDaily: "Daily summary (18:00)",
    adminAlertsModeDailyHint: "One tidy evening email bundling the whole day's changes, with the day's write-off total.",
    adminAlertsModeOff: "Off",
    adminAlertsModeOffHint: "No emails — you keep the bell alerts.",
    adminAlertsSaved_instant: "Instant email alerts turned on",
    adminAlertsSaved_daily: "Daily summary enabled — one email every evening at 18:00",
    adminAlertsSaved_off: "Email alerts muted — you'll keep bell alerts",
    adminAlertsTestBtn: "Send today's summary",
    adminAlertsTestSent: "Today's summary sent to your email ({n} changes)",
    adminAlertsError: "Could not save the preference",
    brandingSection: "Branding", brandingHint: "Customize how your workspace looks — shown in the sidebar and header.",
    brandName: "Display name", brandNameHint: "Shown next to the logo. Leave empty for 'LactoPilot'.",
    uploadLogo: "Upload logo", logoUploaded: "Logo uploaded", accentColor: "Accent color",
    standardWorkdayHours: "Standard hours per day", standardWorkdayHint: "Shifts longer than this are highlighted as overtime.",
    workingDays: "Working days", workingDaysHint: "Reminders set to 'daily on working days' skip the unchecked days. These days are also treated as working in the Time Clock.",
    deliveryDays: "Delivery days", deliveryDaysHint: "Orders can only be scheduled for delivery on the selected weekdays.",
    dayMon: "Mon", dayTue: "Tue", dayWed: "Wed", dayThu: "Thu", dayFri: "Fri", daySat: "Sat", daySun: "Sun",
    backupTitle: "Backup & export",
    backupHint: "Download a full backup of your company's data — productions, stock, sales, milk intake, suppliers, HACCP, tasks, settings and more.",
    backupJson: "Download JSON backup",
    backupExcel: "Download Excel workbook",
    backupPreparing: "Preparing…",
    backupStarted: "Your backup download has started",
    backupFailed: "Could not generate the backup",
    backupNote: "JSON is a complete backup (best for safekeeping / re-import). Excel is a readable workbook with one sheet per data type.",
  },

  prod: {
    statuses: "Statuses", newProduction: "New production",
    runsTitle: "Production runs", ofTotal: "of {n}",
    table: "Table", board: "Board",
    allProducts: "All products", allStatuses: "All statuses",
    shelfLife: "Shelf life", maturationLabel: "Maturation",
    emptyStart: "No productions yet. Start your first run.",
    noMatch: "No production runs match the selected filters.",
    clearFilters: "Clear filters",
    operator: "Operator", milkL: "Milk (L)", yieldKg: "Yield (kg)",
    byproducts: "By-products", coldRoomReady: "Cold-room ready", yieldEff: "Yield eff.",
    ready: "ready", daysShort: "{n}d", flagN: "{n} flag", ok: "OK",
    statusArrow: "Status → {label}", failedStatus: "Failed to update status",
    movedTo: "{code} → {label}", failedMove: "Failed to move production",
    dropHere: "Drop here", exp: "exp",
    statusRenamed: "Status renamed", renameFailed: "Rename failed",
    colorFailed: "Color update failed", updateFailed: "Update failed",
    reorderFailed: "Reorder failed", statusDeleted: "Status deleted",
    deleteFailed: "Delete failed", enterStatusName: "Enter a status name",
    statusAdded: "Status added", failedAddStatus: "Failed to add status",
    manageStatuses: "Manage production statuses",
    statusManagerHint: "Reorder the workflow, rename stages, pick colors, mark a stage as final (completed), or mark the Maturation stage that starts the cold-room countdown. Renaming updates all productions automatically.",
    final: "Final", addCustomStatus: "Add a custom status",
    statusPlaceholder: "e.g. Quality hold", pickColor: "Pick color",
    columns: "Columns", extraColumns: "Extra columns",
    expirationDate: "Expiration date", coldRoomReadyDate: "Cold-room ready date",
    yieldEfficiency: "Yield efficiency", byproductsYield: "By-products (yield)",
    qcFields: "QC fields", noQcFields: "No QC fields available yet.",
    appliedStd: 'Applied standardization "{name}"',
    addMilkQty: "Add at least one milk batch with quantity",
    needRawOrYield: "Add some raw material (milk or a by-product) or at least a yield.",
    correctiveAction: "Corrective action",
    correctivePh: "Describe what was done to fix the non-conformity (e.g. batch isolated, re-checked, discarded)...",
    correctiveRequired: "A corrective action note is required for '{field}' (non-conforming option selected)",
    created: "Production created", failedCreate: "Failed to create production",
    notFound: "Production not found",
    startPlanned: "Start planned production", newRun: "New production run",
    tabProduct: "1. Product", tabMilk: "2. Raw materials", tabQc: "3. QC", tabReview: "4. Review",
    productType: "Product type", namePlaceholder: "Name", nextMilk: "Next: Raw materials",
    selectOperator: "Select operator",
    madeOnDate: "Date made", madeOnHint: "When this batch was actually produced.",
    applyStd: "Apply a saved standardization plan (optional)",
    loadStd: "Load fat standardization…", stdOption: "{name} · target {fat}%",
    addMilkBatch: "Add milk batch", selectBatch: "Select an available batch",
    noMilk: "No available milk. Add milk intake first.",
    batchOption: "{code} · {l}L · Fat {f}%", availFat: "Available {l} L · Fat {f}%",
    totalMilk: "Total milk:", nextQc: "Next: QC",
    qcTemplate: "QC template", selectTemplate: "Select template",
    starterTag: "(starter)", noTemplates: "No templates for {product}. Create one in QC Templates.",
    nextReview: "Next: Review",
    product: "Product",
    targetFat: "Target fat (%)", yield: "Yield",
    yieldPreview: "= {kg} kg · auto-creates a stock lot",
    yieldNoNet: 'Set net weight for "{unit}" in Settings to convert to kg.',
    addPackaging: "Add packaging", yieldTotalKg: "Total: {kg} kg",
    perLineStorageHint: "Choose Frozen or Refrigerated per packaging. Frozen lots keep the full shelf life; refrigerated lots expire on the shorter refrigerated shelf life.",
    yieldSomeNoNet: "Some packagings have no net weight set — set it in Settings to count them in kg.",
    creating: "Creating...", createBtn: "Create production",
    byproductsOptional: "By-products (optional)", addByproduct: "Add by-product",
    byproductHintSug: "e.g. {sug} recovered from the whey — record its yield.",
    byproductHint: "Record co-products produced from this run and their yield.",
    byproductNamePlaceholder: "By-product name (e.g. Ricotta)",
    subproductAdd: "Add subproduct", subproductPick: "Pick a subproduct",
    subproductNone: "No subproducts defined for this product.",
    subproductManageHint: "Manage subproducts on the Products page.",
    spInputsTitle: "Subproduct inputs (raw material)",
    spInputsHint: "Use available by-products (e.g. Whey, Cream) produced earlier as raw material in this run.",
    spInputsNone: "This product has no input subproducts configured. Set them on the Products page.",
    spInputsNoStock: "No available {names} stock yet. Produce it first in another run.",
    spPickLot: "Add a lot", spAvailable: "available", spFrom: "from",
    spInsufficient: "Using more than available for some lots — recorded as a warning.",
    spRemove: "Remove",
    prodDeleted: "Production deleted · milk restored", failedDelete: "Failed to delete production",
    editQc: "Edit QC", reportFailed: "Report failed", saveQc: "Save QC",
    qcUpdated: "QC file updated",
    deleteProdTitle: "Delete production {code}?",
    deleteProdBody: "This permanently deletes it and restores the milk it consumed back to the source batches. This action is logged and cannot be undone.",
    deleteRestoreMilk: "Delete & restore milk",
    outOfRange: "{n} out-of-range reading(s)",
    manufactureDate: "Manufacture date", shelfNotSet: "Shelf life not set",
    lotNumber: "Lot no.",
    lotNumberHint: "Changing the lot number updates all linked stock lots, delivery notes and traceability records.",
    scTitle: "Specific consumption",
    scHint: "Milk converted to kg (configurable density), minus the deducted subproducts, divided by the batch yield. Tap a subproduct to include/exclude it from the deduction.",
    scMilk: "Milk",
    scNetMilk: "Net milk",
    scDeducted: "Deducted subproducts",
    scNoSubs: "This batch has no recorded subproduct quantities to deduct.",
    scResult: "Specific consumption",
    scUnit: "kg milk / kg product",
    scNoYield: "Add a yield to compute.",
    scNoMilkLeft: "Deducted subproducts exceed the milk quantity.",
    scChipOn: "Deducted from milk — click to exclude",
    scChipOff: "Not deducted — click to deduct from milk",
    scTarget: "Target",
    scOverTarget: "Over target ({target})",
    exportBtn: "Export Excel",
    exportDone: "Export ready",
    exportError: "Export failed",
    notInMaturation: "Not in maturation", maturationNotSet: "Maturation not set",
    vsTarget: "vs {kg} kg target", addYieldCompute: "Add a yield to compute",
    noTarget: "No target set", milkInputs: "Milk inputs",
    variantStockTitle: "Sub-variants",
    variantStockHint: "Carve part of this batch out as a sellable sub-variant (e.g. Fresh Telemea). The kg is deducted from the main product and packed as you choose, with the variant's own shelf life. Removing an allocation returns the kg to the main product.",
    variantStockProduct: "Sub-variant", variantStockPick: "Pick a sub-variant",
    variantStockQty: "Quantity", variantStockUnit: "Unit", variantStockAdd: "Allocate to stock",
    variantStockAdded: "Added {qty} {unit} of {name} to stock (lot {lot})",
    variantStockError: "Could not add variant to stock",
    variantAllocatedKg: "Allocated to sub-variants",
    variantAllocAdded: "Sub-variant {name} carved out and added to stock",
    variantAllocRemoved: "Sub-variant allocation removed — quantity returned to the main product",
    variantAllocRemove: "Remove allocation",
    variantAllocRemoveError: "Could not remove the allocation",
    editMilk: "Edit milk", milkQty: "Quantity (L)", milkRecalcNote: "Editing the quantity recalculates milk stock automatically.",
    milkUpdated: "Milk usage updated", milkUpdateFailed: "Failed to update milk usage",
    qualityControl: "Quality control — {name}", addField: "Add field",
    field: "Field", value: "Value", range: "Range",
    notesPrefix: "Notes:",
    fieldNameRequired: "Field name required", addQcField: "Add QC field",
    fieldName: "Field name", fieldNamePlaceholder: "e.g. Extra check",
    type: "Type", min: "Min", max: "Max", options: "Options (comma separated)",
  },

  planning: {
    intakeNotFound: "Milk reception not found",
    rescheduled: "Rescheduled to {date}", failedReschedule: "Failed to reschedule",
    planRemoved: "Plan removed", receptionRemoved: "Reception removed",
    new: "New", productionPlan: "Production plan", milkReception: "Milk reception",
    tabCalendar: "Calendar", tabRecommendations: "Recommendations",
    legMilkIn: "Milk taken in", legProduced: "Produced", legPlanned: "Planned (to do)",
    tipDrag: "Tip: drag a planned production or milk reception to another day to reschedule it.",
    tipClick: "Tip: click a green Produced event to open its QC file, or a blue Milk in event to see how much milk was taken.",
    dragToReschedule: " (drag to reschedule)", clickOpenQc: " (click to open QC file)", clickSeeMilk: " (click to see how much milk was taken)",
    moreEvents: "+{n} more",
    itemsCount: "{n} items",
    todo: "To do", noPlanned: "No planned productions.",
    volumeTbd: "volume TBD", unassigned: "unassigned", targetFatLine: "target fat {n}%",
    startProduction: "Start production",
    incomingMilk: "Incoming milk", noReceptions: "No scheduled milk receptions.",
    volExpected: "{vol} L expected", fatLine: "fat {n}%", receiveMilk: "Receive milk",
    taken: "Taken", used: "Used", pctUsed: "{n}% used", protein: "Protein",
    productDateRequired: "Product and date are required", planSaved: "Plan saved",
    failedSavePlan: "Failed to save plan", editPlan: "Edit plan", newProductionPlan: "New production plan",
    plannedDate: "Planned date", plannedVolume: "Planned volume (L)", milkBatch: "Milk batch (optional)",
    savePlan: "Save plan",
    supplierDateRequired: "Supplier and expected date are required", receptionSaved: "Milk reception saved",
    failedSaveReception: "Failed to save reception", editReception: "Edit milk reception", scheduleReception: "Schedule milk reception",
    supplier: "Supplier", supplierPlaceholder: "Select the supplier", noSuppliers: "No suppliers — add one in the Suppliers page first.", expectedDate: "Expected date",
    expectedVolume: "Expected volume (L)", expectedFat: "Expected fat (%)", saveReception: "Save reception",
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  },

  planner: {
    title: "Production Planner",
    addToCalendar: "Add to calendar ({n})", planThis: "Add this to the planning calendar", milkShort: "milk",
    addTitle: "Add recommendations to the planning calendar",
    addDesc: "Review and adjust the quantity, milk volume and date for each product, then add them as planned productions. Milk litres are derived from the product yield; you can overwrite any value.",
    addAllDate: "Date for all", addSelectAll: "Select all", addKg: "To produce (kg)", addMilk: "Milk (L)",
    addRecommended: "Recommended: {kg} kg", addYieldTarget: "Target yield {y} kg / 100 L", addYieldActual: "Recent yield {y} kg / 100 L", addNoYield: "No yield set — enter milk litres manually",
    addAlreadyPlanned: "Already planned: {l} L in {n} plan(s)", addEmpty: "Nothing to add — all products are covered.",
    addSummary: "{n} plan(s) · {kg} kg · {l} L milk", addMissingMilk: "{n} without milk volume", addSubmit: "Add {n} to calendar", addNoneSelected: "Select at least one product",
    addDone: "{n} plan(s) added to the calendar", addNote: "From Production Planner: recommended {kg} kg (demand {demand} kg vs usable stock {usable} kg over {horizon} days).",
    subtitle: "What to produce next, based on recent sales vs current stock — with expiry taken into account.",
    refresh: "Refresh",
    horizon: "Cover the next",
    days7: "7 days", days14: "14 days",
    lookback: "Sales history",
    lastNDays: "Last {n} days",
    methodHint: "Demand is estimated from the last {lookback} days of deliveries and projected over the next {horizon} days. Stock that will expire before it can be sold is excluded from usable stock.",
    toProduceCount: "Products to produce",
    totalToProduce: "Total to produce",
    totalAtRisk: "At risk of expiring",
    empty: "No sales or stock data yet — record some deliveries to get recommendations.",
    colProduct: "Product",
    colSold: "Sold ({n}d)", colSoldHint: "Total delivered in the sales-history window (in kg).",
    colVelocity: "Velocity", colVelocityHint: "Average kg sold per day over the sales-history window.",
    colDemand: "Demand ({n}d)", colDemandHint: "Projected demand over the coverage horizon (velocity × days).",
    colInStock: "In stock",
    colUsable: "Usable", colUsableHint: "On-hand stock that can realistically be sold before it expires, minus stock reserved to open orders.",
    colAtRisk: "At risk", colAtRiskHint: "On-hand stock that will expire within the horizon before it can be sold.",
    colCover: "Cover", colCoverHint: "How many days the usable stock will last at the current sales pace.",
    colRecommend: "Produce",
    needsProduction: "Needs production",
    lastSold: "last sold {date}",
    reservedShort: "{kg} kg reserved",
    covered: "Covered",
    generatedAt: "Updated {time}",
  },


  tpl: {
    nameRequired: "Template name is required", saved: "Template saved",
    failedSave: "Failed to save template", deleted: "Template deleted",
    newTemplate: "New template", empty: "No templates yet.",
    copySuffix: "(copy)", starter: "Starter", fieldsCount: "{n} fields",
    duplicate: "Duplicate", selectPrompt: "Select a template to edit, or create a new one.",
    editTemplate: "Edit template", templateName: "Template name", description: "Description",
    fields: "Fields", noFields: "No fields yet. Add your first QC field.",
    unnamed: "(unnamed)", required: "required", rangeLine: "range: {min} to {max}",
    saveTemplate: "Save template",
    fieldSettings: "Field settings", fieldNamePlaceholder: "e.g. Pasteurization Temp",
    minAcceptable: "Min acceptable", maxAcceptable: "Max acceptable",
    requiredField: "Required field", saveField: "Save field",
    optionConformity: "Option conformity",
    optionConformityHint: "Tap an option to mark it as non-conforming — selecting it during production flags the batch as out of spec.",
    optionPass: "Pass",
    optionFail: "Fail",
    shelfLife: "Shelf life", targetYield: "Target yield",
    deliveryShelfLife: "Refrigerated shelf life",
    deliveryShelfLifeHint: "Shorter shelf life used when a product is refrigerated for sale — applied to refrigerated production lots, when taking a lot out of the freezer, or when repackaging into smaller portions.",
    minSaleShelfLife: "Min sale shelf life",
    invalidProductType: "Invalid type: {raw}",
    validPositive: "Enter a valid positive number", savedFor: "{label} saved for {product}",
    notSet: "Not set", productTargets: "Product targets",
    freezerStorage: "Freezer", freezerStorageHint: "Products kept in a freezer. Frozen lots keep full shelf life; refrigerated lots (and lots taken out of the freezer) use the shorter refrigerated shelf life.",
    targetsHint: "Shelf life sets expiration dates automatically. Maturation sets when a batch is ready to move to the cold room. Target yield (kg of product per 100 L of milk) drives the yield-efficiency tracking.",
    targetYieldHint: "Target yield is in kg per 100 L (e.g. Telemea ≈ 13 kg/100L).",
    targetSc: "Target consumption",
    targetScHint: "Target consumption is in kg of (net) milk per kg of product — batches above it are flagged red in the production drawer and Excel export.",
    adminOnly: "Only admins and managers can edit these settings.",
  },

  milk: {
    supplierQtyRequired: "Supplier and quantity are required",
    invalidDateYear: "Please enter a valid 4-digit year (e.g. 2026) for the pickup date.",
    futureDateNotAllowed: "Milk cannot be received on a future date.",
    updated: "Milk reception updated", receivedClosed: "Milk received & reception closed",
    added: "Milk reception added", failedUpdate: "Failed to update batch", failedAdd: "Failed to add batch",
    deleted: "Batch deleted", addMilk: "Add milk",
    editReception: "Edit milk reception", receiveScheduled: "Receive scheduled milk", addReception: "Add milk reception",
    fulfillingHint: "Fulfilling a scheduled reception — fields prefilled. Saving will mark it received.",
    supplierReq: "Supplier *", farmName: "Farm name", quantityReq: "Quantity *",
    receptionDate: "Reception date", temperature: "Temperature", density: "Density",
    pickupDate: "Pickup date", selectSupplier: "Select supplier",
    noSuppliers: "No suppliers — add one in the Suppliers page first.",
    editHint: "Changing quantity adjusts remaining stock by the same amount; it cannot go below milk already used in productions.",
    saveChanges: "Save changes", addBatch: "Add batch",
    title: "Received milk batches", supplier: "Supplier", allSuppliers: "All suppliers",
    empty: "No milk received yet. Add your first reception.",
    noMatch: "No milk batches match the selected filters.",
    intakeL: "Intake (L)", remainingL: "Remaining (L)", fatPct: "Fat %", proteinPct: "Protein %",
    totalIntake: "Total milk received", totalFiltered: "Total milk (filtered)", acrossBatches: "across {n} batches",
    avgFat: "Avg fat", avgProtein: "Avg protein", avgDensity: "Avg density",
    tempC: "Temp. °C", acidity: "Acidity", vehicle: "Vehicle / plate", driver: "Driver",
    tempOverLimit: "Above the accepted limit of {max} °C", tempMaxHint: "Max accepted: {max} °C (Settings)",
    printReport: "Print report", reportPreparing: "Preparing…", printTicket: "Print reception ticket",
    reportReady: "Milk report downloaded", reportFailed: "Could not generate the PDF",
  },

  suppliers: {
    title: "Suppliers", titleMilk: "Milk suppliers", titleOther: "Ingredient suppliers", add: "Add supplier", addSupplier: "Add supplier", editSupplier: "Edit supplier",
    supplierName: "Supplier name", contactPerson: "Contact person", contact: "Contact", taxId: "Tax ID",
    empty: "No suppliers yet. Add your first supplier.", nameRequired: "Supplier name is required",
    saved: "Supplier saved", removed: "Supplier removed",
  },

  rawMaterials: {
    title: "Raw Materials",
    subtitle: "Manage raw materials like rennet, cultures, salt and additives — track their lots and trace them through production for HACCP.",
    tabLots: "Lots", tabTypes: "Material types",
    lotsTitle: "Received lots", typesTitle: "Material types",
    receiveLot: "Receive lot", addType: "Add type", editType: "Edit type", editLot: "Edit lot",
    allTypes: "All types", pickType: "Pick a type",
    noTypesFirst: "First create a raw material type, then you can receive lots.",
    lotsEmpty: "No lots received yet.", typesEmpty: "No raw material types yet.",
    hideEmpty: "Hide empty lots",
    allEmptyHidden: "All lots are empty — turn off the filter to see them.",
    lotCode: "Lot code", type: "Type", supplier: "Supplier", supplierLot: "Supplier lot no.",
    remaining: "Remaining", received: "Received", expiry: "Expiry", quantity: "Quantity", unit: "Unit",
    name: "Name", category: "Category", defaultUnit: "Default unit", tracksExpiry: "Tracks expiry",
    tracksExpiryHint: "Require an expiry date when receiving lots of this material.",
    customFields: "Custom fields", addField: "Add field",
    customFieldsHint: "Add extra fields to capture per lot (e.g. Concentration, Strain).",
    fieldLabel: "Field label", fieldUnit: "Unit",
    notes: "Notes", inactive: "Inactive", activeLabel: "Active",
    expired: "Expired",
    namePlaceholder: "e.g. Rennet, Thermophilic culture",
    supplierPlaceholder: "Supplier name",
    nameRequired: "Name is required", typeRequired: "Select a type", qtyRequired: "Quantity must be greater than 0",
    typeCreated: "Type created", typeUpdated: "Type updated", typeDeleted: "Type deleted",
    lotCreated: "Lot received", lotUpdated: "Lot updated", lotDeleted: "Lot deleted",
    deleteTypeTitle: "Delete raw material type?",
    deleteTypeDesc: "Delete \"{name}\"? Types with received lots must be deactivated instead.",
    deleteLotTitle: "Delete lot?",
    deleteLotDesc: "Delete lot {code}? This cannot be undone.",
    cat: { rennet: "Rennet", culture: "Culture", salt: "Salt", additive: "Additive", other: "Other" },
    ftype: { text: "Text", number: "Number", date: "Date", select: "Select" },
    // Categories & units manager
    manageConfig: "Categories & units",
    cfgSubtitle: "Add, rename or remove the categories and measurement units used for raw materials.",
    cfgAdd: "Add",
    cfgAddCategoryPh: "New category name",
    cfgAddUnitPh: "New unit (e.g. kg, l, pcs)",
    cfgEmpty: "Nothing here yet — add your first entry above.",
    cfgAdded: "Added", cfgRenamed: "Renamed", cfgDeleted: "Deleted",
    cfgDeleteTitle: "Delete this entry?",
    cfgDeleteDesc: "Delete \"{name}\"? Entries in use by existing materials cannot be deleted.",
    // Production + traceability
    prodSectionTitle: "Raw materials",
    prodSectionHint: "Add rennet, cultures or other raw materials consumed in this batch.",
    addLine: "Add raw material", pickLot: "Pick a lot", qtyUsed: "Qty used", noneUsed: "No raw materials used.",
    traceTitle: "Raw materials used", traceLotTitle: "Raw material lot",
    consumedIn: "Consumed in productions", noConsumers: "Not used in any production yet.",
  },

  fat: {
    title: "Fat Standardization — Pearson Square",
    subtitle: "Calculate how much skim or cream to blend — or how much milk to degrease — to reach a target fat %.",
    enterValuesDiagram: "Enter values to see the Pearson square",
    whole: "Whole", cream: "Cream", skim: "Skim", target: "Target",
    partsWhole: "parts whole", parts: "parts",
    giveName: "Give the plan a name", planSaved: "Standardization plan saved", failedSavePlan: "Failed to save plan",
    inputs: "Inputs", milkBatch: "Milk batch", selectBatch: "Select milk batch",
    mode: "Mode", wholeSkim: "Whole + Skim", wholeCream: "Whole + Cream",
    milkFat: "Milk fat", targetFat: "Target fat", milkVolume: "Milk volume",
    skimFat: "Skim fat", creamFat: "Cream fat", separatorCapacity: "Separator capacity",
    separatorHint: "Used to estimate how long to run the separator when degreasing milk.",
    pearsonSquare: "Pearson Square", blendRatio: "Blend ratio", results: "Results",
    enterValid: "Enter valid values to calculate.", cannotStandardize: "Cannot standardize",
    blendPlan: "Blend plan", wholeMilk: "Whole milk", addType: "Add {type}",
    totalVolume: "Total volume", resultingFat: "Resulting fat",
    degreasePlan: "Degrease (separation) plan", separateCream: "Separate cream",
    remainingAtTarget: "Remaining at target", liveRunTime: "Live separator run time",
    milkToRun: "Milk to run through separator", runTime: "Run time", creamRemoved: "Cream removed",
    skimProduced: "Skim produced", bypass: "Bypass (don't separate)",
    separatorSummary: "Run the {rate} L/h separator for {time} to skim only {sep} L of the {total} L batch and pull out {cream} L of cream. Re-blend the skim with the bypassed {bypass} L to bring all {remaining} L to {target}% fat.",
    savePlanBtn: "Save standardization plan", savedPlans: "Saved standardization plans",
    creamCalcTitle: "Cream fat calculator",
    creamCalcSubtitle: "Find out the fat content of the cream you separated from a batch of milk.",
    cfMilkFat: "Milk fat", cfSkimFat: "Skim milk fat", cfMilkVol: "Milk degreased", cfCreamVol: "Cream obtained",
    cfSkimVol: "Skim milk (derived)", cfResult: "Cream fat content",
    cfEnter: "Enter the values on the left to compute the cream fat.",
    cfInvalidTitle: "Check the volumes",
    cfInvalidVol: "Cream volume must be greater than 0 and smaller than the milk volume.",
    cfImplausible: "The result looks unusual — double-check the fat values and volumes.",
    cfHint: "Based on a fat mass balance: (milk volume × milk fat − skim volume × skim fat) ÷ cream volume. Skim volume = milk − cream.",
    savedPlansHint: "Saved plans can be applied inside the Production wizard (Milk step) to pre-fill target fat and milk batch.",
    planName: "Plan name", planNamePlaceholder: "e.g. Yogurt 3.5% line",
    previewLine: "{from}% → {to}% · add {vol} L {type}",
  },

  trace: {
    title: "Traceability",
    subtitle: "Search a product, batch, lot, order or delivery note to see the full production file.",
    searchPlaceholder: "Enter code or subproduct name (e.g. PRD-…, LOT-…, SUB-…, DN-…, Whey)",
    searchBtn: "Search", searching: "Searching…", print: "Print / Export PDF",
    matchedBy: "Matched by",
    matchProduction: "Production code", matchLot: "Stock lot", matchOrder: "Order", matchDelivery: "Delivery note",
    emptyTitle: "Search the production file", emptyHint: "Type a code above and press Search.",
    notFound: "No record found for this code.", resultsFor: "Results for", filesFound: "production file(s)",
    prodSummary: "Production summary", code: "Code", product: "Product", date: "Date",
    operator: "Operator", status: "Status", totalMilk: "Milk used", yield: "Yield",
    sold: "Sold", remaining: "Remaining", expiration: "Expiration", maturationUntil: "Maturation until",
    notes: "Notes", qcWarnings: "Quality warnings", recipe: "Recipe", none: "None",
    milkUsed: "Milk used (origin)", batch: "Batch", supplier: "Supplier", litres: "Litres",
    reception: "Received", analysis: "Analysis", fat: "Fat", protein: "Protein", ph: "pH",
    temperature: "Temp.", density: "Density", noMilk: "No milk inputs recorded.",
    stockLots: "Stock lots", lot: "Lot", qty: "Qty",
    sales: "Sales & distribution", deliveryNote: "Delivery note", client: "Client",
    quantity: "Quantity", unitPrice: "Unit price", value: "Value", order: "Order",
    noSales: "Not sold yet — no deliveries recorded.", totalSold: "Total sold", totalValue: "Total value",
    reportTitle: "Traceability report", generatedAt: "Generated", auditNote: "HACCP traceability — for audit purposes",
    matchSubproduct: "Subproduct lot",
    subproductSection: "Subproduct (by-product)", subLotCode: "Lot code", byproduct: "By-product",
    producedQty: "Produced", remainingQty: "Remaining", origin: "Origin (source production)",
    consumedIn: "Consumed in productions", noConsumers: "Not consumed yet — still in stock.", qtyUsed: "Qty used",
    stockLotSection: "Stock lot", lotCodeLabel: "Lot code", madeOn: "Made on", packagedOn: "Packaged on",
    madeFrom: "Made from (rebatched from)", noProductionFile: "No production file is linked to this lot.",
    hasProductionFile: "Full production file below.",
    rebatchedInto: "Rebatched into (where it went)", wentTo: "Went to", stillInStock: "Still in stock",
    thisLotWentTo: "This lot was delivered to",
    subUsedSection: "Subproducts used (inputs)",
    sourceProduction: "Source production",
    corrective: "Corrective action",
    subWarnings: "Subproduct stock warnings",
    traceThis: "Trace this code",
  },

  haccp: {
    ccpBreach: "CCP breach", warning: "Warning", inRange: "In range", outOfLimit: "Out of limit",
    logDeleted: "Monitoring log deleted", failedDeleteLog: "Failed to delete log", exportFailed: "Export failed",
    unresolvedBreaches: "{n} unresolved CCP breach(es)",
    addCorrectiveHint: ". Add corrective actions in Monitoring Logs.",
    tabLogs: "Monitoring Logs", tabCcps: "CCP Definitions", tabTrends: "Trends",
    logsTitle: "Monitoring logs", logReading: "Log reading",
    controlPoint: "Control point", allCcps: "All CCPs",
    noLogs: "No monitoring logs yet.", noMatch: "No readings match the selected filters.",
    time: "Time", ccp: "CCP", measurements: "Measurements", action: "Action",
    correctiveAction: "Corrective action", resolved: "Resolved", deleteLogTitleAttr: "Delete log",
    ccpsTitle: "Critical Control Points", newCcp: "New CCP", noCcps: "No CCPs defined yet.",
    processStep: "Process step", criticalLimits: "Critical limits", frequency: "Frequency", schedule: "Schedule",
    customFields: "Custom · {n} fields", perField: "Per field", due: "Due {date}",
    toComplete: "To complete", overdue: "Overdue", scheduled: "Scheduled", onTrack: "On track", viewOnly: "View only",
    ccpDeleted: "CCP deleted",
    deleteLogTitle: "Delete monitoring log?",
    deleteLogDesc: "This permanently removes the {ccp} log from {time}. This cannot be undone.",
    deleteLogBtn: "Delete log",
    nameStepRequired: "Name and process step are required",
    addOneField: "Add at least one custom field, or switch to single measurement",
    ccpSaved: "CCP saved", failedSaveCcp: "Failed to save CCP",
    editCcp: "Edit control point", newControlPoint: "New control point",
    ccpName: "CCP name", ccpNamePlaceholder: "Pasteurization Temp", stepPlaceholder: "Pasteurization",
    freqPlaceholder: "Per batch / Every 4 hours",
    reminderTitle: "Reminder to complete", reminderHint: "Show this control point in the notification bell until it's logged.",
    recurring: "Recurring (every N hours)", oneTime: "One-time (due date/time)",
    reminderWorkingDays: "Daily on working days",
    reminderWeekly: "Weekly (every N weeks)", everyWeeks: "Repeat every", weeksOption: "{n} week(s)",
    editLog: "Edit log", editLogTitle: "Edit HACCP log", saveChanges: "Save changes", logUpdated: "Log updated",
    failedUpdateLog: "Failed to update log", resolveLog: "Resolve", logResolved: "Exception resolved", failedResolve: "Failed to resolve",
    everyHours: "Every (hours)", dueDateTime: "Due date & time",
    customTitle: "Custom multi-field control point",
    customHint: "Record several typed parameters (temp, checks, photos…) at one checkpoint.",
    measurementType: "Measurement type", critMin: "Critical min", critMax: "Critical max",
    warnMinOpt: "Warning min (optional)", warnMaxOpt: "Warning max (optional)",
    monitoringFields: "Monitoring fields",
    noFieldsHint: "No fields yet. Add temperature, pH, a pass/fail check, a dropdown, a note, or a photo.",
    saveCcp: "Save CCP",
    fieldNamePlaceholder: "e.g. Temperature",
    critMinShort: "Crit min", critMaxShort: "Crit max", warnMinShort: "Warn min", warnMaxShort: "Warn max",
    acceptableAnswer: "Acceptable answer is {ans} (otherwise flagged critical)",
    acceptableOptions: "Acceptable options (comma separated)",
    checklistItems: "Checklist items (comma separated)", checklistHint: "Each item shows as a tickable checkbox when logging.",
    freeTextHint: "Free-text note (informational).", photoHint: "Photo evidence upload (informational).",
    fieldRequired: "{field} is required", selectControlPoint: "Select a control point", enterReading: "Enter a reading value",
    breachRecorded: "CCP breach recorded — add a corrective action", readingLogged: "Reading logged", failedLog: "Failed to log reading",
    logCcpReading: "Log CCP reading", selectCcp: "Select CCP", readingValue: "Reading value",
    criticalRange: "Critical: {min} – {max} {unit}", productionOpt: "Production (opt.)", saveReading: "Save reading",
    readingDateTime: "Reading date & time", readingDateHint: "Defaults to now — change it to log a check from another day.",
    invalidDate: "Please enter a valid date and time.",
    allOperators: "All operators", freqAll: "All frequencies", freqOff: "No reminder", filtered: "filtered",
    pdfFiltered: "Audit pack PDF for the current filters", pdfAll: "Audit pack PDF (all records)",
    escalation: "Missed check escalation", escalationOff: "Off — no escalation",
    escalationAfterMin: "Alert admin after {n} min", escalationAfterHours: "Alert admin after {n} h",
    escalationHint: "If the check is still not logged this long after it became due, admins get a critical bell alert and an email (once per occurrence).",
    printRecord: "Print record sheet", reminderTime: "Reminder time", reminderTimeHint: "Becomes due at this hour (leave empty = from midnight).",
    autoTaskOperator: "Assign as task to operator", autoTaskNone: "No task — bell reminder only", autoTaskShort: "Task",
    autoTaskHint: "When the check becomes due, an open task is created for this operator and closed automatically once a reading is logged.",
    unitHoursPlain: "hours", firstDueOpt: "Start / first due (optional)", dueRequired: "Please set the due date & time.",
    reminderOnceHint: "Shows in Reminders from the due date until a reading is logged.",
    reminderRepeatHint: "Shows in Reminders whenever a check is due and no reading has been logged for that period.",
    dateFieldHint: "Date picker (informational). Defaults to today when logging.",
    datetimeFieldHint: "Date & time picker (informational). Defaults to now when logging.",
    ccpReport: "CCP report", ccpReportDesc: "Printable monitoring report for one control point or all of them, for a calendar month.",
    reportMonth: "Month", reportGenerate: "Generate PDF", reportGenerating: "Generating…",
    reportReady: "Report downloaded", reportInvalidMonth: "Please pick a valid month.",
    reportIncl1: "CCP definition: process step, limits, monitoring frequency",
    reportIncl2: "Readings: date/time, values, status, operator, production, notes",
    reportIncl3: "Corrective actions for breaches + summary (checks, warnings, breaches, missed)",
    reportIncl4: "Signature lines: prepared by / verified by / date",
    selectEllipsis: "Select…", noChecklistItems: "No checklist items configured.",
    photoUploaded: "Photo uploaded", uploadFailed: "Upload failed",
    uploading: "Uploading…", replace: "Replace", uploadPhoto: "Upload photo", remove: "Remove",
    describeAction: "Describe the corrective action", actionRecorded: "Corrective action recorded", breachWord: "breach",
    actionsTaken: "Actions taken", actionsPlaceholder: "Re-pasteurized batch, adjusted thermostat...",
    disposition: "Product disposition", dispositionPlaceholder: "Released / Held / Discarded",
    verifiedBy: "Verified by", closeIncident: "Close incident",
    trendTitle: "CCP reading trend", selectACcp: "Select a CCP", selectToView: "Select a CCP to view its trend.",
    noNumeric: "This control point has no numeric fields to chart.", noReadingsFor: "No readings logged for {ccp} yet.",
    warningRange: "Warning: {min} – {max} {unit}", readingsCount: "{n} readings",
  },

  activity: {
    title: "Activity log", all: "All", productions: "Productions",
    exportXlsx: "Export Excel", exporting: "Exporting…", exported: "Activity log downloaded", exportFailed: "Could not export the activity log",
    empty: "No activity recorded yet.",
    when: "When", user: "User", action: "Action", item: "Item", details: "Details",
    detailsVal: "(details)", production: "Production",
    stock: "Stock", stockLot: "Stock lot",
    created: "created", updated: "updated", deleted: "deleted",
    repacked: "repacked", adjusted: "adjusted", out_of_freezer: "taken out of freezer", dispatched: "dispatched", renamed: "lot renamed", writeoff_reverted: "write-off reverted",
    searchPlaceholder: "Search lot no., user or details…",
    noSearchResults: "No entries match \"{q}\".",
    noRangeResults: "No entries in the selected period.",
    fromDate: "From date",
    toDate: "To date",
  },

  yieldSim: {
    title: "Yield Simulator",
    subtitle: "Estimate the milk needed to make a target amount of product — or the product you can make from a given amount of milk.",
    modeProductToMilk: "Product → Milk",
    modeMilkToProduct: "Milk → Product",
    products: "Products",
    addProduct: "Add product",
    product: "Product",
    selectProduct: "Select product",
    yieldBasis: "Yield basis",
    yieldPer100: "Yield / 100 L",
    desiredQty: "Desired qty",
    milkNeeded: "Milk needed",
    productOutput: "Product output",
    basisTarget: "Target",
    basisAverage: "Real average",
    basisCustom: "Custom",
    notSet: "not set",
    noHistory: "no history",
    availableMilk: "Available milk",
    milkModeHint: "Each row shows what you'd get if all this milk went to that product.",
    totalMilkNeeded: "Total milk needed",
    footnote: "Target yield comes from Product settings; the real average is computed from your past productions (yield ÷ milk × 100).",
    loadFailed: "Failed to load product yields",
    noDataTitle: "No yield data yet",
    noDataHint: "Set a target yield per 100 L in Product settings, or record productions to build a real average. You can still use a custom yield per product.",
  },


  users: {
    emailNamePassRequired: "Email, name and password are required",
    emailNameRequired: "Email and name are required",
    passwordRequired: "Set a password or enable the activation email",
    saved: "User saved", failedSave: "Failed to save user",
    removed: "User removed", failedDelete: "Failed to delete",
    addUser: "Add user", title: "Users & roles", empty: "No users yet.",
    name: "Name", email: "Email", role: "Role", you: "(you)",
    editUser: "Edit user", newPassword: "New password", password: "Password",
    leaveBlankKeep: "Leave blank to keep the current password.",
    sendActivation: "Send activation email",
    sendActivationHint: "The user sets their own password via an emailed link.",
    inviteSent: "Activation email sent", inviteResent: "Activation email re-sent",
    resetSent: "Password reset email sent", pending: "Pending",
    resendInvite: "Resend activation email", sendReset: "Send password reset email",
  },
  notif: {
    reminders: "Reminders", active: "{n} active", allCaughtUp: "All caught up — nothing overdue.",
    digest: "Daily digest of overdue CCP checks, plans & unresolved breaches",
    dismiss: "Dismiss", dismissAll: "Dismiss all", dismissed: "Reminder dismissed",
    allDismissed: "{n} reminders dismissed", undo: "Undo", dismissFailed: "Could not dismiss the reminder",
    undoFailed: "Could not restore the reminder", restoreAll: "Show {n} dismissed",
    dismissGroup: "Dismiss all in this group", collapse: "Click to collapse",
    group: {
      ccp_overdue: "{n} CCP checks due", plan_overdue: "{n} planned productions overdue",
      reception_overdue: "{n} milk receptions overdue", haccp_breach: "{n} unresolved CCP breaches",
      maturation_ready: "{n} batches ready for cold room", task: "{n} tasks due",
      lot_expiring: "{n} lots expiring soon", audit_change: "{n} stock / yield changes",
      ccp_escalation: "{n} missed CCP checks escalated",
    },
  },

  pager: {
    showing: "Showing {from}–{to} of {total}", perPage: "Per page",
    prev: "Previous", next: "Next",
  },

  dashboard: {
    openOrders: "Open orders · awaiting delivery",
    completedOrders: "Completed orders · delivered",
    planningTitle: "Upcoming planning",
    noPlanning: "No upcoming production plans or milk receptions.",
    planColDate: "Date", planColType: "Type", planColDetail: "Detail", planColVolume: "Volume", planColInfo: "Assigned / Fat",
    milkInventory: "Milk inventory",
    currentMonth: "Current month", lastMonth: "Last month",
    trends: "Trends", recentProductions: "Recent productions",
    overviewTitle: "Production Overview",
    overviewSubtitle: "Live snapshot of milk inventory, active productions and quality exceptions.",
    chartsPeriod: "Charts period",
    milkBtn: "Milk", productionBtn: "Production",
    milkAvailable: "Milk available", activeProductions: "Active productions",
    milkReceived: "Total milk received",
    periodThisMonth: "This month", periodLastMonth: "Last month", periodThisYear: "This year",
    periodAllTime: "All time", periodCustomMonth: "Specific month",
    planned: "Planned", qcExceptions: "QC exceptions", haccpAlerts: "HACCP alerts",
    milkInVsProduced: "Milk in vs produced", thisMonth: "this month",
    milkInL: "Milk in (L)", producedL: "Produced (L)",
    prodVolumeByProduct: "Production volume by product", milkUsedL: "Milk used (L)",
    noProdData: "No production data yet.",
    scTrend: "Avg. specific consumption per month",
    scTrendHint: "kg of (net) milk needed per kg of product — lower is better. Last 6 months, per product. Dashed line = product target.",
    scUnitShort: "kg/kg",
    scTargetLabel: "target",
    noScData: "No consumption data yet. Batches need milk + yield recorded.",
    lossTrend: "Monthly losses by reason",
    lossTrendHint: "kg written off each month, stacked by reason — waste patterns jump out over time. Last 6 months.",
    noLossData: "No losses recorded in the last 6 months.",
    lossValueLabel: "est. value of losses (6 months)",
    byproducts: "By-products produced (per month)",
    viewAll: "View all", manage: "Manage",
    noProductions: "No productions yet.", flag: "flag",
    qcExceptionsTitle: "QC exceptions", noOutOfRange: "No out-of-range readings.",
    expiringSoon: "Expiring soon", noExpiring: "No products expiring within {days} days.",
    expiredAgo: "Expired {n}d ago", expiresToday: "Expires today", daysLeft: "{n}d left",
    exp: "Exp",
    readyForColdRoom: "Ready for cold room",
    noColdRoom: "No batches in maturation ready within {days} days.",
    salesTrend: "Sales trend", salesTrendSub: "Delivered quantity vs the previous period",
    salesTrendValueSub: "Total value sold vs the previous period",
    thisPeriod: "This period", lastPeriod: "Last period", revenueLabel: "Revenue",
    period7d: "Last 7 days", period30d: "Last 30 days", period90d: "Last 90 days",
    periodMonth: "This month", periodYear: "This year",
    byProduct: "By product", vsPrev: "vs prev", newSales: "New",
    noSales: "No deliveries in this period.",
    current: "Current", previous: "Previous", change: "Change",
    allProducts: "All products", viewDetailedReport: "View detailed report",
    ready: "Ready", readyAgo: "Ready {n}d ago", readyToday: "Ready today",
    yieldEfficiency: "Yield efficiency by product",
    yieldSubtitle: "Average actual yield vs target (kg per 100 L of milk). Set targets in QC Templates.",
    noYieldData: "No yield data yet. Set a target yield per product in QC Templates and record production yields.",
    runs: "runs", run: "run",
    noMilk: "No milk batches yet.", fat: "Fat", ph: "pH",
  },

  // Built-in data values (product types, default statuses). Custom user values pass through.
  data: {
    // product types
    "Telemea": "Telemea",
    "Mozzarella": "Mozzarella",
    "Cas": "Caș",
    "Urda": "Urdă",
    "Cascaval": "Cașcaval",
    "Branza": "Brânză",
    "Cream Cheese": "Cremă de brânză",
    "Cottage Cheese": "Brânză de vaci",
    "Fresh Cottage Cheese": "Brânză de vaci proaspătă",
    "Diet Fresh Cottage Cheese": "Brânză de vaci proaspătă dietetică",
    "Butter": "Unt",
    "Yogurt": "Iaurt",
    "Smantana": "Smântână",
    "Sour Cream": "Smântână",
    "Milk": "Lapte",
    "Whey": "Zer",
    "Kashkaval": "Cașcaval",
    "Ricotta": "Ricotta",
    // default statuses
    "In progress": "În curs",
    "Maturation": "Maturare",
    "Maturing": "La maturare",
    "Done": "Finalizat",
    "Completed": "Finalizat",
    "Ready": "Gata",
    "Planned": "Planificat",
    "Packaged": "Ambalat",
  },
};

export const ro = {
  lang: { english: "English", romanian: "Română", label: "Limbă" },

  pwa: {
    title: "Adaugă LactoPilot pe ecranul principal",
    androidHint: "Se deschide pe tot ecranul ca o aplicație, cu scurtături rapide pentru citiri PCC și recepții de lapte.",
    iosStep1: "În Safari apasă Partajează", iosStep2: "apoi „Adaugă pe ecranul principal”.",
    genericHint: "Din meniul browserului alege „Instalează aplicația” sau „Adaugă pe ecranul principal” pentru a deschide LactoPilot pe tot ecranul.",
    install: "Instalează",
  },
  quick: { title: "Acțiuni rapide", logCcp: "Înregistrează citire PCC", addMilk: "Adaugă recepție lapte" },
  supplierTrend: {
    title: "Tendința calității pe furnizor", subtitle: "Medii lunare ponderate cu volumul, ultimele {n} luni", flagged: "{n} furnizor(i) în scădere",
    months: "{n} luni", colSupplier: "Furnizor", colVolume: "Volum", colSignals: "Semnale", receptions: "{n} recepții", overTemp: "{n} peste limita de temp.",
    stable: "Stabil", viewAll: "Toți furnizorii", empty: "Nicio recepție de lapte în această perioadă.", loadError: "Tendința furnizorilor nu a putut fi încărcată",
    flag_fat_down: "Grăsime în scădere", flag_protein_down: "Proteină în scădere", flag_temp_up: "Livrări mai calde", flag_temp_over: "Peste limita de temp.",
    footnote: "Ultima lună comparată cu media ponderată a lunilor anterioare din perioadă. Semnale: grăsime −0,2 pp sau mai mult, proteină −0,15 pp sau mai mult, temperatură +1 °C sau mai mult, ori peste limita companiei.",
    tabReceptions: "Recepții", tabSuppliers: "Furnizori",
  },
  usage: {
    tab: "Utilizare", title: "Activitatea tenanților", lastDays: "Ultimele {n} zile", loadError: "Datele de utilizare nu au putut fi încărcate", empty: "Nicio organizație încă.", refresh: "Reîmprospătează",
    status_active: "Activ", status_low: "Activitate redusă", status_dormant: "Inactiv", status_never: "Neutilizat",
    hint_active: "Activitate în ultimele 7 zile", hint_low: "Activitate în ultimele {days} zile, nu săptămâna aceasta", hint_dormant: "Nimic în ultimele {days} zile", hint_never: "Nicio autentificare sau dată, vreodată",
    colStatus: "Stare", colOrg: "Organizație", colUsers: "Activi / utilizatori", colLastLogin: "Ultima autentificare", colLastActivity: "Ultima activitate",
    count_activity: "Acțiuni înregistrate", count_productions: "Producții", count_haccp_logs: "Verificări PCC", count_delivery_notes: "Livrări", count_orders: "Comenzi", count_stock_lots: "Loturi stoc", count_milk_batches: "Recepții lapte",
    since: "din {date}", logins: "{n} autentificări în total",
    footnote: "Numerele acoperă perioada selectată. „Utilizatori activi” = cei autentificați în perioadă (urmărirea autentificărilor începe cu această versiune). „Ultima activitate” = cea mai nouă înregistrare creată în tenant.",
    colTrend: "Tendință ({n} săpt.)", trendTooltipTitle: "Acțiuni pe săptămână (de la cea mai veche la cea mai nouă)", weekAgo: "acum {n} săpt.", thisWeek: "Săptămâna aceasta", trendTotal: "{n} acțiuni în {weeks} săptămâni", trendEmpty: "Nicio activitate în ultimele {weeks} săptămâni",
    dormantTitle: "Alertă tenant inactiv", dormantHint: "Zilnic la ora {hour}:00 (București), tenanții plătitori fără autentificare sau activitate de {days} zile sunt raportați prin email superadminului. Fiecare tenant este alertat o singură dată per perioadă de inactivitate; alerta se rearmează când redevine activ.",
    dormantRun: "Verifică acum", dormantRunning: "Se verifică…", dormantAlerted: "Alertă trimisă", dormantNoneToast: "Verificare finalizată — niciun tenant plătitor inactiv nou", dormantAlertedToast: "Alertă trimisă pentru {n} tenant(ți)", dormantResolvedToast: "{n} tenant(ți) din nou activi — alerta a fost rearmată",
    dormantRunError: "Verificarea a eșuat", dormantRecipients: "Destinatari: {emails}", dormantLastCheck: "Ultima verificare: {when}", dormantNever: "încă nu", paidPlan: "Plătitor", freePlan: "Gratuit / trial",
    colLogins: "Autentificări ({n} săpt.)", loginsTooltipTitle: "Autentificări pe săptămână (de la cea mai veche la cea mai nouă)", loginsTotal: "{n} autentificări în {weeks} săptămâni", loginsEmpty: "Nicio autentificare înregistrată în ultimele {weeks} săptămâni", loginsNote: "Urmărirea săptămânală a autentificărilor începe cu această versiune.",
    reengage: "Trimite email", reengageTitle: "Email de reactivare", reengageDesc: "Un mesaj prietenos către administratorul {name}. Editează textul înainte de trimitere — răspunsurile ajung la {replyTo}.",
    reengageTo: "Către", reengageSubject: "Subiect", reengageBody: "Mesaj", reengageSend: "Trimite emailul", reengageSending: "Se trimite…", reengageQuiet: "Inactiv de {n} zile",
    reengageSent: "Email trimis către {emails}", reengageFailed: "Emailul nu a putut fi trimis", reengageNoRecipient: "Acest tenant nu are un email de administrator", reengageLast: "Ultimul trimis {when}", reengageHistory: "Trimise anterior", reengageDraftError: "Emailul nu a putut fi pregătit",
  },
  common: {
    save: "Salvează", cancel: "Anulează", delete: "Șterge", edit: "Editează", add: "Adaugă",
    create: "Creează", close: "Închide", confirm: "Confirmă", done: "Gata", search: "Caută",
    loading: "Se încarcă...", actions: "Acțiuni", yes: "Da", no: "Nu", back: "Înapoi",
    download: "Descarcă", view: "Vezi", total: "Total", date: "Dată", notes: "Note",
    status: "Stare", all: "Toate", none: "Niciunul", qty: "Cant.", quantity: "Cantitate",
    unit: "Unitate", name: "Nume", email: "Email", phone: "Telefon", address: "Adresă",
    price: "Preț", product: "Produs", products: "Produse", packaging: "Ambalaj",
    client: "Client", clients: "Clienți", expiry: "Expirare", remaining: "Rămas",
    saving: "Se salvează...", deleting: "Se șterge...", generating: "Se generează...",
    optional: "opțional", required: "obligatoriu", today: "Astăzi", noData: "Nu există date",
    retry: "Reîncearcă", apply: "Aplică", filter: "Filtru", clear: "Golește", select: "Selectează",
    month: "Lună",
    allClients: "Toți clienții", noMatchClient: "Niciun rezultat pentru clientul selectat.",
    of: "din", items: "articole", item: "articol", currency: "Monedă", code: "Cod",
    failed: "A apărut o eroare", success: "Succes", lot: "Lot", batch: "Șarjă",
  },

  signup: {
    title: "Creează-ți spațiul de lucru", subtitle: "Începe să-ți gestionezi fabrica în câteva minute",
    company: "Numele companiei", adminName: "Numele tău", email: "Email de serviciu", password: "Parolă",
    submit: "Creează spațiul de lucru", haveAccount: "Ai deja un cont?", signInCta: "Autentifică-te",
    error: "Nu am putut crea spațiul de lucru. Încearcă din nou.",
    captchaRequired: "Confirmă că nu ești robot.",
    verifyTitle: "Confirmă-ți adresa de email",
    verifySent: "Am trimis un link de activare la",
    verifyHint: "Deschide linkul din email pentru a-ți activa contul, apoi autentifică-te. Linkul este valabil 48 de ore.",
    resend: "Retrimite emailul",
    resent: "Emailul de confirmare a fost trimis.",
    resendFailed: "Nu am putut retrimite emailul. Încearcă din nou.",
    backToLogin: "Înapoi la autentificare",
  },

  verify: {
    loading: "Confirmăm adresa ta de email…",
    missing: "Link invalid — nu am găsit tokenul de verificare.",
    failed: "Acest link de verificare este invalid sau a expirat.",
    successTitle: "Email confirmat",
    successBody: "Contul tău este acum activ. Te poți autentifica.",
    errorTitle: "Verificarea a eșuat",
    goToLogin: "Mergi la autentificare",
  },


  nav: {
    dashboard: "Tablou de bord", milk: "Recepție lapte", calculator: "Calculator grăsime", yieldSimulator: "Simulator randament",
    productions: "Producții", planning: "Planificare", productionPlanner: "Planificator producție", templates: "Șabloane producție",
    haccp: "HACCP", stock: "Stoc", orders: "Comenzi", deliveryNotes: "Avize de livrare",
    subproducts: "Subproduse",
    traceability: "Trasabilitate",
    guide: "Ghid",
    clients: "Clienți", priceLists: "Liste de prețuri", settings: "Setări companie",
    activity: "Jurnal activitate", users: "Utilizatori", suppliers: "Furnizori", milkSuppliers: "Furnizori lapte", materialSuppliers: "Furnizori materii prime", rawMaterials: "Materii prime",
    permissions: "Permisiuni",
    billing: "Facturare",
    salesInventory: "Vânzări & Stoc", administration: "Administrare",
    productPackaging: "Ambalaje produse",
    products: "Produse",
    staff: "Personal", operators: "Operatori", timeClock: "Pontaj",
    tasks: "Sarcini", complaints: "Sesizări",
    modMilkReception: "Recepție materii prime", modProduction: "Producție",
    modStockOrders: "Stoc & Comenzi", modWorkforce: "Personal", modSettingsAdmin: "Setări & Administrare",
    modFinance: "Financiar", expenses: "Cheltuieli", reports: "Rapoarte",
    stockMovements: "Mișcări de stoc",
    losses: "Pierderi & Casări",
    nonconformities: "Registru neconformități",
    modCompliance: "HACCP & Trasabilitate",
  },

  footer: { units: "Unități metrice · °C · Litri", brandSub: "Producție & HACCP" },

  expenses: {
    title: "Cheltuieli",
    subtitle: "Urmărește costurile de funcționare — chirie, utilități, curățenie și altele — și vezi cum evoluează.",
    addExpense: "Adaugă cheltuială", editExpense: "Editează cheltuiala",
    manageCategories: "Categorii",
    amount: "Sumă", currency: "Monedă", category: "Categorie", date: "Data",
    vendor: "Furnizor", vendorPlaceholder: "ex. Electrica, proprietar…",
    pickCategory: "Alege o categorie",
    allExpenses: "Toate cheltuielile", empty: "Nicio cheltuială încă. Adaugă prima pentru a începe urmărirea.",
    allCategories: "Toate categoriile",
    periodAll: "Tot timpul", periodThisWeek: "Săptămâna aceasta", periodThisMonth: "Luna aceasta",
    periodLastMonth: "Luna trecută", periodThisYear: "Anul acesta", periodCustom: "Interval personalizat",
    from: "De la", to: "Până la", clearFilters: "Resetează", filteredTotal: "Total filtrat",
    noResults: "Nicio cheltuială nu corespunde acestor filtre.",
    weekly: "Săptămânal", monthly: "Lunar",
    thisWeek: "Săptămâna aceasta", thisMonth: "Luna aceasta",
    vsPrevious: "față de perioada anterioară", allTime: "Total general", noChange: "Fără date",
    trendTitle: "Evoluția cheltuielilor", byCategory: "Pe categorii", spent: "Cheltuit", noData: "Nicio cheltuială în această perioadă.",
    created: "Cheltuială adăugată", updated: "Cheltuială actualizată", deleted: "Cheltuială ștearsă",
    amountRequired: "Introdu o sumă mai mare ca 0", categoryRequired: "Te rog alege o categorie",
    deleteTitle: "Ștergi această cheltuială?", deleteDesc: "Această acțiune va elimina definitiv înregistrarea.",
    catSubtitle: "Adaugă, redenumește sau șterge categoriile de cheltuieli.",
    catAdd: "Adaugă", catPlaceholder: "Categorie nouă (ex. Chirie, Gaz)…", catEmpty: "Nicio categorie încă.",
    catAdded: "Categorie adăugată", catRenamed: "Categorie redenumită", catDeleted: "Categorie ștearsă",
    catDeleteTitle: "Ștergi această categorie?",
    catDeleteDesc: "Ștergi „{name}”? Categoriile folosite de cheltuieli existente nu pot fi șterse.",
    fromRecurring: "Generată automat dintr-o regulă recurentă",
    recurringBtn: "Recurente", recurringTitle: "Cheltuieli recurente",
    recurringSubtitle: "Reguli care creează automat o cheltuială în fiecare săptămână, lună sau an.",
    recurringEmpty: "Nicio regulă recurentă încă. Adaugă o cheltuială și activează „Fă recurentă”.",
    makeRecurring: "Fă recurentă", makeRecurringDesc: "Repetă automat această cheltuială după un program.",
    frequency: "Frecvență", freqWeekly: "Săptămânal", freqMonthly: "Lunar", freqYearly: "Anual",
    startDate: "Data de început", endDate: "Data de sfârșit (opțional)", endAfterStart: "Data de sfârșit trebuie să fie după data de început",
    nextCharge: "Următoarea", ends: "Se termină", forever: "Rulează până la oprire",
    pause: "Pune pe pauză", resume: "Reia", paused: "Pe pauză", resumed: "Reluată",
    editRule: "Editează cheltuiala recurentă",
    ruleCreated: "Cheltuială recurentă creată", ruleUpdated: "Regulă recurentă actualizată", ruleDeleted: "Regulă recurentă ștearsă",
    ruleDeleteTitle: "Ștergi această regulă recurentă?",
    ruleDeleteDesc: "Cheltuielile viitoare nu vor mai fi create. Cele deja generate rămân în istoric.",
  },

  guide: {
    title: "Ghid de utilizare",
    subtitle: "Învață cum folosești LactoPilot și ce face fiecare setare. Alege un subiect din listă.",
    contents: "Cuprins",
  },

  operators: {
    title: "Operatori", subtitle: "Gestionează echipa",
    add: "Adaugă operator", edit: "Editează operator", new: "Operator nou",
    name: "Nume", jobTitle: "Funcție / rol", notes: "Note",
    linkedUser: "Cont de autentificare asociat", linkedUserHint: "Opțional — permite pontajul din contul propriu",
    noUser: "Fără cont", active: "Activ", inactive: "Inactiv",
    status: "Status", linked: "Asociat", empty: "Niciun operator încă. Adaugă primul.",
    created: "Operator adăugat", updated: "Operator actualizat", deleted: "Operator șters",
    confirmDelete: "Ștergi acest operator?", deleteBlocked: "Acest operator are pontaje — dezactivează în loc să ștergi.",
    saveFailed: "Operatorul nu a putut fi salvat", count: "{n} operatori", actions: "Acțiuni",
  },

  clock: {
    title: "Pontaj", subtitle: "Pontează la sosire, depontează la plecare",
    myClock: "Pontajul meu", kiosk: "Kiosk", history: "Istoric",
    clockIn: "Pontează", clockOut: "Depontează",
    clockedIn: "Pontat", notClockedIn: "Nepontat",
    since: "De la {time}", todayWorked: "Azi: {dur}",
    nowAtWork: "La muncă acum", peopleIn: "{n} pontați", totalToday: "Total azi",
    selectOperator: "Alege un operator pentru pontaj", noOperators: "Niciun operator activ. Adaugă operatori întâi.",
    notLinked: "Contul tău nu este asociat unui operator. Cere unui admin să îl asocieze sau folosește kiosk-ul de mai jos.",
    clockedInToast: "{name} a pontat", clockedOutToast: "{name} a depontat · {dur}",
    actionFailed: "Acțiunea a eșuat", entriesEmpty: "Niciun pontaj pentru această zi.",
    operator: "Operator", in: "Intrare", out: "Ieșire", duration: "Durată", date: "Data", source: "Sursă",
    sourceKiosk: "Kiosk", sourceSelf: "Propriu", ongoing: "în curs", viewDay: "Ziua",
    filterDay: "Zi", filterWeek: "Săptămână", filterMonth: "Lună", filterView: "Vizualizare",
    rangeLabel: "{from} – {to}", weekendBadge: "Weekend", overtimeBadge: "Ore suplimentare",
    legendWeekend: "Zi nelucrătoare", legendOvertime: "Peste {h}h (ore suplimentare)",
    totalRange: "Total în interval", entriesEmptyRange: "Nicio înregistrare în acest interval.",
    break: "Pauză", totalBreak: "Total pauză", totalOvertime: "Total ore suplimentare", totalDeficit: "Total deficit", allEmployees: "Toți angajații", weekendOnly: "Doar weekend",
    overtimeBasis: "Orele suplimentare și deficitul se calculează pe săptămână ISO față de {h}h/săptămână obligatorii (zi standard × zile lucrătoare), net de zilele lipsă; concediile și sărbătorile sunt excluse.",
    timeOffTitle: "Concedii & sărbători", timeOffHint: "Concediile și sărbătorile scutesc zilele lucrătoare, astfel încât nu sunt luate în calcul la orele obligatorii săptămânale.",
    leavesTitle: "Concedii angajați", addLeave: "Adaugă concediu", leavesEmpty: "Niciun concediu în această perioadă.",
    holidaysTitle: "Sărbători companie", addHoliday: "Adaugă sărbătoare", holidaysEmpty: "Nicio sărbătoare în această perioadă.",
    leaveDialogHint: "Scutește zilele lucrătoare ale unui angajat (concediu, medical, neplătit). Aceste zile sunt eliminate din orele obligatorii săptămânale.",
    leaveStart: "De la", leaveEnd: "Până la", leaveTypeLabel: "Tip",
    leaveType_leave: "Concediu", leaveType_sick: "Medical", leaveType_unpaid: "Neplătit", leaveType_other: "Altul",
    holidayDialogHint: "O zi nelucrătoare la nivel de companie, scutită pentru toți angajații.", holidayDate: "Dată", holidayName: "Denumire", holidayNamePh: "ex. Ziua Națională",
    offFillAll: "Completați toate câmpurile obligatorii.",
    leaveSaved: "Concediu adăugat", leaveDeleted: "Concediu șters", holidaySaved: "Sărbătoare adăugată", holidayDeleted: "Sărbătoare ștearsă",
    breakMinutes: "Pauză (minute)", breakHint: "Timpul total de pauză care se scade din orele lucrate.",
    // Admin manual edit
    manage: "Gestionează pontaje", addEntry: "Adaugă pontaj", editEntry: "Editează pontaj",
    addEntryTitle: "Adaugă o înregistrare", editEntryTitle: "Editează înregistrarea",
    selectOp: "Operator", clockInTime: "Intrare", clockOutTime: "Ieșire (opțional)",
    leaveOpenHint: "Lasă ieșirea goală pentru a păstra pontajul deschis.",
    entryCreated: "Pontaj adăugat", entryUpdated: "Pontaj actualizat", entryDeleted: "Pontaj șters",
    confirmDeleteEntry: "Ștergi acest pontaj? Acțiunea nu poate fi anulată.",
    saveEntryFailed: "Pontajul nu a putut fi salvat", deleteEntryFailed: "Pontajul nu a putut fi șters",
    photo: "Foto", photoIn: "Foto intrare", photoOut: "Foto ieșire", noPhoto: "Fără foto",
    actionsCol: "Acțiuni", sourceManual: "Manual",
    // Kiosk link / banner
    openKiosk: "Deschide kiosk", kioskLink: "Link kiosk", copyLink: "Copiază link", linkCopied: "Link kiosk copiat",
    kioskHint: "Distribuie acest link pe o tabletă comună pentru ca personalul să poată ponta cu o fotografie.",
    // Public kiosk page
    kioskTitle: "Kiosk Pontaj", kioskSubtitle: "Atinge numele tău, fă o fotografie și pontează intrarea sau ieșirea.",
    kioskPickPrompt: "Cine pontează?", kioskTakePhoto: "Fă fotografia", kioskRetake: "Refă",
    kioskConfirmIn: "Confirmă intrarea", kioskConfirmOut: "Confirmă ieșirea",
    kioskCameraError: "Camera nu este disponibilă. Permite accesul la cameră.",
    kioskStartCamera: "Pornește camera", kioskCancel: "Anulează", kioskDone: "Gata",
    kioskSuccessIn: "{name} a pontat intrarea", kioskSuccessOut: "{name} a pontat ieșirea · {dur}",
    kioskBack: "Înapoi", kioskNoOps: "Niciun operator activ încă.", kioskRefresh: "Reîmprospătează",
    kioskCapturing: "Se capturează…", kioskProcessing: "Se procesează…",
    startBreak: "Începe pauza", endBreak: "Termină pauza", onBreak: "În pauză", onBreakSince: "În pauză de la {time}",
    kioskConfirmBreakStart: "Confirmă începerea pauzei", kioskConfirmBreakEnd: "Confirmă terminarea pauzei",
    kioskSuccessBreakStart: "{name} este în pauză", kioskSuccessBreakEnd: "{name} a revenit din pauză",
  },

  tasks: {
    title: "Sarcini", subtitle: "Atribuie sarcini operatorilor",
    add: "Sarcină nouă", edit: "Editează sarcina",
    taskTitle: "Titlu", description: "Descriere", assignTo: "Atribuie lui",
    unassigned: "Neatribuit", dueDate: "Termen", linkTo: "Deschide secțiunea",
    noLink: "Niciuna", markDone: "Marchează finalizat", reopen: "Redeschide", open: "Deschisă", done: "Finalizată",
    openTasks: "Deschise", doneTasks: "Finalizate", allTasks: "Toate", filterOperator: "Operator",
    empty: "Nicio sarcină încă.", emptyOpen: "Nicio sarcină deschisă.", emptyDone: "Nicio sarcină finalizată încă.",
    created: "Sarcină creată", updated: "Sarcină actualizată", deleted: "Sarcină ștearsă",
    statusUpdated: "Sarcină actualizată", confirmDelete: "Ștergi această sarcină?",
    saveFailed: "Sarcina nu a putut fi salvată", dueOn: "Termen {date}", completedBy: "Finalizat de {name}",
    goTo: "Deschide {section}", createdBy: "De {name}",
    repeat: "Repetare", repeatHint: "La finalizare, următoarea apariție se creează automat și apare în Memento când e scadentă.",
    every: "La fiecare", unitDaysPlain: "zile", unitWeeksPlain: "săptămâni",
    recur_none: "O singură dată", recur_daily: "Zilnic", recur_weekly: "Săptămânal", recur_monthly: "Lunar", recur_custom: "Personalizat",
    recur_working_days: "Zile lucrătoare (L–V)", recur_weekdays: "Zile din săptămână",
    dayMon: "Lu", dayTue: "Ma", dayWed: "Mi", dayThu: "Jo", dayFri: "Vi", daySat: "Sâ", daySun: "Du",
    pickDays: "Alege cel puțin o zi",
    recurEvery: "La fiecare {interval}", unitDays: "zile", unitWeeks: "săptămâni",
    ccpField: "Punct critic de control", noCcp: "Niciunul", ccp: "PCC",
    ccpHint: "Finalizarea acestei sarcini va deschide formularul HACCP pentru a înregistra citirea acestui PCC.",
    ccpLog: "Înregistrează PCC: {name}", autoFromCcp: "Auto din PCC",
  },

  complaints: {
    title: "Sesizări", subtitle: "Registru intern de sesizări și probleme",
    add: "Sesizare nouă", file: "Depune o sesizare",
    complaintTitle: "Titlu", description: "Descriere", about: "Despre operator (opțional)",
    none: "—", status: "Status",
    open: "Deschisă", inProgress: "În lucru", resolved: "Rezolvată",
    setStatus: "Setează status", markResolved: "Marchează rezolvată", reopen: "Redeschide",
    empty: "Nicio sesizare depusă.", filterAll: "Toate", filterOpen: "Deschise", filterResolved: "Rezolvate",
    created: "Sesizare depusă", updated: "Sesizare actualizată", deleted: "Sesizare ștearsă",
    confirmDelete: "Ștergi această sesizare?", saveFailed: "Sesizarea nu a putut fi salvată",
    filedBy: "Depusă de {name}", resolvedBy: "Rezolvată de {name}", aboutOperator: "Despre {name}",
  },

  userMenu: { signOut: "Deconectare" },
  changePw: {
    menu: "Schimbă parola", title: "Schimbă parola",
    current: "Parola curentă", new: "Parolă nouă", confirm: "Confirmă parola nouă",
    success: "Parola a fost schimbată", failed: "Schimbarea parolei a eșuat",
    tooShort: "Parola trebuie să aibă cel puțin 6 caractere", mismatch: "Parolele nu coincid",
  },
  activate: {
    title: "Activează-ți contul", subtitle: "Setează o parolă pentru a finaliza configurarea contului",
    missing: "Acestui link de activare îi lipsește tokenul.",
    newPassword: "Parolă nouă", confirmPassword: "Confirmă parola", activate: "Activează contul",
    tooShort: "Parola trebuie să aibă cel puțin 6 caractere", mismatch: "Parolele nu coincid",
    failed: "Activarea a eșuat. Linkul poate fi invalid sau expirat.",
    successTitle: "Cont activat", successBody: "Contul tău este gata. Te poți conecta acum.",
    errorTitle: "Activarea a eșuat", goToLogin: "Mergi la conectare",
  },
  reset: {
    title: "Resetează parola", subtitle: "Alege o parolă nouă pentru contul tău",
    missing: "Acestui link de resetare îi lipsește tokenul.",
    newPassword: "Parolă nouă", confirmPassword: "Confirmă parola", reset: "Resetează parola",
    tooShort: "Parola trebuie să aibă cel puțin 6 caractere", mismatch: "Parolele nu coincid",
    failed: "Resetarea a eșuat. Linkul poate fi invalid sau expirat.",
    successTitle: "Parolă actualizată", successBody: "Parola ta a fost schimbată. Te poți conecta acum.",
    errorTitle: "Resetarea a eșuat", goToLogin: "Mergi la conectare",
  },
  reports: {
    title: "Rapoarte vânzări", subtitle: "Vânzări livrate pe produs, cantitate și venit.",
    revenue: "Venit", prevRevenue: "Venit ant.", totalQty: "Cantitate totală",
    acrossUnits: "pe {n} unitate/unități", productLines: "Linii de produs", trendTitle: "Tendință vânzări",
  },

  stockMovements: {
    title: "Mișcări de stoc",
    subtitle: "Fiecare produs finit care intră în stoc (producție, sub-variante) și iese (vânzări, casări), cu sold curent pe produs.",
    from: "De la", to: "Până la", allProducts: "Toate produsele",
    export: "Export Excel", exportDone: "Export gata", exportError: "Exportul a eșuat", loadError: "Nu s-au putut încărca mișcările de stoc",
    totalIn: "Total intrări", totalOut: "Total ieșiri", netChange: "Variație netă", movements: "Mișcări",
    acrossProducts: "pe {n} produs(e)",
    balanceTitle: "Sold pe produs (kg)", detailTitle: "Detalii mișcări",
    opening: "Sold inițial", inKg: "Intrări (kg)", outKg: "Ieșiri (kg)", closing: "Sold final",
    date: "Dată", direction: "Sens", movementType: "Tip", qty: "Cantitate", kg: "Kg", lot: "Lot / șarjă", reference: "Referință",
    empty: "Nicio mișcare de stoc în această perioadă.", emptyDetail: "Nicio mișcare de afișat pentru filtrele selectate.",
    kgNote: "Soldurile sunt afișate în echivalent kg (greutate netă) pentru a putea însuma ambalaje diferite. Sold final = Sold inițial + Intrări − Ieșiri.",
    dir: { in: "Intrare", out: "Ieșire" },
    type: {
      production: "Randament producție", subvariant: "Ambalare sub-variantă", repack: "Reambalare",
      manual: "Introducere manuală", adjustment: "Ajustare", sale: "Vânzare / livrare", carve: "Extragere sub-variantă",
      writeoff: "Casare",
    },
    losses: {
      title: "Pierderi pe motiv",
      subtitle: "Casările din perioada selectată, împărțite pe motive — tiparele de risipă ies imediat în evidență.",
      writeOffs: "{n} casare(i)",
      empty: "Nicio pierdere înregistrată în această perioadă.",
      estValueNote: "Valori estimate pe baza listei de prețuri (nivel de bază 1).",
      uncovered: "{kg} kg fără preț definit — valoarea este subestimată.",
      reason: { expired: "Expirat", damaged: "Deteriorat", quality: "Problemă de calitate", other: "Alt motiv", unspecified: "Nespecificat" },
    },
  },
  losses: {
    title: "Pierderi & Casări",
    subtitle: "Fiecare casare și reducere de stoc, cu motiv, valoare estimată și anulare cu un click.",
    loadError: "Nu s-au putut încărca pierderile",
    totalLost: "Total pierdut",
    estValue: "Valoare estimată",
    writeOffCount: "Casări",
    revertedCount: "+{n} anulate (nu sunt numărate)",
    reasonCol: "Motiv",
    valueCol: "Valoare est.",
    byWhom: "De către",
    allReasons: "Toate motivele",
    empty: "Nicio pierdere în această perioadă.",
    tableNote: "Casările anulate rămân listate pentru audit, dar sunt excluse din totaluri și din toate rapoartele de pierderi.",
    undo: "Anulează",
    reverted: "Anulată",
    revertConfirmTitle: "Anulezi această casare?",
    revertConfirmText: "{qty} {unit} vor fi returnate în lotul {code} · {product}. Pierderea dispare din toate rapoartele; Jurnalul de activitate păstrează atât înregistrarea originală (marcată), cât și anularea.",
    revertDone: "Casare anulată — stoc restaurat pentru {code}",
    revertError: "Casarea nu a putut fi anulată",
    manageReasons: "Gestionează motivele",
    builtinReasons: "Motive predefinite",
    customReasons: "Motivele tale personalizate",
    noCustomReasons: "Niciun motiv personalizat încă — adaugă unul mai jos.",
    newReason: "Motiv nou",
    newReasonPh: "ex. Furt, Vărsare, Returnat de client",
    reasonAdded: "Motiv adăugat",
    reasonDeleted: "Motiv eliminat",
    reasonAddError: "Motivul nu a putut fi salvat",
    manageHint: "Eliminarea unui motiv personalizat doar îl ascunde din selector — casările existente îl păstrează în rapoarte.",
    exportXlsx: "Export Excel",
    exportError: "Exportul a eșuat — încearcă din nou",
  },
  ncr: {
    title: "Registru neconformități",
    subtitle: "Fiecare verificare de calitate picată din producții, cu acțiunea sa corectivă — o singură listă pentru audit HACCP.",
    loadError: "Registrul nu a putut fi încărcat",
    totalCard: "Neconformități",
    withCorrective: "Cu acțiune corectivă",
    missingCorrective: "Fără acțiune corectivă",
    checkCol: "Verificare",
    issueCol: "Problemă",
    operatorCol: "Operator",
    missing: "Lipsește",
    empty: "Nicio neconformitate în această perioadă — totul în parametri.",
    tableNote: "Folosește meniul rândului pentru a confirma, adăuga acțiunea corectivă, rezolva sau închide o neconformitate. Notele corective scrise pe șarjă sunt afișate și ele.",
    statusCol: "Stare NC", openCard: "Deschise", resolvedCard: "Rezolvate",
    filter_active: "Active (fără cele închise)", filter_open: "Deschise", filter_acknowledged: "Confirmate", filter_resolved: "Rezolvate", filter_dismissed: "Închise", filter_all: "Toate",
    status_open: "Deschisă", status_acknowledged: "Confirmată", status_resolved: "Rezolvată", status_dismissed: "Închisă",
    actAcknowledge: "Confirmă (văzut)", actResolve: "Marchează ca rezolvată…", actAddCorrective: "Adaugă acțiune corectivă…", actEditCorrective: "Editează acțiunea corectivă…",
    actDismiss: "Închide (nu se aplică)…", actReopen: "Redeschide",
    resolveTitle: "Marchează neconformitatea ca rezolvată", editCorrectiveTitle: "Acțiune corectivă", dismissTitle: "Închide neconformitatea",
    correctivePlaceholder: "Ce s-a făcut pentru corectarea abaterii și prevenirea repetării…", noteOpt: "Notă (opțional)",
    dismissReason: "Motiv", dismissPlaceholder: "ex. eroare de măsurare, reverificat în limite, înregistrare dublă…",
    dismissHint: "Elementele închise rămân în registru și în export pentru audit, dar sunt excluse din numărul de deschise / lipsă.",
    dismissBtn: "Închide", resolveBtn: "Marchează ca rezolvată",
    correctiveRequired: "Descrie acțiunea corectivă înainte de a marca ca rezolvată.", reasonRequired: "Indică un motiv pentru închidere.",
    acknowledged: "Neconformitate confirmată", resolvedToast: "Neconformitate marcată ca rezolvată", dismissed: "Neconformitate închisă",
    reopened: "Neconformitate redeschisă", correctiveSaved: "Acțiune corectivă salvată", saveError: "Modificarea nu a putut fi salvată",
    issueFailOption: "opțiune neconformă",
    belowMin: "sub minim",
    aboveMax: "peste maxim",
  },
  perm: {
    title: "Permisiuni roluri",
    subtitle: "Alege ce secțiuni poate accesa fiecare rol. Administratorii au întotdeauna acces complet.",
    adminNote: "Administratorul are întotdeauna acces la toate secțiunile — acest lucru nu poate fi schimbat.",
    sectionCol: "Secțiune",
    dashboard: "Tablou de bord",
    alwaysOn: "Disponibil întotdeauna pentru toți",
    save: "Salvează modificările", saving: "Se salvează…",
    savedToast: "Permisiuni actualizate", saveError: "Salvarea permisiunilor a eșuat", loadError: "Încărcarea permisiunilor a eșuat",
    all: "Toate", none: "Niciuna",
    roleManager: "Manager", roleQa: "QA", roleOperator: "Operator",
    groupGeneral: "General",
  },

  billing: {
    invoices: "Facturi", invoice: "Factură", plan: "Plan", plans: "Planuri",
    statusDraft: "Ciornă", statusSent: "Trimisă", statusPaid: "Plătită", statusOverdue: "Restantă",
    number: "Număr", org: "Organizație", amount: "Sumă", currency: "Monedă",
    issueDate: "Data emiterii", dueDate: "Scadență", status: "Status", notes: "Note",
    actions: "Acțiuni", downloadPdf: "PDF", markPaid: "Marchează plătită", markSent: "Marchează trimisă",
    edit: "Editează", delete: "Șterge", create: "Creează factură", save: "Salvează",
    lines: "Linii", description: "Descriere", qty: "Cant.", unitPrice: "Preț unitar",
    addLine: "Adaugă linie", total: "Total", empty: "Nicio factură încă.", period: "Perioadă",
    createdToast: "Factură creată", updatedToast: "Factură actualizată", deletedToast: "Factură ștearsă",
    statusToast: "Status factură actualizat", loadError: "Încărcarea datelor de facturare a eșuat",
    actionError: "Acțiunea a eșuat", confirmDelete: "Ștergi această factură?",
    filterAll: "Toate statusurile", monthly: "/lună", yearly: "/an",
    priceRon: "Preț (RON)", priceEur: "Preț (EUR)", features: "Caracteristici", planName: "Nume plan",
    savePlan: "Salvează plan", plansSaved: "Planuri actualizate",
    milkLimit: "Limită lapte (L/lună)", milkLimitHint: "0 = nelimitat", milkUsage: "Consum lapte",
    thisMonth: "luna aceasta", unlimited: "Nelimitat",
    pageTitle: "Facturare", pageSubtitle: "Planul de abonament și facturile tale",
    currentPlan: "Plan curent", noPlan: "Niciun plan alocat",
    outstanding: "Sold restant", allPaid: "Toate facturile sunt plătite. Mulțumim!",
    lockedBanner: "Spațiul de lucru este blocat din cauza unei facturi restante. Te rugăm să achiți soldul de mai jos pentru a restabili accesul.",
    contactSupport: "Contactează suportul", myInvoices: "Facturile mele",
  },

  subproducts: {
    title: "Subproduse în stoc", subtitle: "Loturi trasabile de subproduse rezultate din producții",
    lotsTitle: "Loturi de subproduse", inStockOnly: "Doar în stoc",
    filterName: "Subprodus", allNames: "Toate subprodusele", dateFrom: "De la", dateTo: "Până la", reset: "Resetează",
    lotCode: "Cod lot", name: "Subprodus", source: "Din producția", date: "Data",
    quantity: "Produs", remaining: "Rămas", depleted: "Epuizat",
    adjust: "Ajustează stocul", adjustTitle: "Ajustează stocul de subprodus",
    newRemaining: "Cantitate rămasă nouă", setZero: "Golește (0)",
    rangeHint: "Valoarea trebuie să fie între 0 și {max}",
    reasonLabel: "Motiv (pentru reducere)", reasonPlaceholder: "Alege un motiv (opțional)",
    noteLabel: "Notă (opțional)",
    adjusted: "Stoc ajustat", adjustError: "Stocul nu a putut fi ajustat",
    empty: "Niciun lot de subprodus găsit.", loadError: "Încărcarea subproduselor a eșuat",
  },

  login: {
    title: "LactoPilot",
    subtitle: "Conectează-te pentru a gestiona laptele, producția și HACCP",
    email: "Email", password: "Parolă", signIn: "Conectare",
    demo: "Conturi demo (apasă pentru completare)",
    manager: "Manager", qa: "CC", operator: "Operator",
    failed: "Conectare eșuată. Încearcă din nou.",
    forgotPassword: "Ai uitat parola?", forgotTitle: "Resetează parola",
    forgotHint: "Introdu emailul contului și îți trimitem un link pentru resetarea parolei.",
    forgotSend: "Trimite link de resetare", forgotSent: "Dacă acel email există, am trimis un link de resetare.",
  },

  landing: {
    nav: {
      features: "Funcționalități", industries: "Industrii", pricing: "Prețuri", testimonials: "Testimoniale",
      how: "Cum funcționează",
      login: "Intră în cont", getStarted: "Începe acum", openApp: "Deschide aplicația",
      menu: "Meniu", themeToggle: "Schimbă tema", tagline: "ERP producție lactate",
    },
    hero: {
      badge: "ERP pentru producători de lactate",
      title1: "ERP-ul construit pentru", title2: "Producători de lactate",
      subtitle: "De la recepția laptelui la produse finite, LactoPilot te ajută să planifici, produci, urmărești și să crești cu încredere.",
      ctaPrimary: "Începe acum", ctaSecondary: "Vezi demo",
      proofItems: ["Trasabilitate completă", "Date în timp real", "Creat pentru lactate"],
    },
    mock: {
      barTitle: "Tablou de bord", plant: "Fabrica Lapte 1",
      todayProduction: "Producție azi", milkIntake: "Recepție lapte", yield: "Randament (luna)", waste: "Pierderi",
      overview: "Prezentare producție", thisMonth: "Luna aceasta", lastMonth: "Luna trecută",
      topProducts: "Top produse", recentBatches: "Loturi recente", qualityAlerts: "Alerte calitate",
      batchId: "ID lot", product: "Produs", qty: "Cant.", status: "Stare",
      inProgress: "În lucru", completed: "Finalizat", viewAll: "Vezi toate",
      cheese: "Brânză", yogurt: "Iaurt", milk: "Lapte", other: "Altele",
    },
    features: {
      title: "Toate instrumentele de care ai nevoie, într-o platformă",
      subtitle: "Module integrate care acoperă întregul flux — de la recepția laptelui la produse finite, trasabile.",
      items: [
        { icon: "CalendarDays", title: "Planificare producție", desc: "Planifică rețete, programe și resurse cu ușurință." },
        { icon: "Boxes", title: "Urmărire loturi", desc: "Urmărește fiecare lot în timp real, de la început la final." },
        { icon: "ClipboardList", title: "Gestionare rețete", desc: "Standardizează rețetele și menține consistența." },
        { icon: "Package", title: "Control stocuri", desc: "Gestionează materii prime, ambalaje și produse finite." },
        { icon: "ShieldCheck", title: "Trasabilitate", desc: "Trasabilitate completă, de la fermă la produsul finit." },
        { icon: "FlaskConical", title: "Control calitate", desc: "Asigură calitatea cu verificări și integrări de laborator." },
        { icon: "Truck", title: "Vânzări & distribuție", desc: "Gestionează comenzi, livrări și relații cu clienții." },
        { icon: "PieChart", title: "Analiză costuri", desc: "Analizează costuri, randamente și crește profitabilitatea." },
      ],
    },
    stats: {
      title: "Date în timp real. Decizii mai bune.",
      subtitle: "Monitorizează ce contează și crește-ți afacerea.",
      items: [
        { icon: "TrendingUp", value: "11.42%", label: "Randament mediu", delta: "+0.6% față de luna trecută", positive: true },
        { icon: "Droplet", value: "98.6%", label: "Utilizare lapte", delta: "+2.1% față de luna trecută", positive: true },
        { icon: "Tag", value: "1.35%", label: "Rată pierderi", delta: "-0.3% față de luna trecută", positive: true },
        { icon: "Layers", value: "2.450", label: "SKU active", delta: "+120 luna aceasta", positive: true },
        { icon: "DollarSign", value: "+18.7%", label: "Marjă brută", delta: "+3.4% față de luna trecută", positive: true },
      ],
    },
    industries: {
      title: "Construit pentru orice tip de afacere din lactate",
      subtitle: "De la lactate artizanale la procesatori multi-site — LactoPilot se adaptează modului tău de lucru.",
      items: [
        { icon: "Layers", title: "Fabrici de brânză", desc: "Gestionează rețete complexe și procese de maturare." },
        { icon: "CupSoda", title: "Producători de iaurt", desc: "Menține consistența și calitatea la scară mare." },
        { icon: "Droplets", title: "Fabrici de mozzarella", desc: "Optimizează randamentul și eficiența producției." },
        { icon: "Milk", title: "Procesatori de lapte", desc: "Eficientizează pasteurizarea și ambalarea." },
        { icon: "Home", title: "Lactate artizanale", desc: "Crește-ți afacerea rămânând conform." },
      ],
    },
    how: {
      title: "Implementare fără bătăi de cap",
      subtitle: "Te ghidăm pas cu pas, de la configurare până la go-live.",
      steps: [
        { title: "Mapăm fluxurile", desc: "Recepție → producție → livrare, adaptate la modul real de lucru al fabricii tale." },
        { title: "Importăm & configurăm", desc: "Nomenclatoare, tipuri de produse și acces pe roluri pentru toată echipa." },
        { title: "Activăm HACCP", desc: "CCP-uri, alerte și rapoarte pregătite pentru audit, ca să fii conform din prima zi." },
        { title: "Go-live & suport", desc: "Lansăm împreună, apoi îmbunătățim continuu cu suport în limba română." },
      ],
    },
    testimonials: {
      title: "Fabrici care lucrează mai inteligent",
      subtitle: "Construit împreună cu și pentru procesatorii de lactate și brânzeturi.",
      items: [
        { quote: "Am renunțat la tabele și registre pe hârtie. Acum trasabilitatea de la lapte la livrare durează secunde, nu ore.", name: "Andrei Munteanu", role: "Director fabrică, fabrică de brânzeturi" },
        { quote: "Auditurile HACCP erau stresante. Cu LactoPilot, rapoartele sunt mereu pregătite, iar excepțiile sunt urmărite.", name: "Ioana Petrescu", role: "Responsabil CC, procesator lactate" },
        { quote: "Planificarea producției și stocul comunică în sfârșit. Mai puține greșeli, randamente mai bune.", name: "Cristian Dobre", role: "Proprietar, lactate de familie" },
      ],
    },
    pricing: {
      title: "Prețuri simple, transparente",
      subtitle: "Începe cu o singură locație și scalează la operațiuni multi-site.",
      perMonth: "/lună", popular: "Cel mai popular", customPrice: "La cerere", contactCta: "Discută cu noi",
      tiers: [
        { name: "Start", for: "o singură locație", price: "€149", cta: "Începe", highlight: false,
          features: ["Recepție lapte & furnizori", "Urmărire producție de bază", "Stocuri & comenzi", "Suport pe email"] },
        { name: "Pro", for: "producție + HACCP", price: "€349", cta: "Alege Pro", highlight: true,
          features: ["Tot ce include Start", "HACCP complet & rapoarte de conformitate", "Planificare & calculator Pearson", "Personal & pontaj", "Suport prioritar"] },
        { name: "Enterprise", for: "multi-site + integrări", price: "La cerere", cta: "Discută cu noi", highlight: false,
          features: ["Tot ce include Pro", "Management multi-site", "Integrări personalizate", "Onboarding dedicat", "SLA & instruire"] },
      ],
    },
    finalCta: {
      eyebrow: "Pregătit să preiei controlul?",
      title: "Hai să creștem împreună afacerea ta din lactate.",
      subtitle: "Alătură-te producătorilor de lactate care au încredere în LactoPilot pentru operațiuni mai inteligente.",
      ctaPrimary: "Începe acum",
      ctaSecondary: "Contactează vânzările",
    },
    footer: {
      tagline: "Operațiuni, trasabilitate și siguranță alimentară pentru fabrici de lactate moderne.",
      product: "Produs", company: "Companie", legal: "Legal",
      rights: "Toate drepturile rezervate.", madeIn: "Construit pentru industria lactatelor",
      links: {
        features: "Funcționalități", pricing: "Prețuri", how: "Cum funcționează",
        about: "Despre noi", contact: "Contact", support: "Suport",
        privacy: "Confidențialitate", terms: "Termeni",
      },
    },
  },

  client: {
    store: "Magazin LactoPilot", portal: "Portal client", shop: "Magazin", myOrders: "Comenzile mele",
    clientRole: "Client",
    catalogEmpty: "Niciun produs disponibil momentan.",
    perUnit: "per {unit}", madeToOrder: "la comandă",
    available: "{qty} {unit} disponibil", outOfStock: "Stoc epuizat",
    packageType: "Tip ambalaj", inStock: "în stoc",
    priceNotSet: "Preț nestabilit", priceNotSetHint: "Acest ambalaj este disponibil dar nu are încă preț. Cere-ne să stabilim un preț pentru a-l comanda.",
    addToOrder: "Adaugă la comandă", cart: "Coș", placeOrder: "Plasează comanda",
    yourOrder: "Comanda ta", emptyCart: "Coșul tău este gol.",
    orderPlaced: "Comandă plasată cu succes", deliveryAddress: "Adresă de livrare",
    noOrders: "Nu ai nicio comandă încă.", orderCode: "Comandă", placedOn: "Plasată pe",
    yourPrices: "{name} · prețurile tale (Nivel {level})",
    levelPricing: "Prețuri nivel {level}",
    qtyPlaceholder: "Cant.", addProductsHint: "Adaugă produse pentru a-ți forma comanda.",
    enterQty: "Introdu o cantitate", onlyAvailable: "Doar {qty} {unit} disponibil",
    added: "{product} adăugat", myOrdersTitle: "Comenzile mele",
    orderHistory: "Istoric comenzi", noOrdersYet: "Nu ai plasat nicio comandă încă.",
    colItems: "Articole", emptyCartToast: "Coșul tău este gol",
    failedPlace: "Plasarea comenzii a eșuat",
  },

  status: {
    placed: "Plasată", confirmed: "Confirmată", delivered: "Livrată", cancelled: "Anulată",
  },

  prices: {
    addEdit: "Adaugă / editează preț", title: "Liste de prețuri", empty: "Nicio intrare de preț încă.",
    level: "Nivel {n}", addEntry: "Adaugă intrare preț", editEntry: "Editează intrarea de preț",
    saved: "Lista de prețuri salvată", entryRemoved: "Intrare ștearsă",
  },

  productPackaging: {
    title: "Ambalaje produse",
    subtitle: "Alege în ce dimensiuni de ambalaj se vinde fiecare produs. Aceste selecții controlează listele de ambalaje din Comenzi, Portalul clientului, Listele de prețuri și Stoc. Produsele fără selecție afișează toate ambalajele.",
    addLabel: "Adaugă etichetă ambalaj",
    noLabels: "Nicio etichetă de ambalaj încă. Adaugă prima pentru a le aloca produselor.",
    noProducts: "Niciun produs încă. Adaugă tipuri de produse în Setări companie mai întâi.",
    allAvailable: "Toate ambalajele",
    nSelected: "{n} selectate",
    kgNote: "Vracul „kg” este întotdeauna disponibil ca unitate principală de stoc.",
    stockMode: "Mod stoc", modeBulk: "kg vrac", modePackage: "Pe ambalaj",
    modeBulkHint: "Randamentul producției se adaugă într-un singur stoc kg vrac; disponibilitatea ambalajelor se calculează din el.",
    modePackageHint: "Randamentul se stochează ca loturi separate pe ambalaj; comenzile se scad din acele loturi.",
    labelName: "Etichetă ambalaj",
    labelPlaceholder: "ex. 400g, 8kg, 330ml, borcan 200",
    netKg: "Greutate netă (kg, opțional)",
    netHint: "Folosită pentru a converti ambalajele în/din kg vrac (ex. 400g = 0.4).",
    labelRequired: "Eticheta de ambalaj este obligatorie",
    eanTitle: "Cod de bare EAN pe ambalaj",
    eanCodeTitle: "EAN și cod pe ambalaj",
    eanPlaceholder: "ex. 5941234567890",
    codePlaceholder: "Cod",
    eanCodeHint: "Codul de bare EAN și codul produsului se tipăresc per ambalaj pe Declarația de conformitate.",
    eanSaved: "EAN salvat",
    codeSaved: "Cod salvat",
  },

  products: {
    title: "Produse",
    subtitle: "Gestionează produsele și subprodusele (co-produsele) obținute din fiecare. Subprodusele pot fi selectate rapid la înregistrarea unei producții.",
    addProduct: "Adaugă produs", productName: "Nume produs",
    productPlaceholder: "ex. Telemea, Mozzarella",
    nameRequired: "Numele produsului este obligatoriu",
    labelName: "Nume comercial / de etichetă",
    labelNamePlaceholder: "ex. Cremă de brânză proaspătă „Cremvit”",
    labelNameHint: "Apare pe Declarația de conformitate în locul numelui intern al produsului. Lasă gol pentru a folosi numele produsului.",
    empty: "Niciun produs încă. Adaugă primul produs pentru a începe.",
    active: "Activ", inactive: "Inactiv",
    subproducts: "Subproduse", subproductsCount: "{n} subproduse",
    noSubproducts: "Niciun subprodus încă.",
    addSubproduct: "Adaugă subprodus", subproductPlaceholder: "ex. Zer, Smântână",
    subproductsHint: "Co-produse obținute la fabricarea acestui produs (ex. Zer, Smântână).",
    created: "Produs creat", updated: "Produs actualizat", deleted: "Produs șters",
    saveFailed: "Produsul nu a putut fi salvat", deleteFailed: "Produsul nu a putut fi șters",
    merge: "Îmbină / consolidează", mergeTitle: "Îmbină „{name}” în alt produs",
    mergeHint: "Reatribuie toate producțiile, stocul, comenzile, avizele și ambalajele de la acest produs către cel ales, apoi șterge acest produs (duplicat). Util pentru a elimina un duplicat care nu poate fi șters pentru că este încă folosit.",
    mergeInto: "Îmbină în", mergeSelect: "Alege produsul de păstrat", mergeConfirm: "Îmbină și șterge",
    merged: "„{from}” a fost îmbinat în „{to}”", mergeFailed: "Îmbinarea produsului a eșuat",
    confirmDelete: "Ștergi „{name}”? Acțiunea nu poate fi anulată.",
    renameTitle: "Editează produsul", rename: "Editează",
    code: "Cod", codePlaceholder: "ex. TLM-01", storageTemp: "Temp. depozitare", tempPlaceholder: "ex. +2…+4 °C",
    duplicateSub: "Acest subprodus există deja.",
    days: "zile",
    variantsTitle: "Sub-variante vandabile",
    variantsEmpty: "Nicio sub-variantă încă.",
    variantsHint: "Sub-variantele (ex. „Telemea proaspătă”) se vând ca atare, cu propriul termen de valabilitate mai scurt. Apar în stoc și pot fi vândute ca orice produs.",
    variantNamePlaceholder: "ex. Telemea proaspătă",
    variantShelfPlaceholder: "Valabilitate",
    addVariant: "Adaugă variantă",
    variantShelfDays: "valabilitate {n} zile",
    variantNoShelf: "fără valabilitate setată",
    variantAdded: "Sub-varianta „{name}” a fost adăugată",
    variantRemoved: "Sub-varianta „{name}” a fost ștearsă",
    variantUpdated: "Sub-varianta „{name}” a fost actualizată",
    variantError: "Nu s-a putut actualiza sub-varianta",
    unitKg: "kg", unitL: "L", unit: "Unitate",
    yields: "Subproduse obținute (ieșiri)",
    yieldsHint: "Co-produse obținute la fabricarea acestui produs (ex. Zer, Smântână). Setează unitatea pentru fiecare. Pictograma picătură marchează subprodusele scăzute implicit din lapte la calculul consumului specific.",
    deductOn: "Scăzut implicit din lapte (consum specific) — apasă pentru a dezactiva",
    deductOff: "Nescăzut din lapte — apasă pentru scădere implicită (consum specific)",
    deductToggle: "Comută scăderea din lapte pentru",
    lotPrefix: "Prefix lot",
    lotPrefixHint: "Litere scurte afișate la începutul numerelor de lot generate (ex. TL → TL0207-001). Maxim 5 litere/cifre. Lasă gol pentru fără prefix.",
    consumes: "Subproduse folosite ca materie primă (intrări)",
    consumesHint: "Subproduse pe care acest produs le consumă la producție (ex. Ricotta folosește Zer, Untul folosește Smântână).",
    consumesEmpty: "Niciun subprodus de intrare încă.",
    pickToConsume: "Adaugă un subprodus de consumat",
    noYieldsDefined: "Definește mai întâi subproduse la alte produse (ex. Zer, Smântână) ca să poată fi selectate aici.",
  },

  clients: {
    add: "Adaugă client", title: "Clienți", empty: "Niciun client încă.",
    level: "Nivel {n}", contact: "Contact", portal: "Portal", none: "niciunul",
    portalAccess: "Acces portal", editClient: "Editează clientul", addClient: "Adaugă client",
    companyName: "Nume companie / client", priceLevel: "Nivel preț", contactPerson: "Persoană de contact",
    taxId: "CUI / TVA", regNo: "Nr. înreg.", createLogin: "Creează cont portal",
    addresses: "Adrese de livrare", addAddress: "Adaugă adresă", noAddresses: "Nicio adresă încă. Adaugă una pentru livrări.",
    addrLabelPh: "Etichetă (ex. Depozit)", addrLinePh: "Adresă completă", defaultAddr: "Implicit", setDefault: "Setează implicit",
    docRefs: "Referințe documente", reqCustomOrderNo: "Necesită număr comandă personalizat",
    reqCustomOrderNoHint: "Afișează un câmp pentru numărul comenzii la crearea comenzilor acestui client.",
    reqInvoiceNo: "Necesită număr factură",
    reqInvoiceNoHint: "Afișează un câmp pentru numărul facturii la comenzile și avizele acestui client.",
    createLoginHint: "Permite acestui client să se conecteze pentru a plasa comenzi.",
    loginEmail: "Email conectare", password: "Parolă", saveAccess: "Salvează accesul",
    portalDesc: "Setează sau resetează emailul și parola clientului.",
    nameRequired: "Numele clientului este obligatoriu", loginNeeds: "Contul de portal necesită email și parolă",
    saved: "Client salvat", removed: "Client șters", portalUpdated: "Acces portal actualizat",
    emailPwRequired: "Emailul și parola sunt obligatorii",
  },

  stock: {
    addLot: "Adaugă lot de stoc", title: "Loturi de stoc", empty: "Niciun lot de stoc încă.",
    onHand: "Disponibil fizic", reserved: "Rezervat", available: "Disponibil", expiry: "Expirare",
    source: "Sursă", lotCode: "Cod lot", editLot: "Editează lotul",
    fromProduction: "Din producție (opțional)", manualEntry: "Introducere manuală",
    qtyGt0: "Cantitatea trebuie să fie mai mare ca 0", saved: "Lot de stoc salvat", removed: "Lot șters",
    availability: "Disponibilitate pe produs", lots: "loturi",
    liveAvailability: "Disponibilitate în timp real",
    hideOutOfStock: "Ascunde produsele epuizate",
    totalAvailableKg: "Total disponibil:",
    fromExistingLot: "Dintr-un lot existent",
    newStock: "Stoc nou",
    searchLotOrProduct: "Caută după lot sau produs…",
    noLotMatch: "Niciun lot nu corespunde căutării.",
    rebatchTitle: "Reambalare din lot",
    rebatchHint: "Reambalează o parte dintr-un lot existent într-o șarjă mai mică; kg echivalent se scad din lotul sursă. Expirarea se setează la azi + valabilitatea la refrigerare, dar niciodată după expirarea lotului părinte; data de azi este înregistrată ca dată de ambalare.",
    newPackaging: "Ambalaj nou",
    rebatchUses: "Folosește {kg} kg din {source} (~{avail} kg disponibil)",
    rebatchedToast: "Creat {code} · s-au luat {kg} kg din {source}",
    rebatchedKeptToast: "Creat {code} · s-au luat {kg} kg · s-a păstrat numărul lotului părinte",
    keepSourceLot: "Păstrează numărul lotului părinte",
    keepSourceLotHint: "Cantitatea se scade în continuare din lotul sursă, dar noua șarjă păstrează numărul lotului părinte — și, implicit, data lui de expirare (un număr de lot = o singură expirare).",
    rebatchExpiryInherited: "Moștenită de la lotul părinte ({date}). Debifează „Păstrează numărul lotului părinte” pentru a seta altă expirare.",
    rebatchExpiryTooLate: "Un lot reambalat nu poate expira după lotul părinte ({date}).",
    rebatchResult: "Rezultat: lot {code} · expiră {date}", rebatchNewNumber: "număr nou",
    expiryMismatch: "Lotul părinte expiră {date}", expiryMismatchHint: "Acest lot a păstrat numărul lotului părinte, dar are altă expirare — editează-l ca să corespundă părintelui.",
    outOfStock: "Epuizat",
    allOutOfStock: "Toate produsele sunt epuizate.",
    noSellable: "Niciun produs vandabil încă. Adaugă intrări în lista de prețuri și loturi de stoc.",
    nPackagings: "{n} ambalaje",
    addFirstLot: "Adaugă primul lot", fromProductionCol: "Din producție",
    onHandReservedLine: "fizic {oh} · rezervat {r}",
    expired: "Expirat", daysLeft: "{n}z rămase",
    expiringSoon: "Expiră curând", noExpiringSoon: "Niciun lot nu expiră în următoarele 7 zile.",
    writeOff: "Casează",
    writeOffNote: "Casare — lot expirat",
    writeOffConfirmTitle: "Casezi lotul?",
    writeOffConfirmText: "Lot {code} · {product}: alege cât din cele {qty} {unit} din stoc scoți. Ajustarea și motivul se înregistrează în jurnalul de activitate și în rapoartele de mișcări de stoc.",
    writeOffDone: "Lotul {code} a fost casat",
    writeOffQtyLabel: "Cantitate de casat",
    writeOffQtyMax: "din {qty} {unit} în stoc",
    writeOffQtyInvalid: "Introdu o cantitate între 0 și {qty} {unit}",
    writeOffAll: "Tot",
    writeOffReason: "Motivul casării",
    writeOffNoteOptional: "Notă (opțional)",
    reason: { expired: "Expirat", damaged: "Deteriorat", quality: "Problemă de calitate", other: "Alt motiv" },
    kgHintNoNet: 'Setează o greutate netă pentru "{unit}" în Setări pentru a deduce automat kg vrac.',
    kgHint: 'Ambalare din vrac: ≈ {kg} kg vor fi deduse din stocul vrac "kg" de {product}.',
    savedKgDeducted: "Lot de stoc salvat · {kg} kg deduse din stocul vrac de {product}",
    savedKgShort: "Lot de stoc salvat · doar {deducted} din {required} kg deduse din vrac (lipsă {short} kg)",
    failedSave: "Salvarea lotului a eșuat", failedDelete: "Ștergerea a eșuat", expiryDate: "Data expirării",
    noPrice: "Fără preț", madeOn: "Fabricat la",
    allProducts: "Toate produsele", dateField: "Câmp dată", noMatch: "Niciun lot nu corespunde filtrelor.",
    entryDate: "Data în stoc", entryDateHint: "Când a devenit disponibil acest stoc — folosit pentru a-l afișa la comenzi cu data din această zi sau ulterioară.",
    storageState: "Depozitare", frozen: "Congelat", refrigerated: "Refrigerat",
    takeOutOfFreezer: "Scoate din congelator",
    takeOutSuccess: "{code} mutat la refrigerat · expiră {date}",
    takeOutFailed: "Scoaterea lotului din congelator a eșuat",
    adjustRemaining: "Ajustează stocul", remainingAdjusted: "Cantitatea rămasă a fost actualizată",
    adjustRemainingHint: "Setează direct cantitatea disponibilă pentru acest lot (corecție de stoc / inventar). Nu modifică cantitatea inițială a lotului.",
    lotQty: "Cantitate lot", adjustRange: "Trebuie să fie între 0 și {max} {unit}.", adjustNotePh: "Motivul ajustării (opțional)",
    refrigeratedHint: "Expirarea se setează automat la data în stoc + valabilitatea la refrigerare ({days} zile). O poți modifica manual.",
    refrigeratedNoDsl: "Setează o valabilitate la refrigerare pentru acest produs pentru a calcula automat expirarea la refrigerare.",
    refrigeratedStateHint: "Doar o etichetă — expirarea rămâne cea introdusă. Valabilitatea la refrigerare se aplică la scoaterea din congelator sau la reambalarea în porții mai mici.",
    frozenHint: "Stocul congelat își păstrează întreaga valabilitate. Valabilitatea la refrigerare se aplică doar la scoaterea din congelator sau la reambalare.",
  },

  orders: {
    newOrder: "Comandă nouă", title: "Comenzi", empty: "Nicio comandă încă.",
    order: "Comandă", client: "Client", items: "Articole", placed: "Plasată",
    createDelivery: "Creează aviz de livrare", viewOrder: "Vezi comanda",
    confirm: "Confirmă", cancel: "Anulează comanda",
    createOrder: "Creează comandă", selectClient: "Selectează clientul", addItem: "Adaugă articol",
    deliveryAddress: "Adresă de livrare", selectAddress: "Selectează adresa de livrare",
    noClientAddress: "Acest client nu are o adresă salvată — gestionează adresele în pagina Clienți.",
    selectClientFirst: "Selectează un client pentru a alege adresa de livrare.",
    customOrderNo: "Număr comandă personalizat", customOrderNoPh: "ex. PO-12345",
    invoiceNo: "Număr factură", invoiceNoPh: "ex. INV-2026-001",
    selectProduct: "Selectează produsul", selectPackaging: "Selectează ambalajul",
    deleteOrder: "Ștergi comanda?", deleteOrderDesc: "Comanda {code} pentru {client} va fi ștearsă definitiv. Stocul rezervat este eliberat. Această acțiune nu poate fi anulată.",
    orderDeleted: "Comanda {code} a fost ștearsă", saved: "Comandă creată",
    failedDelete: "Ștergerea comenzii a eșuat", statusToast: "Comandă {status}", deleteBtn: "Șterge comanda",
    addOneItem: "Adaugă cel puțin un articol", orderPlacedToast: "Comandă plasată", failedPlace: "Plasarea comenzii a eșuat",
    editBtn: "Editează comanda", editOrder: "Editează comanda", orderUpdatedToast: "Comandă actualizată",
    clientOption: "{name} (Nivel {level})", noSellable: "Niciun produs vandabil. Setează întâi listele de prețuri și stocul.",
    availLine: "disp {qty} {unit} · {price}/{unit}", placeOrder: "Plasează comanda", unitPrice: "Preț unitar",
    orderDate: "Data comenzii", orderDateHint: "Implicit: astăzi.", backdateHint: "Dată retroactivă — se afișează stocul valabil la această dată.",
    deliveryDate: "Data livrării", deliveryDateRequired: "Data livrării este obligatorie",
    deliveryDaysHint: "Livrări permise în: {days}", deliveryDayNotAllowed: "Livrările sunt permise doar în: {days}",
    dueBy: "De livrat până la (inclusiv)", dueOn: "Zi de livrare (exact)",
    loadingStock: "Se încarcă stocul disponibil…", noSellableDate: "Niciun stoc disponibil la data selectată.",
    selectSourceQty: "Selectează o sursă și o cantitate pentru cel puțin o linie",
    dnCreated: "Aviz de livrare creat · stoc dedus", failedDn: "Crearea avizului de livrare a eșuat",
    deliverDesc: "selectează comenzile de consolidat și împarte liniile pe mai multe loturi.",
    ordersToInclude: "Comenzi de inclus", allocateFrom: "Alocă din stoc / șarje",
    bulkDeliverBtn: "Livrare în bloc ({orders} comenzi · {clients} clienți)", bulkSelectAll: "Selectează toate comenzile deschise de pe pagină", bulkSelectOrder: "Selectează comanda pentru livrare în bloc",
    bulkTitle: "Livrare în bloc — un aviz pe client", bulkDesc: "{clients} clienți · {orders} comenzi. Loturile au fost pre-alocate în ordinea expirării dintr-un stoc comun; ajustează orice lot sau cantitate înainte de creare.",
    bulkNotes: "Notă pe toate avizele (opțional)", bulkAutoAllocate: "Realocă automat", bulkNoClients: "Nu mai sunt clienți de livrat.",
    bulkState_ok: "Gata", bulkState_short: "Incomplet", bulkState_over: "Supra-alocat", bulkRemoveClient: "Exclude acest client",
    bulkUsedElsewhere: "{n} la alți clienți", bulkLotOver: "Lotul {code} are doar {n} disponibil pentru toți clienții.",
    bulkOverAllocated: "Unele loturi sunt alocate peste stocul disponibil — corectează liniile roșii.",
    bulkReady: "{ready} / {total} clienți gata", bulkSubmit: "Creează {n} avize & deduce stocul", bulkCreated: "{n} avize create",
    selectOneOrder: "Selectează cel puțin o comandă.", ordered: "comandat {n}", allocated: "alocat {a} / {o}",
    selectSource: "Selectează sursa", noStock: "Stoc indisponibil", sourceLeft: "{n} rămas",
    expShort: "exp", batchTag: "(șarjă)", addAnotherLot: "Adaugă alt lot", createDeduct: "Creează & deduce stocul",
    deliveryExpiry: "Expiră (livrare)", deliveryExpiryHint: "Est. de la data comenzii",
    shortShelfLifeTitle: "Confirmă valabilitatea scurtă",
    shortShelfLifeConfirm: "Unele produse expiră sub pragul minim de vânzare. Vinzi oricum?",
    sellAnyway: "Vinde oricum",
    lowStockWarning: "Comandă salvată fără stoc suficient pentru {products}. Produ-le înainte de confirmare.",
    lowStockBadge: "Stoc insuficient",
    confirmAnyway: "Confirmă oricum",
    insufficientStockTitle: "Stoc insuficient",
    insufficientStockBody: "Nu există încă suficient stoc pentru această comandă. Poți confirma oricum și produce ulterior.",
    insufficientStockLine: "cerut {requested}, disponibil {available}",
  },

  deliv: {
    title: "Avize de livrare", empty: "Niciun aviz de livrare încă. Creează unul dintr-o comandă sau generează-le mai sus.",
    prepTitle: "Pregătire livrare — marfă de încărcat",
    prepSubtitle: "Totalul mărfii necesare pentru toate comenzile deschise programate în ziua selectată.",
    deliveryDay: "Ziua livrării", generateNotes: "Generează avize",
    noOpenOrders: "Nicio comandă deschisă de livrat pe {day}.",
    loadByPackaging: "Încărcare pe ambalaj", totalKg: "≈ {kg} kg total",
    packs: "pachete", detailedBreakdown: "Detaliere", packsQty: "Pachete / cant.",
    ordersForRun: "Comenzi pentru această tură ({n})",
    deliveryNote: "Aviz de livrare", deleteTitle: "Ștergi avizul de livrare?",
    deleteDesc: "Ștergerea {code} ({client}) va restabili stocul dedus în loturile/șarjele sale și va redeschide comanda/comenzile asociate ca confirmate. Această acțiune nu poate fi anulată.",
    deleteAndRestore: "Șterge & restabilește stocul",
    downloadCert: "Descarcă Certificatul de Conformitate", noteDeleted: "Avizul {code} a fost șters · stoc restabilit · comenzi redeschise",
    failedDownload: "Descărcarea certificatului a eșuat", noOpenDay: "Nicio comandă deschisă pentru această zi",
    createdToast: "{n} avize de livrare create · stoc dedus", skippedToast: "{n} omise: {details}",
    nothingToDeliver: "Nimic de livrat", failedGenerate: "Generarea avizelor de livrare a eșuat",
    approxKg: "≈ kg", itemsCount: "{n} articole", downloadCertShort: "Descarcă certificatul", deleteBtn: "Șterge avizul de livrare",
    printSelected: "Printează selectate ({n})", printSelectedEmpty: "Printează selectate", selectAll: "Selectează toate avizele",
    selectOne: "Selectează {code}", selectToPrint: "Selectează cel puțin un aviz pentru printare", printedToast: "{n} certificat(e) pregătite",
    customOrderNo: "Număr comandă", invoiceNo: "Număr factură", dispatchNo: "Număr aviz", invoiceDocType: "Tip document", invoiceTypeInvoice: "Factură", invoiceTypeDispatch: "Aviz", notSet: "Nesetat", saveRefs: "Salvează referințele", refsSaved: "Referințe actualizate",
    colCode: "Cod", colLot: "Lot", colPackaged: "Ambalat", colDaysLeft: "Zile rămase", colExpiry: "Expiră", colDelExpiry: "Expiră (livrare)", colStorage: "Temp. depozitare",
    expired: "expirat", daysShort: "z", downloadPacking: "Listă ambalare", printCerts: "Certificate de conformitate", printPackingLists: "Liste de ambalare",
  },

  settings: {
    title: "Setări companie", company: "Detalii companie", logo: "Logo",
    packagingOptions: "Opțiuni de ambalare",
    packagingHint: "Formate de ambalare disponibile la crearea loturilor de stoc și a listelor de prețuri. Greutatea netă (kg) este folosită pentru a converti cantitățile la comandă și pentru a crea automat stoc din randamentele de producție.",
    newPackaging: "Etichetă ambalaj nou", net: "Net (kg)", saved: "Setări salvate",
    failedSave: "Salvarea a eșuat", netGt0: "Greutatea netă trebuie să fie mai mare ca 0",
    packAdded: "Ambalaj adăugat", failedAdd: "Adăugarea a eșuat", packRemoved: "Ambalaj șters",
    failedRemove: "Ștergerea a eșuat", failedUpdate: "Actualizarea a eșuat", netUpdated: "Greutatea netă pentru {label} a fost actualizată",
    packRenamed: 'Redenumit „{from}” în „{to}”', renameHint: "Redenumește ambalajul (actualizează toate înregistrările legate)",
    productTypes: "Tipuri de produse", productTypesHint: "Gestionează produsele pe care le fabrici. Tipurile noi apar imediat în Producție, Stoc, Planificare și Liste de prețuri. Dezactivează pentru a le ascunde din liste fără a le șterge.",
    newProductType: "Tip de produs nou", productTypePlaceholder: "ex. Brânză de vaci proaspătă", typeAdded: "Tip de produs adăugat", typeRemoved: "Tip de produs șters",
    saveBtn: "Salvează setările", legalName: "Denumire legală", coc: "Certificat de Conformitate",
    docTitle: "Titlu document", declarationText: "Text declarație",
    declarationHint: "Afișat ca declarație de conformitate pe fiecare certificat.",
    footerText: "Text subsol (opțional)", activeToggle: "Activ — apasă pentru a dezactiva",
    inactiveToggle: "Inactiv — apasă pentru a activa", active: "activ", inactive: "inactiv",
    netShort: "net", packPlaceholder: "ex. tavă 500g", packedTitle: "Vândute la pachet (din stoc)",
    packedHint: "Marchează produsele pe care le ții în vrac (kg) dar le vinzi clienților la pachet. Adaugă un preț pe dimensiune de pachet în Liste de prețuri; pachetele disponibile = câte încap în stocul în kg.",
    minMatLabel: "Zile minime de maturare înainte de vânzare",
    minMatHint: "Produsele ambalate la comandă pot fi vândute doar după ce o șarjă s-a maturat cel puțin atâtea zile.",
    packedBadge: "Vândute la pachet: {products}",
    workSection: "Program de lucru", workSectionHint: "Folosit pentru evidențierea orelor suplimentare la pontaj și pentru mementourile „zilnic în zile lucrătoare”.",
    lotNumbering: "Numerotare loturi",
    lotNumberingHint: "Controlează cum sunt generate numerele de lot pentru producții și loturile de stoc adăugate manual.",
    lotIncludeYear: "Include anul în numerele de lot",
    lotIncludeYearHint: "Când este activat, loturile noi folosesc formatul ZZLLAA-NNN (ex. 020726-003). Când este dezactivat, ZZLL-NNN (ex. 0207-003). Loturile existente își păstrează numerele.",
    lotFormatExample: "Loturile noi vor arăta astfel:",
    yieldCalc: "Calcul randament & lapte",
    yieldCalcHint: "Folosit pentru calculul consumului specific afișat la fiecare producție.",
    milkDensity: "Densitate lapte (kg/L)",
    milkDensityHint: "Transformă litrii de lapte în kg. Laptele integral standard are circa 1,03 kg/L.",
    milkMaxTemp: "Temperatura maximă a laptelui la recepție (°C)",
    milkMaxTempHint: "Recepțiile peste această temperatură sunt marcate în listă, în formular și în rapoartele tipărite. Standard UE pentru lapte crud răcit: 6 °C.",
    digestTitle: "Raport săptămânal pe email",
    digestHint: "În fiecare vineri la 17:00 (ora României): șarjele peste țintă, sumarul producției, mișcările de stoc și loturile care expiră în următoarele 7 zile.",
    digestEnabled: "Trimite raportul săptămânal",
    digestEnabledHint: "Activ implicit. Dezactivează pentru a opri emailul automat de vineri.",
    digestEmails: "Destinatari",
    digestEmailsHint: "Adrese de email separate prin virgulă sau rând nou (max 20). Nu uita să salvezi.",
    digestTestBtn: "Trimite test acum",
    digestSending: "Se trimite…",
    digestTestSent: "Raport de test trimis către: {emails}",
    digestTestFailed: "Raportul de test nu a putut fi trimis",
    digestSectionsTitle: "Secțiuni email",
    digestSectionsHint: "Alege ce secțiuni apar în emailul de vineri.",
    digestSectionsAdminOnly: "Doar administratorii pot modifica secțiunile.",
    digestSecProduction: "Sumar producție",
    digestSecOverTarget: "Șarje peste ținta de consum",
    digestSecStock: "Mișcări de stoc",
    digestSecExpiring: "Loturi care expiră (7 zile)",
    digestSecLosses: "Pierderi pe motiv",
    digestSecNcr: "Neconformități calitate",
    digestSecAudit: "Modificări randament & ajustări stoc",
    digestPerRecipientTitle: "Secțiuni pe destinatar",
    digestPerRecipientHint: "Fiecare destinatar primește secțiunile companiei, dacă nu personalizezi mai jos.",
    digestRecipientDefault: "Secțiunile companiei",
    digestRecipientCustom: "Personalizat",
    adminAlertsTitle: "Alerte administrator",
    adminAlertsEmailLabel: "Emailuri de alertă la modificări (randament & stoc)",
    adminAlertsHint: "Alege cum primești emailuri când se modifică un randament, se ajustează stocul sau se anulează o casare.",
    adminAlertsPersonal: "Preferință personală — alertele din clopoțel rămân active oricum.",
    restockAlertsTitle: "Stoc setat de la 0 la o valoare (reaprovizionare)", restockAlertsHint: "Alege dacă vrei să primești aceste ajustări. Casările și celelalte corecții alertează întotdeauna.",
    restockAlertEmail: "Email", restockAlertBell: "Notificare în clopoțel", restockAlertOn: "Alerte de reaprovizionare activate", restockAlertOff: "Alerte de reaprovizionare oprite",
    adminAlertsModeInstant: "Instant",
    adminAlertsModeInstantHint: "Câte un email imediat, la fiecare modificare.",
    adminAlertsModeDaily: "Sumar zilnic (18:00)",
    adminAlertsModeDailyHint: "Un singur email pe seară cu toate modificările zilei, plus totalul casărilor din ziua respectivă.",
    adminAlertsModeOff: "Oprit",
    adminAlertsModeOffHint: "Fără emailuri — păstrezi alertele din clopoțel.",
    adminAlertsSaved_instant: "Alertele instant pe email au fost activate",
    adminAlertsSaved_daily: "Sumar zilnic activat — un singur email în fiecare seară la 18:00",
    adminAlertsSaved_off: "Alertele pe email au fost oprite — păstrezi alertele din clopoțel",
    adminAlertsTestBtn: "Trimite sumarul de azi",
    adminAlertsTestSent: "Sumarul de azi a fost trimis pe emailul tău ({n} modificări)",
    adminAlertsError: "Preferința nu a putut fi salvată",
    brandingSection: "Branding", brandingHint: "Personalizează aspectul spațiului tău — afișat în bara laterală și în antet.",
    brandName: "Nume afișat", brandNameHint: "Afișat lângă logo. Lasă gol pentru „LactoPilot”.",
    uploadLogo: "Încarcă logo", logoUploaded: "Logo încărcat", accentColor: "Culoare accent",
    standardWorkdayHours: "Ore standard pe zi", standardWorkdayHint: "Turele mai lungi de atât sunt evidențiate ca ore suplimentare.",
    workingDays: "Zile lucrătoare", workingDaysHint: "Mementourile setate „zilnic în zile lucrătoare” sar peste zilele nebifate. Aceste zile sunt considerate lucrătoare și la Pontaj.",
    deliveryDays: "Zile de livrare", deliveryDaysHint: "Comenzile pot fi programate pentru livrare doar în zilele bifate.",
    dayMon: "Lun", dayTue: "Mar", dayWed: "Mie", dayThu: "Joi", dayFri: "Vin", daySat: "Sâm", daySun: "Dum",
    backupTitle: "Backup & export",
    backupHint: "Descarcă un backup complet al datelor companiei — producții, stoc, vânzări, recepție lapte, furnizori, HACCP, sarcini, setări și altele.",
    backupJson: "Descarcă backup JSON",
    backupExcel: "Descarcă fișier Excel",
    backupPreparing: "Se pregătește…",
    backupStarted: "Descărcarea backupului a început",
    backupFailed: "Nu s-a putut genera backupul",
    backupNote: "JSON este un backup complet (ideal pentru păstrare / reimport). Excel este un fișier lizibil, cu câte o filă pentru fiecare tip de date.",
  },

  prod: {
    statuses: "Stări", newProduction: "Producție nouă",
    runsTitle: "Rulări de producție", ofTotal: "din {n}",
    table: "Tabel", board: "Panou",
    allProducts: "Toate produsele", allStatuses: "Toate stările",
    shelfLife: "Termen de valabilitate", maturationLabel: "Maturare",
    emptyStart: "Nicio producție încă. Începe prima rulare.",
    noMatch: "Nicio rulare de producție nu corespunde filtrelor selectate.",
    clearFilters: "Șterge filtrele",
    operator: "Operator", milkL: "Lapte (L)", yieldKg: "Randament (kg)",
    byproducts: "Subproduse", coldRoomReady: "Gata pentru frig", yieldEff: "Ef. randament",
    ready: "gata", daysShort: "{n}z", flagN: "{n} abateri", ok: "OK",
    statusArrow: "Stare → {label}", failedStatus: "Actualizarea stării a eșuat",
    movedTo: "{code} → {label}", failedMove: "Mutarea producției a eșuat",
    dropHere: "Trage aici", exp: "exp",
    statusRenamed: "Stare redenumită", renameFailed: "Redenumirea a eșuat",
    colorFailed: "Actualizarea culorii a eșuat", updateFailed: "Actualizarea a eșuat",
    reorderFailed: "Reordonarea a eșuat", statusDeleted: "Stare ștearsă",
    deleteFailed: "Ștergerea a eșuat", enterStatusName: "Introdu un nume de stare",
    statusAdded: "Stare adăugată", failedAddStatus: "Adăugarea stării a eșuat",
    manageStatuses: "Gestionează stările de producție",
    statusManagerHint: "Reordonează fluxul, redenumește etapele, alege culori, marchează o etapă ca finală (finalizat) sau marchează etapa de Maturare care pornește numărătoarea pentru camera frigorifică. Redenumirea actualizează automat toate producțiile.",
    final: "Finală", addCustomStatus: "Adaugă o stare personalizată",
    statusPlaceholder: "ex. Blocaj calitate", pickColor: "Alege culoarea",
    columns: "Coloane", extraColumns: "Coloane suplimentare",
    expirationDate: "Data expirării", coldRoomReadyDate: "Data gata pentru frig",
    yieldEfficiency: "Eficiență randament", byproductsYield: "Subproduse (randament)",
    qcFields: "Câmpuri CC", noQcFields: "Niciun câmp CC disponibil încă.",
    appliedStd: 'Standardizare aplicată „{name}”',
    addMilkQty: "Adaugă cel puțin o șarjă de lapte cu cantitate",
    needRawOrYield: "Adaugă materie primă (lapte sau un subprodus) sau cel puțin un randament.",
    correctiveAction: "Acțiune corectivă",
    correctivePh: "Descrie ce s-a făcut pentru remedierea neconformității (ex. lot izolat, reverificat, casat)...",
    correctiveRequired: "Este necesară o acțiune corectivă pentru '{field}' (opțiune neconformă selectată)",
    created: "Producție creată", failedCreate: "Crearea producției a eșuat",
    notFound: "Producția nu a fost găsită",
    startPlanned: "Începe producția planificată", newRun: "Rulare nouă de producție",
    tabProduct: "1. Produs", tabMilk: "2. Materii prime", tabQc: "3. CC", tabReview: "4. Revizuire",
    productType: "Tip produs", namePlaceholder: "Nume", nextMilk: "Următorul: Materii prime",
    selectOperator: "Selectează operator",
    madeOnDate: "Data fabricării", madeOnHint: "Când a fost de fapt fabricat acest lot.",
    applyStd: "Aplică un plan de standardizare salvat (opțional)",
    loadStd: "Încarcă standardizarea grăsimii…", stdOption: "{name} · țintă {fat}%",
    addMilkBatch: "Adaugă șarjă de lapte", selectBatch: "Selectează o șarjă disponibilă",
    noMilk: "Lapte indisponibil. Adaugă întâi recepție de lapte.",
    batchOption: "{code} · {l}L · Grăsime {f}%", availFat: "Disponibil {l} L · Grăsime {f}%",
    totalMilk: "Total lapte:", nextQc: "Următorul: CC",
    qcTemplate: "Șablon CC", selectTemplate: "Selectează șablon",
    starterTag: "(de bază)", noTemplates: "Niciun șablon pentru {product}. Creează unul în Șabloane CC.",
    nextReview: "Următorul: Revizuire",
    product: "Produs",
    targetFat: "Grăsime țintă (%)", yield: "Randament",
    yieldPreview: "= {kg} kg · creează automat un lot de stoc",
    yieldNoNet: 'Setează greutatea netă pentru "{unit}" în Setări pentru conversia în kg.',
    addPackaging: "Adaugă ambalaj", yieldTotalKg: "Total: {kg} kg",
    perLineStorageHint: "Alege Congelat sau Refrigerat pentru fiecare ambalaj. Loturile congelate păstrează valabilitatea completă; loturile refrigerate expiră conform valabilității mai scurte la refrigerare.",
    yieldSomeNoNet: "Unele ambalaje nu au greutatea netă setată — setează-o în Setări pentru a le calcula în kg.",
    creating: "Se creează...", createBtn: "Creează producția",
    byproductsOptional: "Subproduse (opțional)", addByproduct: "Adaugă subprodus",
    byproductHintSug: "ex. {sug} recuperat din zer — înregistrează randamentul.",
    byproductHint: "Înregistrează co-produsele obținute din această rulare și randamentul lor.",
    byproductNamePlaceholder: "Nume subprodus (ex. Ricotta)",
    subproductAdd: "Adaugă subprodus", subproductPick: "Alege un subprodus",
    subproductNone: "Niciun subprodus definit pentru acest produs.",
    subproductManageHint: "Gestionează subprodusele în pagina Produse.",
    spInputsTitle: "Subproduse intrate (materie primă)",
    spInputsHint: "Folosește subproduse disponibile (ex. Zer, Smântână) obținute anterior ca materie primă în această rulare.",
    spInputsNone: "Acest produs nu are subproduse de intrare configurate. Setează-le în pagina Produse.",
    spInputsNoStock: "Niciun stoc {names} disponibil încă. Produ-l mai întâi în altă rulare.",
    spPickLot: "Adaugă un lot", spAvailable: "disponibil", spFrom: "din",
    spInsufficient: "Se folosește mai mult decât disponibil pentru unele loturi — înregistrat ca avertisment.",
    spRemove: "Elimină",
    prodDeleted: "Producție ștearsă · lapte restabilit", failedDelete: "Ștergerea producției a eșuat",
    editQc: "Editează CC", reportFailed: "Raportul a eșuat", saveQc: "Salvează CC",
    qcUpdated: "Fișa CC actualizată",
    deleteProdTitle: "Ștergi producția {code}?",
    deleteProdBody: "Aceasta o șterge definitiv și restabilește laptele consumat înapoi în șarjele sursă. Acțiunea este înregistrată și nu poate fi anulată.",
    deleteRestoreMilk: "Șterge & restabilește laptele",
    outOfRange: "{n} valori în afara limitelor",
    manufactureDate: "Data fabricației", shelfNotSet: "Termen de valabilitate nesetat",
    lotNumber: "Nr. lot",
    lotNumberHint: "Schimbarea numărului de lot actualizează toate loturile de stoc, avizele de livrare și înregistrările de trasabilitate asociate.",
    scTitle: "Consum specific",
    scHint: "Laptele transformat în kg (densitate configurabilă), minus subprodusele scăzute, împărțit la randamentul șarjei. Apasă pe un subprodus pentru a-l include/exclude din scădere.",
    scMilk: "Lapte",
    scNetMilk: "Lapte net",
    scDeducted: "Subproduse scăzute",
    scNoSubs: "Această șarjă nu are cantități de subproduse înregistrate pentru scădere.",
    scResult: "Consum specific",
    scUnit: "kg lapte / kg produs",
    scNoYield: "Adaugă un randament pentru calcul.",
    scNoMilkLeft: "Subprodusele scăzute depășesc cantitatea de lapte.",
    scChipOn: "Scăzut din lapte — apasă pentru a exclude",
    scChipOff: "Nescăzut — apasă pentru a scădea din lapte",
    scTarget: "Țintă",
    scOverTarget: "Peste țintă ({target})",
    exportBtn: "Export Excel",
    exportDone: "Export gata",
    exportError: "Exportul a eșuat",
    notInMaturation: "Nu este la maturare", maturationNotSet: "Maturare nesetată",
    vsTarget: "vs țintă {kg} kg", addYieldCompute: "Adaugă un randament pentru calcul",
    noTarget: "Nicio țintă setată", milkInputs: "Intrări de lapte",
    variantStockTitle: "Sub-variante",
    variantStockHint: "Extrage o parte din această șarjă ca sub-variantă vandabilă (ex. Telemea proaspătă). Cantitatea (kg) este scăzută din produsul principal și ambalată cum alegi, cu propriul termen de valabilitate. Ștergerea unei alocări returnează kg la produsul principal.",
    variantStockProduct: "Sub-variantă", variantStockPick: "Alege o sub-variantă",
    variantStockQty: "Cantitate", variantStockUnit: "Unitate", variantStockAdd: "Alocă în stoc",
    variantStockAdded: "S-au adăugat {qty} {unit} de {name} în stoc (lot {lot})",
    variantStockError: "Nu s-a putut adăuga varianta în stoc",
    variantAllocatedKg: "Alocat sub-variantelor",
    variantAllocAdded: "Sub-varianta {name} a fost extrasă și adăugată în stoc",
    variantAllocRemoved: "Alocare de sub-variantă ștearsă — cantitatea a revenit la produsul principal",
    variantAllocRemove: "Șterge alocarea",
    variantAllocRemoveError: "Nu s-a putut șterge alocarea",
    editMilk: "Editează laptele", milkQty: "Cantitate (L)", milkRecalcNote: "Editarea cantității recalculează automat stocul de lapte.",
    milkUpdated: "Consum de lapte actualizat", milkUpdateFailed: "Actualizarea consumului de lapte a eșuat",
    qualityControl: "Control calitate — {name}", addField: "Adaugă câmp",
    field: "Câmp", value: "Valoare", range: "Interval",
    notesPrefix: "Note:",
    fieldNameRequired: "Numele câmpului este obligatoriu", addQcField: "Adaugă câmp CC",
    fieldName: "Nume câmp", fieldNamePlaceholder: "ex. Verificare extra",
    type: "Tip", min: "Min", max: "Max", options: "Opțiuni (separate prin virgulă)",
  },

  planning: {
    intakeNotFound: "Recepția de lapte nu a fost găsită",
    rescheduled: "Reprogramat pe {date}", failedReschedule: "Reprogramarea a eșuat",
    planRemoved: "Plan șters", receptionRemoved: "Recepție ștearsă",
    new: "Nou", productionPlan: "Plan de producție", milkReception: "Recepție lapte",
    tabCalendar: "Calendar", tabRecommendations: "Recomandări",
    legMilkIn: "Lapte primit", legProduced: "Produs", legPlanned: "Planificat (de făcut)",
    tipDrag: "Sfat: trage o producție planificată sau o recepție de lapte în altă zi pentru a o reprograma.",
    tipClick: "Sfat: apasă pe un eveniment verde Produs pentru a-i deschide fișa CC sau pe unul albastru Lapte primit pentru a vedea cât lapte a fost primit.",
    dragToReschedule: " (trage pentru reprogramare)", clickOpenQc: " (apasă pentru a deschide fișa CC)", clickSeeMilk: " (apasă pentru a vedea cât lapte a fost primit)",
    moreEvents: "+{n} altele",
    itemsCount: "{n} elemente",
    todo: "De făcut", noPlanned: "Nicio producție planificată.",
    volumeTbd: "volum nedefinit", unassigned: "nealocat", targetFatLine: "grăsime țintă {n}%",
    startProduction: "Începe producția",
    incomingMilk: "Lapte în sosire", noReceptions: "Nicio recepție de lapte programată.",
    volExpected: "{vol} L estimat", fatLine: "grăsime {n}%", receiveMilk: "Primește lapte",
    taken: "Primit", used: "Folosit", pctUsed: "{n}% folosit", protein: "Proteină",
    productDateRequired: "Produsul și data sunt obligatorii", planSaved: "Plan salvat",
    failedSavePlan: "Salvarea planului a eșuat", editPlan: "Editează planul", newProductionPlan: "Plan nou de producție",
    plannedDate: "Dată planificată", plannedVolume: "Volum planificat (L)", milkBatch: "Șarjă de lapte (opțional)",
    savePlan: "Salvează planul",
    supplierDateRequired: "Furnizorul și data estimată sunt obligatorii", receptionSaved: "Recepție de lapte salvată",
    failedSaveReception: "Salvarea recepției a eșuat", editReception: "Editează recepția de lapte", scheduleReception: "Programează recepție de lapte",
    supplier: "Furnizor", supplierPlaceholder: "Alege furnizorul", noSuppliers: "Niciun furnizor — adaugă mai întâi unul în pagina Furnizori.", expectedDate: "Dată estimată",
    expectedVolume: "Volum estimat (L)", expectedFat: "Grăsime estimată (%)", saveReception: "Salvează recepția",
    months: ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"],
    weekdays: ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"],
  },

  planner: {
    title: "Planificator producție",
    addToCalendar: "Adaugă în calendar ({n})", planThis: "Adaugă în calendarul de planificare", milkShort: "lapte",
    addTitle: "Adaugă recomandările în calendarul de planificare",
    addDesc: "Verifică și ajustează cantitatea, volumul de lapte și data pentru fiecare produs, apoi adaugă-le ca producții planificate. Litrii de lapte sunt calculați din randamentul produsului; poți modifica orice valoare.",
    addAllDate: "Data pentru toate", addSelectAll: "Selectează tot", addKg: "De produs (kg)", addMilk: "Lapte (L)",
    addRecommended: "Recomandat: {kg} kg", addYieldTarget: "Randament țintă {y} kg / 100 L", addYieldActual: "Randament recent {y} kg / 100 L", addNoYield: "Fără randament setat — introdu litrii manual",
    addAlreadyPlanned: "Deja planificat: {l} L în {n} plan(uri)", addEmpty: "Nimic de adăugat — toate produsele sunt acoperite.",
    addSummary: "{n} plan(uri) · {kg} kg · {l} L lapte", addMissingMilk: "{n} fără volum de lapte", addSubmit: "Adaugă {n} în calendar", addNoneSelected: "Selectează cel puțin un produs",
    addDone: "{n} plan(uri) adăugate în calendar", addNote: "Din Planificator: recomandat {kg} kg (cerere {demand} kg vs stoc utilizabil {usable} kg pe {horizon} zile).",
    subtitle: "Ce să produci în continuare, pe baza vânzărilor recente vs. stocul actual — ținând cont de termenele de valabilitate.",
    refresh: "Reîmprospătează",
    horizon: "Acoperă următoarele",
    days7: "7 zile", days14: "14 zile",
    lookback: "Istoric vânzări",
    lastNDays: "Ultimele {n} zile",
    methodHint: "Cererea este estimată din livrările din ultimele {lookback} zile și proiectată pe următoarele {horizon} zile. Stocul care expiră înainte de a putea fi vândut este exclus din stocul utilizabil.",
    toProduceCount: "Produse de fabricat",
    totalToProduce: "Total de produs",
    totalAtRisk: "Risc de expirare",
    empty: "Încă nu există date de vânzări sau stoc — înregistrează câteva livrări pentru a primi recomandări.",
    colProduct: "Produs",
    colSold: "Vândut ({n}z)", colSoldHint: "Total livrat în fereastra de istoric a vânzărilor (în kg).",
    colVelocity: "Ritm", colVelocityHint: "Media de kg vânduți pe zi în fereastra de istoric.",
    colDemand: "Cerere ({n}z)", colDemandHint: "Cererea proiectată pe orizontul de acoperire (ritm × zile).",
    colInStock: "În stoc",
    colUsable: "Utilizabil", colUsableHint: "Stocul disponibil care poate fi vândut realist înainte de expirare, minus stocul rezervat comenzilor deschise.",
    colAtRisk: "La risc", colAtRiskHint: "Stocul disponibil care va expira în orizont înainte de a putea fi vândut.",
    colCover: "Acoperire", colCoverHint: "Câte zile va dura stocul utilizabil la ritmul actual de vânzare.",
    colRecommend: "De produs",
    needsProduction: "Necesită producție",
    lastSold: "ultima vânzare {date}",
    reservedShort: "{kg} kg rezervat",
    covered: "Acoperit",
    generatedAt: "Actualizat {time}",
  },


  tpl: {
    nameRequired: "Numele șablonului este obligatoriu", saved: "Șablon salvat",
    failedSave: "Salvarea șablonului a eșuat", deleted: "Șablon șters",
    newTemplate: "Șablon nou", empty: "Niciun șablon încă.",
    copySuffix: "(copie)", starter: "De bază", fieldsCount: "{n} câmpuri",
    duplicate: "Duplică", selectPrompt: "Selectează un șablon pentru editare sau creează unul nou.",
    editTemplate: "Editează șablonul", templateName: "Nume șablon", description: "Descriere",
    fields: "Câmpuri", noFields: "Niciun câmp încă. Adaugă primul câmp CC.",
    unnamed: "(fără nume)", required: "obligatoriu", rangeLine: "interval: {min} până la {max}",
    saveTemplate: "Salvează șablonul",
    fieldSettings: "Setări câmp", fieldNamePlaceholder: "ex. Temp. pasteurizare",
    minAcceptable: "Minim acceptabil", maxAcceptable: "Maxim acceptabil",
    requiredField: "Câmp obligatoriu", saveField: "Salvează câmpul",
    optionConformity: "Conformitatea opțiunilor",
    optionConformityHint: "Apasă pe o opțiune pentru a o marca drept neconformă — alegerea ei în producție marchează șarja ca neconformă.",
    optionPass: "Conform",
    optionFail: "Neconform",
    shelfLife: "Termen de valabilitate", targetYield: "Randament țintă",
    deliveryShelfLife: "Valabilitate la refrigerare",
    deliveryShelfLifeHint: "Termen de valabilitate mai scurt, folosit când produsul este refrigerat pentru vânzare — aplicat loturilor de producție refrigerate, la scoaterea unui lot din congelator sau la reambalarea în porții mai mici.",
    minSaleShelfLife: "Valabilitate min. vânzare",
    invalidProductType: "Tip invalid: {raw}",
    validPositive: "Introdu un număr pozitiv valid", savedFor: "{label} salvat pentru {product}",
    notSet: "Nesetat", productTargets: "Ținte produs",
    freezerStorage: "Congelator", freezerStorageHint: "Produse păstrate în congelator. Loturile congelate păstrează întreaga valabilitate; loturile refrigerate (și cele scoase din congelator) folosesc valabilitatea mai scurtă la refrigerare.",
    targetsHint: "Termenul de valabilitate setează automat datele de expirare. Maturarea stabilește când o șarjă este gata de mutat la camera frigorifică. Randamentul țintă (kg de produs la 100 L de lapte) determină urmărirea eficienței randamentului.",
    targetYieldHint: "Randamentul țintă este în kg la 100 L (ex. Telemea ≈ 13 kg/100L).",
    targetSc: "Consum țintă",
    targetScHint: "Consumul țintă este în kg de lapte (net) per kg de produs — șarjele peste țintă sunt marcate cu roșu în fișa producției și în exportul Excel.",
    adminOnly: "Doar administratorii și managerii pot edita aceste setări.",
  },

  milk: {
    supplierQtyRequired: "Furnizorul și cantitatea sunt obligatorii",
    invalidDateYear: "Introduceți un an valid din 4 cifre (ex. 2026) pentru data recepției.",
    futureDateNotAllowed: "Laptele nu poate fi recepționat la o dată din viitor.",
    updated: "Recepție de lapte actualizată", receivedClosed: "Lapte primit & recepție închisă",
    added: "Recepție de lapte adăugată", failedUpdate: "Actualizarea șarjei a eșuat", failedAdd: "Adăugarea șarjei a eșuat",
    deleted: "Șarjă ștearsă", addMilk: "Adaugă lapte",
    editReception: "Editează recepția de lapte", receiveScheduled: "Primește lapte programat", addReception: "Adaugă recepție de lapte",
    fulfillingHint: "Onorezi o recepție programată — câmpuri precompletate. Salvarea o va marca drept primită.",
    supplierReq: "Furnizor *", farmName: "Nume fermă", quantityReq: "Cantitate *",
    receptionDate: "Data recepției", temperature: "Temperatură", density: "Densitate",
    pickupDate: "Data colectării", selectSupplier: "Selectează furnizorul",
    noSuppliers: "Niciun furnizor — adaugă mai întâi unul în pagina Furnizori.",
    editHint: "Modificarea cantității ajustează stocul rămas cu aceeași valoare; nu poate scădea sub laptele deja folosit în producții.",
    saveChanges: "Salvează modificările", addBatch: "Adaugă șarjă",
    title: "Șarje de lapte primite", supplier: "Furnizor", allSuppliers: "Toți furnizorii",
    empty: "Niciun lapte primit încă. Adaugă prima recepție.",
    noMatch: "Nicio șarjă de lapte nu corespunde filtrelor selectate.",
    intakeL: "Primit (L)", remainingL: "Rămas (L)", fatPct: "Grăsime %", proteinPct: "Proteină %",
    totalIntake: "Total lapte primit", totalFiltered: "Total lapte (filtrat)", acrossBatches: "din {n} șarje",
    avgFat: "Grăsime medie", avgProtein: "Proteină medie", avgDensity: "Densitate medie",
    tempC: "Temp. °C", acidity: "Aciditate", vehicle: "Vehicul / nr. înmatriculare", driver: "Șofer",
    tempOverLimit: "Peste limita admisă de {max} °C", tempMaxHint: "Maxim admis: {max} °C (Setări)",
    printReport: "Tipărește raport", reportPreparing: "Se pregătește…", printTicket: "Tipărește fișa de recepție",
    reportReady: "Raportul de lapte a fost descărcat", reportFailed: "PDF-ul nu a putut fi generat",
  },

  suppliers: {
    title: "Furnizori", titleMilk: "Furnizori lapte", titleOther: "Furnizori materii prime", add: "Adaugă furnizor", addSupplier: "Adaugă furnizor", editSupplier: "Editează furnizorul",
    supplierName: "Nume furnizor", contactPerson: "Persoană de contact", contact: "Contact", taxId: "CUI",
    empty: "Niciun furnizor încă. Adaugă primul furnizor.", nameRequired: "Numele furnizorului este obligatoriu",
    saved: "Furnizor salvat", removed: "Furnizor șters",
  },

  rawMaterials: {
    title: "Materii prime",
    subtitle: "Gestionează materiile prime precum cheag, culturi, sare și aditivi — urmărește loturile și trasabilitatea în producție pentru HACCP.",
    tabLots: "Loturi", tabTypes: "Tipuri de materiale",
    lotsTitle: "Loturi recepționate", typesTitle: "Tipuri de materiale",
    receiveLot: "Recepție lot", addType: "Adaugă tip", editType: "Editează tipul", editLot: "Editează lotul",
    allTypes: "Toate tipurile", pickType: "Alege un tip",
    noTypesFirst: "Creează mai întâi un tip de materie primă, apoi poți recepționa loturi.",
    lotsEmpty: "Niciun lot recepționat încă.", typesEmpty: "Niciun tip de materie primă încă.",
    hideEmpty: "Ascunde loturile goale",
    allEmptyHidden: "Toate loturile sunt goale — dezactivează filtrul pentru a le vedea.",
    lotCode: "Cod lot", type: "Tip", supplier: "Furnizor", supplierLot: "Nr. lot furnizor",
    remaining: "Rămas", received: "Recepționat", expiry: "Expiră", quantity: "Cantitate", unit: "U.M.",
    name: "Nume", category: "Categorie", defaultUnit: "U.M. implicită", tracksExpiry: "Urmărește expirarea",
    tracksExpiryHint: "Solicită o dată de expirare la recepția loturilor din acest material.",
    customFields: "Câmpuri personalizate", addField: "Adaugă câmp",
    customFieldsHint: "Adaugă câmpuri suplimentare per lot (ex. Concentrație, Tulpină).",
    fieldLabel: "Denumire câmp", fieldUnit: "U.M.",
    notes: "Note", inactive: "Inactiv", activeLabel: "Activ",
    expired: "Expirat",
    namePlaceholder: "ex. Cheag, Cultură termofilă",
    supplierPlaceholder: "Nume furnizor",
    nameRequired: "Numele este obligatoriu", typeRequired: "Selectează un tip", qtyRequired: "Cantitatea trebuie să fie mai mare decât 0",
    typeCreated: "Tip creat", typeUpdated: "Tip actualizat", typeDeleted: "Tip șters",
    lotCreated: "Lot recepționat", lotUpdated: "Lot actualizat", lotDeleted: "Lot șters",
    deleteTypeTitle: "Ștergi tipul de materie primă?",
    deleteTypeDesc: "Ștergi „{name}”? Tipurile cu loturi recepționate trebuie dezactivate în loc.",
    deleteLotTitle: "Ștergi lotul?",
    deleteLotDesc: "Ștergi lotul {code}? Această acțiune este ireversibilă.",
    cat: { rennet: "Cheag", culture: "Cultură", salt: "Sare", additive: "Aditiv", other: "Altele" },
    ftype: { text: "Text", number: "Număr", date: "Dată", select: "Listă" },
    // Categories & units manager
    manageConfig: "Categorii & unități",
    cfgSubtitle: "Adaugă, redenumește sau șterge categoriile și unitățile de măsură folosite pentru materiile prime.",
    cfgAdd: "Adaugă",
    cfgAddCategoryPh: "Nume categorie nouă",
    cfgAddUnitPh: "Unitate nouă (ex. kg, l, buc)",
    cfgEmpty: "Nimic aici încă — adaugă prima intrare mai sus.",
    cfgAdded: "Adăugat", cfgRenamed: "Redenumit", cfgDeleted: "Șters",
    cfgDeleteTitle: "Ștergi această intrare?",
    cfgDeleteDesc: "Ștergi „{name}”? Intrările folosite de materiale existente nu pot fi șterse.",
    prodSectionTitle: "Materii prime",
    prodSectionHint: "Adaugă cheag, culturi sau alte materii prime consumate în această șarjă.",
    addLine: "Adaugă materie primă", pickLot: "Alege un lot", qtyUsed: "Cant. folosită", noneUsed: "Nicio materie primă folosită.",
    traceTitle: "Materii prime folosite", traceLotTitle: "Lot de materie primă",
    consumedIn: "Consumat în producții", noConsumers: "Neutilizat încă în nicio producție.",
  },

  fat: {
    title: "Standardizarea grăsimii — Pătratul lui Pearson",
    subtitle: "Calculează cât lapte degresat sau smântână să amesteci — sau cât lapte să degresezi — pentru a atinge un % de grăsime țintă.",
    enterValuesDiagram: "Introdu valori pentru a vedea pătratul lui Pearson",
    whole: "Integral", cream: "Smântână", skim: "Degresat", target: "Țintă",
    partsWhole: "părți integral", parts: "părți",
    giveName: "Dă un nume planului", planSaved: "Plan de standardizare salvat", failedSavePlan: "Salvarea planului a eșuat",
    inputs: "Date de intrare", milkBatch: "Șarjă de lapte", selectBatch: "Selectează șarja de lapte",
    mode: "Mod", wholeSkim: "Integral + Degresat", wholeCream: "Integral + Smântână",
    milkFat: "Grăsime lapte", targetFat: "Grăsime țintă", milkVolume: "Volum lapte",
    skimFat: "Grăsime degresat", creamFat: "Grăsime smântână", separatorCapacity: "Capacitate separator",
    separatorHint: "Folosit pentru a estima cât timp să funcționeze separatorul la degresarea laptelui.",
    pearsonSquare: "Pătratul lui Pearson", blendRatio: "Raport de amestec", results: "Rezultate",
    enterValid: "Introdu valori valide pentru calcul.", cannotStandardize: "Nu se poate standardiza",
    blendPlan: "Plan de amestec", wholeMilk: "Lapte integral", addType: "Adaugă {type}",
    totalVolume: "Volum total", resultingFat: "Grăsime rezultată",
    degreasePlan: "Plan de degresare (separare)", separateCream: "Separă smântâna",
    remainingAtTarget: "Rămas la țintă", liveRunTime: "Timp de funcționare separator",
    milkToRun: "Lapte de trecut prin separator", runTime: "Timp de funcționare", creamRemoved: "Smântână extrasă",
    skimProduced: "Degresat produs", bypass: "Ocolire (nu separa)",
    separatorSummary: "Pornește separatorul de {rate} L/h timp de {time} pentru a degresa doar {sep} L din șarja de {total} L și a extrage {cream} L de smântână. Reamestecă degresatul cu cei {bypass} L ocoliți pentru a aduce toți cei {remaining} L la {target}% grăsime.",
    savePlanBtn: "Salvează planul de standardizare", savedPlans: "Planuri de standardizare salvate",
    creamCalcTitle: "Calculator grăsime smântână",
    creamCalcSubtitle: "Află conținutul de grăsime al smântânii separate dintr-o șarjă de lapte.",
    cfMilkFat: "Grăsime lapte", cfSkimFat: "Grăsime lapte degresat", cfMilkVol: "Lapte degresat", cfCreamVol: "Smântână obținută",
    cfSkimVol: "Lapte degresat (rezultat)", cfResult: "Conținut de grăsime al smântânii",
    cfEnter: "Introdu valorile din stânga pentru a calcula grăsimea smântânii.",
    cfInvalidTitle: "Verifică volumele",
    cfInvalidVol: "Volumul de smântână trebuie să fie mai mare ca 0 și mai mic decât volumul de lapte.",
    cfImplausible: "Rezultatul pare neobișnuit — verifică valorile de grăsime și volumele.",
    cfHint: "Pe baza bilanțului de grăsime: (volum lapte × grăsime lapte − volum degresat × grăsime degresat) ÷ volum smântână. Volum degresat = lapte − smântână.",
    savedPlansHint: "Planurile salvate pot fi aplicate în asistentul de Producție (pasul Lapte) pentru a precompleta grăsimea țintă și șarja de lapte.",
    planName: "Nume plan", planNamePlaceholder: "ex. Linie iaurt 3.5%",
    previewLine: "{from}% → {to}% · adaugă {vol} L {type}",
  },

  trace: {
    title: "Trasabilitate",
    subtitle: "Caută un produs, șarjă, lot, comandă sau aviz pentru a vedea fișa completă de producție.",
    searchPlaceholder: "Introdu codul sau numele subprodusului (ex. PRD-…, LOT-…, SUB-…, Zer)",
    searchBtn: "Caută", searching: "Se caută…", print: "Printează / Export PDF",
    matchedBy: "Identificat după",
    matchProduction: "Cod producție", matchLot: "Lot de stoc", matchOrder: "Comandă", matchDelivery: "Aviz de livrare",
    emptyTitle: "Caută fișa de producție", emptyHint: "Scrie un cod mai sus și apasă Caută.",
    notFound: "Niciun rezultat pentru acest cod.", resultsFor: "Rezultate pentru", filesFound: "fișă(e) de producție",
    prodSummary: "Sumar producție", code: "Cod", product: "Produs", date: "Data",
    operator: "Operator", status: "Stare", totalMilk: "Lapte folosit", yield: "Randament",
    sold: "Vândut", remaining: "Rămas", expiration: "Expirare", maturationUntil: "Maturare până la",
    notes: "Note", qcWarnings: "Avertizări calitate", recipe: "Rețetă", none: "Niciuna",
    milkUsed: "Lapte folosit (origine)", batch: "Șarjă", supplier: "Furnizor", litres: "Litri",
    reception: "Recepționat", analysis: "Analize", fat: "Grăsime", protein: "Proteine", ph: "pH",
    temperature: "Temp.", density: "Densitate", noMilk: "Niciun aport de lapte înregistrat.",
    stockLots: "Loturi de stoc", lot: "Lot", qty: "Cant.",
    sales: "Vânzări & distribuție", deliveryNote: "Aviz", client: "Client",
    quantity: "Cantitate", unitPrice: "Preț unitar", value: "Valoare", order: "Comandă",
    noSales: "Încă nevândut — niciun aviz înregistrat.", totalSold: "Total vândut", totalValue: "Valoare totală",
    reportTitle: "Raport de trasabilitate", generatedAt: "Generat", auditNote: "Trasabilitate HACCP — pentru audit",
    matchSubproduct: "Lot subprodus",
    subproductSection: "Subprodus (produs secundar)", subLotCode: "Cod lot", byproduct: "Subprodus",
    producedQty: "Produs", remainingQty: "Rămas", origin: "Origine (producția sursă)",
    consumedIn: "Consumat în producții", noConsumers: "Neconsumat încă — încă în stoc.", qtyUsed: "Cant. folosită",
    stockLotSection: "Lot de stoc", lotCodeLabel: "Cod lot", madeOn: "Fabricat la", packagedOn: "Ambalat la",
    madeFrom: "Fabricat din (re-lotizat din)", noProductionFile: "Niciun dosar de producție asociat acestui lot.",
    hasProductionFile: "Dosarul complet de producție mai jos.",
    rebatchedInto: "Re-lotizat în (unde a mers)", wentTo: "A mers la", stillInStock: "Încă în stoc",
    thisLotWentTo: "Acest lot a fost livrat către",
    subUsedSection: "Subproduse folosite (intrări)",
    sourceProduction: "Producția sursă",
    corrective: "Acțiune corectivă",
    subWarnings: "Avertizări stoc subproduse",
    traceThis: "Trasează acest cod",
  },

  haccp: {
    ccpBreach: "Breșă PCC", warning: "Avertizare", inRange: "În limite", outOfLimit: "În afara limitei",
    logDeleted: "Înregistrare de monitorizare ștearsă", failedDeleteLog: "Ștergerea înregistrării a eșuat", exportFailed: "Exportul a eșuat",
    unresolvedBreaches: "{n} breșe PCC nerezolvate",
    addCorrectiveHint: ". Adaugă acțiuni corective în Înregistrări de monitorizare.",
    tabLogs: "Înregistrări monitorizare", tabCcps: "Definiții PCC", tabTrends: "Tendințe",
    logsTitle: "Înregistrări de monitorizare", logReading: "Înregistrează citire",
    controlPoint: "Punct de control", allCcps: "Toate PCC-urile",
    noLogs: "Nicio înregistrare de monitorizare încă.", noMatch: "Nicio citire nu corespunde filtrelor selectate.",
    time: "Oră", ccp: "PCC", measurements: "Măsurători", action: "Acțiune",
    correctiveAction: "Acțiune corectivă", resolved: "Rezolvat", deleteLogTitleAttr: "Șterge înregistrarea",
    ccpsTitle: "Puncte Critice de Control", newCcp: "PCC nou", noCcps: "Niciun PCC definit încă.",
    processStep: "Etapă proces", criticalLimits: "Limite critice", frequency: "Frecvență", schedule: "Programare",
    customFields: "Personalizat · {n} câmpuri", perField: "Pe câmp", due: "Scadent {date}",
    toComplete: "De completat", overdue: "Întârziat", scheduled: "Programat", onTrack: "În grafic", viewOnly: "Doar vizualizare",
    ccpDeleted: "PCC șters",
    deleteLogTitle: "Ștergi înregistrarea de monitorizare?",
    deleteLogDesc: "Aceasta elimină definitiv înregistrarea {ccp} din {time}. Această acțiune nu poate fi anulată.",
    deleteLogBtn: "Șterge înregistrarea",
    nameStepRequired: "Numele și etapa procesului sunt obligatorii",
    addOneField: "Adaugă cel puțin un câmp personalizat sau comută la măsurătoare unică",
    ccpSaved: "PCC salvat", failedSaveCcp: "Salvarea PCC a eșuat",
    editCcp: "Editează punctul de control", newControlPoint: "Punct de control nou",
    ccpName: "Nume PCC", ccpNamePlaceholder: "Temp. pasteurizare", stepPlaceholder: "Pasteurizare",
    freqPlaceholder: "Pe șarjă / La fiecare 4 ore",
    reminderTitle: "Memento de completare", reminderHint: "Afișează acest punct de control în clopoțelul de notificări până când este înregistrat.",
    recurring: "Recurent (la fiecare N ore)", oneTime: "O singură dată (dată/oră scadentă)",
    reminderWorkingDays: "Zilnic în zile lucrătoare",
    reminderWeekly: "Săptămânal (la fiecare N săptămâni)", everyWeeks: "Repetă la fiecare", weeksOption: "{n} săptămână/-i",
    editLog: "Editează", editLogTitle: "Editează înregistrarea HACCP", saveChanges: "Salvează modificările", logUpdated: "Înregistrare actualizată",
    failedUpdateLog: "Actualizarea înregistrării a eșuat", resolveLog: "Rezolvă", logResolved: "Excepție rezolvată", failedResolve: "Rezolvarea a eșuat",
    everyHours: "La fiecare (ore)", dueDateTime: "Dată & oră scadentă",
    customTitle: "Punct de control personalizat, multi-câmp",
    customHint: "Înregistrează mai mulți parametri tipizați (temp., verificări, foto…) într-un singur punct.",
    measurementType: "Tip măsurătoare", critMin: "Critic min", critMax: "Critic max",
    warnMinOpt: "Avertizare min (opțional)", warnMaxOpt: "Avertizare max (opțional)",
    monitoringFields: "Câmpuri de monitorizare",
    noFieldsHint: "Niciun câmp încă. Adaugă temperatură, pH, o verificare admis/respins, o listă, o notă sau o fotografie.",
    saveCcp: "Salvează PCC",
    fieldNamePlaceholder: "ex. Temperatură",
    critMinShort: "Crit min", critMaxShort: "Crit max", warnMinShort: "Avert min", warnMaxShort: "Avert max",
    acceptableAnswer: "Răspunsul acceptabil este {ans} (altfel marcat critic)",
    acceptableOptions: "Opțiuni acceptabile (separate prin virgulă)",
    checklistItems: "Elemente checklist (separate prin virgulă)", checklistHint: "Fiecare element apare ca o casetă bifabilă la înregistrare.",
    freeTextHint: "Notă text liber (informativ).", photoHint: "Încărcare dovadă foto (informativ).",
    fieldRequired: "{field} este obligatoriu", selectControlPoint: "Selectează un punct de control", enterReading: "Introdu o valoare de citire",
    breachRecorded: "Breșă PCC înregistrată — adaugă o acțiune corectivă", readingLogged: "Citire înregistrată", failedLog: "Înregistrarea citirii a eșuat",
    logCcpReading: "Înregistrează citire PCC", selectCcp: "Selectează PCC", readingValue: "Valoare citire",
    criticalRange: "Critic: {min} – {max} {unit}", productionOpt: "Producție (opț.)", saveReading: "Salvează citirea",
    readingDateTime: "Data și ora citirii", readingDateHint: "Implicit acum — modifică pentru a înregistra o verificare din altă zi.",
    invalidDate: "Introduceți o dată și o oră valide.",
    allOperators: "Toți operatorii", freqAll: "Toate frecvențele", freqOff: "Fără memento", filtered: "filtrat",
    pdfFiltered: "PDF registru pentru filtrele curente", pdfAll: "PDF registru (toate înregistrările)",
    escalation: "Escaladare verificare neefectuată", escalationOff: "Oprit — fără escaladare",
    escalationAfterMin: "Alertează adminul după {n} min", escalationAfterHours: "Alertează adminul după {n} h",
    escalationHint: "Dacă verificarea nu este înregistrată nici după acest interval de la scadență, administratorii primesc o alertă critică în clopoțel și un email (o dată per apariție).",
    printRecord: "Tipărește fișa înregistrării", reminderTime: "Ora mementoului", reminderTimeHint: "Devine scadent la această oră (gol = de la miezul nopții).",
    autoTaskOperator: "Atribuie ca sarcină operatorului", autoTaskNone: "Fără sarcină — doar memento în clopoțel", autoTaskShort: "Sarcină",
    autoTaskHint: "Când verificarea devine scadentă, se creează o sarcină deschisă pentru acest operator, închisă automat la înregistrarea unei citiri.",
    unitHoursPlain: "ore", firstDueOpt: "Start / prima scadență (opțional)", dueRequired: "Setați data și ora scadenței.",
    reminderOnceHint: "Apare în Mementouri de la data scadenței până la înregistrarea unei citiri.",
    reminderRepeatHint: "Apare în Mementouri ori de câte ori o verificare este scadentă și nu există citire pentru perioada respectivă.",
    dateFieldHint: "Selector de dată (informativ). Implicit azi la înregistrare.",
    datetimeFieldHint: "Selector de dată și oră (informativ). Implicit acum la înregistrare.",
    ccpReport: "Raport PCC", ccpReportDesc: "Raport de monitorizare printabil pentru un punct de control sau toate, pentru o lună calendaristică.",
    reportMonth: "Luna", reportGenerate: "Generează PDF", reportGenerating: "Se generează…",
    reportReady: "Raport descărcat", reportInvalidMonth: "Alegeți o lună validă.",
    reportIncl1: "Definiție PCC: etapa de proces, limite, frecvența monitorizării",
    reportIncl2: "Înregistrări: dată/oră, valori, stare, operator, producție, note",
    reportIncl3: "Acțiuni corective pentru breșe + sumar (verificări, avertizări, breșe, lipsă)",
    reportIncl4: "Rubrici semnătură: întocmit de / verificat de / data",
    selectEllipsis: "Selectează…", noChecklistItems: "Niciun element de checklist configurat.",
    photoUploaded: "Fotografie încărcată", uploadFailed: "Încărcarea a eșuat",
    uploading: "Se încarcă…", replace: "Înlocuiește", uploadPhoto: "Încarcă fotografie", remove: "Elimină",
    describeAction: "Descrie acțiunea corectivă", actionRecorded: "Acțiune corectivă înregistrată", breachWord: "breșă",
    actionsTaken: "Acțiuni întreprinse", actionsPlaceholder: "Repasteurizat șarja, reglat termostatul...",
    disposition: "Destinația produsului", dispositionPlaceholder: "Eliberat / Reținut / Eliminat",
    verifiedBy: "Verificat de", closeIncident: "Închide incidentul",
    trendTitle: "Tendința citirilor PCC", selectACcp: "Selectează un PCC", selectToView: "Selectează un PCC pentru a vedea tendința.",
    noNumeric: "Acest punct de control nu are câmpuri numerice de reprezentat.", noReadingsFor: "Nicio citire înregistrată pentru {ccp} încă.",
    warningRange: "Avertizare: {min} – {max} {unit}", readingsCount: "{n} citiri",
  },

  activity: {
    title: "Jurnal de activitate", all: "Toate", productions: "Producții",
    exportXlsx: "Export Excel", exporting: "Se exportă…", exported: "Jurnalul de activitate a fost descărcat", exportFailed: "Jurnalul nu a putut fi exportat",
    empty: "Nicio activitate înregistrată încă.",
    when: "Când", user: "Utilizator", action: "Acțiune", item: "Element", details: "Detalii",
    detailsVal: "(detalii)", production: "Producție",
    stock: "Stoc", stockLot: "Lot de stoc",
    created: "creat", updated: "actualizat", deleted: "șters",
    repacked: "reambalat", adjusted: "ajustat", out_of_freezer: "scos din congelator", dispatched: "expediat", renamed: "lot redenumit", writeoff_reverted: "casare anulată",
    searchPlaceholder: "Caută nr. lot, utilizator sau detalii…",
    noSearchResults: "Nicio înregistrare nu corespunde cu „{q}”.",
    noRangeResults: "Nicio înregistrare în perioada selectată.",
    fromDate: "De la data",
    toDate: "Până la data",
  },

  yieldSim: {
    title: "Simulator randament",
    subtitle: "Estimează laptele necesar pentru a produce o cantitate țintă de produs — sau produsul obținut dintr-o cantitate dată de lapte.",
    modeProductToMilk: "Produs → Lapte",
    modeMilkToProduct: "Lapte → Produs",
    products: "Produse",
    addProduct: "Adaugă produs",
    product: "Produs",
    selectProduct: "Selectează produsul",
    yieldBasis: "Bază randament",
    yieldPer100: "Randament / 100 L",
    desiredQty: "Cantitate dorită",
    milkNeeded: "Lapte necesar",
    productOutput: "Produs obținut",
    basisTarget: "Țintă",
    basisAverage: "Media reală",
    basisCustom: "Personalizat",
    notSet: "nesetat",
    noHistory: "fără istoric",
    availableMilk: "Lapte disponibil",
    milkModeHint: "Fiecare rând arată ce ai obține dacă tot laptele ar merge către acel produs.",
    totalMilkNeeded: "Total lapte necesar",
    footnote: "Randamentul țintă provine din setările produsului; media reală se calculează din producțiile anterioare (randament ÷ lapte × 100).",
    loadFailed: "Încărcarea randamentelor a eșuat",
    noDataTitle: "Încă nu există date de randament",
    noDataHint: "Setează un randament țintă la 100 L în setările produsului sau înregistrează producții pentru a construi o medie reală. Poți folosi și un randament personalizat per produs.",
  },


  users: {
    emailNamePassRequired: "Emailul, numele și parola sunt obligatorii",
    emailNameRequired: "Emailul și numele sunt obligatorii",
    passwordRequired: "Setează o parolă sau activează emailul de activare",
    saved: "Utilizator salvat", failedSave: "Salvarea utilizatorului a eșuat",
    removed: "Utilizator șters", failedDelete: "Ștergerea a eșuat",
    addUser: "Adaugă utilizator", title: "Utilizatori & roluri", empty: "Niciun utilizator încă.",
    name: "Nume", email: "Email", role: "Rol", you: "(tu)",
    editUser: "Editează utilizatorul", newPassword: "Parolă nouă", password: "Parolă",
    leaveBlankKeep: "Lasă gol pentru a păstra parola curentă.",
    sendActivation: "Trimite email de activare",
    sendActivationHint: "Utilizatorul își setează singur parola printr-un link trimis pe email.",
    inviteSent: "Email de activare trimis", inviteResent: "Email de activare retrimis",
    resetSent: "Email de resetare a parolei trimis", pending: "În așteptare",
    resendInvite: "Retrimite emailul de activare", sendReset: "Trimite email de resetare parolă",
  },
  notif: {
    reminders: "Mementouri", active: "{n} active", allCaughtUp: "Totul la zi — nimic întârziat.",
    digest: "Rezumat zilnic al verificărilor PCC întârziate, planurilor și breșelor nerezolvate",
    dismiss: "Închide", dismissAll: "Închide toate", dismissed: "Memento închis",
    allDismissed: "{n} mementouri închise", undo: "Anulează", dismissFailed: "Mementoul nu a putut fi închis",
    undoFailed: "Mementoul nu a putut fi restaurat", restoreAll: "Arată {n} închise",
    dismissGroup: "Închide tot grupul", collapse: "Apasă pentru a restrânge",
    group: {
      ccp_overdue: "{n} verificări PCC scadente", plan_overdue: "{n} producții planificate întârziate",
      reception_overdue: "{n} recepții lapte întârziate", haccp_breach: "{n} breșe PCC nerezolvate",
      maturation_ready: "{n} șarje gata pentru camera frig", task: "{n} sarcini scadente",
      lot_expiring: "{n} loturi expiră curând", audit_change: "{n} modificări stoc / randament",
      ccp_escalation: "{n} verificări PCC neefectuate escaladate",
    },
  },

  pager: {
    showing: "Se afișează {from}–{to} din {total}", perPage: "Pe pagină",
    prev: "Anterior", next: "Următor",
  },

  dashboard: {
    openOrders: "Comenzi deschise · în așteptarea livrării",
    completedOrders: "Comenzi finalizate · livrate",
    planningTitle: "Planificare viitoare",
    noPlanning: "Nicio producție sau recepție de lapte planificată.",
    planColDate: "Data", planColType: "Tip", planColDetail: "Detaliu", planColVolume: "Volum", planColInfo: "Alocat / Grăsime",
    milkInventory: "Stoc lapte",
    currentMonth: "Luna curentă", lastMonth: "Luna trecută",
    trends: "Tendințe", recentProductions: "Producții recente",
    overviewTitle: "Prezentare producție",
    overviewSubtitle: "Imagine în timp real a stocului de lapte, producțiilor active și abaterilor de calitate.",
    chartsPeriod: "Perioadă grafice",
    milkBtn: "Lapte", productionBtn: "Producție",
    milkAvailable: "Lapte disponibil", activeProductions: "Producții active",
    milkReceived: "Total lapte primit",
    periodThisMonth: "Luna aceasta", periodLastMonth: "Luna trecută", periodThisYear: "Anul acesta",
    periodAllTime: "Din totdeauna", periodCustomMonth: "Lună specifică",
    planned: "Planificate", qcExceptions: "Abateri CC", haccpAlerts: "Alerte HACCP",
    milkInVsProduced: "Lapte intrat vs produs", thisMonth: "luna aceasta",
    milkInL: "Lapte intrat (L)", producedL: "Produs (L)",
    prodVolumeByProduct: "Volum producție pe produs", milkUsedL: "Lapte folosit (L)",
    noProdData: "Nu există date de producție încă.",
    scTrend: "Consum specific mediu lunar",
    scTrendHint: "kg de lapte (net) necesari per kg de produs — mai mic e mai bine. Ultimele 6 luni, pe produs. Linia punctată = ținta produsului.",
    scUnitShort: "kg/kg",
    scTargetLabel: "țintă",
    noScData: "Nu există încă date de consum. Șarjele au nevoie de lapte + randament înregistrate.",
    lossTrend: "Pierderi lunare pe motiv",
    lossTrendHint: "kg casate în fiecare lună, cumulate pe motive — tiparele de risipă ies în evidență în timp. Ultimele 6 luni.",
    noLossData: "Nicio pierdere înregistrată în ultimele 6 luni.",
    lossValueLabel: "valoare estimată pierderi (6 luni)",
    byproducts: "Subproduse obținute (pe lună)",
    viewAll: "Vezi toate", manage: "Gestionează",
    noProductions: "Nicio producție încă.", flag: "abatere",
    qcExceptionsTitle: "Abateri CC", noOutOfRange: "Nicio valoare în afara limitelor.",
    expiringSoon: "Expiră curând", noExpiring: "Niciun produs care expiră în {days} zile.",
    expiredAgo: "Expirat acum {n}z", expiresToday: "Expiră azi", daysLeft: "{n}z rămase",
    exp: "Exp",
    readyForColdRoom: "Gata pentru camera frigorifică",
    noColdRoom: "Nicio șarjă la maturare gata în {days} zile.",
    salesTrend: "Tendință vânzări", salesTrendSub: "Cantitate livrată vs perioada anterioară",
    salesTrendValueSub: "Valoare totală vândută vs perioada anterioară",
    thisPeriod: "Perioada curentă", lastPeriod: "Perioada anterioară", revenueLabel: "Venituri",
    period7d: "Ultimele 7 zile", period30d: "Ultimele 30 zile", period90d: "Ultimele 90 zile",
    periodMonth: "Luna aceasta", periodYear: "Anul acesta",
    byProduct: "Pe produs", vsPrev: "vs ant.", newSales: "Nou",
    noSales: "Nicio livrare în această perioadă.",
    current: "Curent", previous: "Anterior", change: "Variație",
    allProducts: "Toate produsele", viewDetailedReport: "Vezi raportul detaliat",
    ready: "Gata", readyAgo: "Gata acum {n}z", readyToday: "Gata azi",
    yieldEfficiency: "Eficiență randament pe produs",
    yieldSubtitle: "Randament real mediu vs țintă (kg la 100 L de lapte). Setează țintele în Șabloane CC.",
    noYieldData: "Nu există date de randament încă. Setează un randament țintă pe produs în Șabloane CC și înregistrează randamentele.",
    runs: "rulări", run: "rulare",
    noMilk: "Nicio șarjă de lapte încă.", fat: "Grăsime", ph: "pH",
  },

  data: {
    "Telemea": "Telemea",
    "Mozzarella": "Mozzarella",
    "Cas": "Caș",
    "Urda": "Urdă",
    "Cascaval": "Cașcaval",
    "Branza": "Brânză",
    "Cream Cheese": "Cremă de brânză",
    "Cottage Cheese": "Brânză de vaci",
    "Fresh Cottage Cheese": "Brânză de vaci proaspătă",
    "Diet Fresh Cottage Cheese": "Brânză de vaci proaspătă dietetică",
    "Butter": "Unt",
    "Yogurt": "Iaurt",
    "Smantana": "Smântână",
    "Sour Cream": "Smântână",
    "Milk": "Lapte",
    "Whey": "Zer",
    "Kashkaval": "Cașcaval",
    "Ricotta": "Ricotta",
    "In progress": "În curs",
    "Maturation": "Maturare",
    "Maturing": "La maturare",
    "Done": "Finalizat",
    "Completed": "Finalizat",
    "Ready": "Gata",
    "Planned": "Planificat",
    "Packaged": "Ambalat",
  },
};

export const resources = { en, ro };
